/** @module @liquidefy/code-editor @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS — import everything needed from types ═══

import type {
  Timestamp,
  Position,
  Range,
  Selection,
  EditorEventDomain,
  EditorEventKind,
  EditorEvent,
  EditorResult,
  EditorResultSuccess,
  DocumentId,
  BufferId,
  CursorGroupId,
  MarkerId,
  TokenId,
  GrammarId,
  LanguageId,
  CompletionId,
  DiagnosticId,
  CodeActionId,
  CodeLensId,
  ViewportId,
  DecorationId,
  WidgetId,
  EditorInstanceId,
  SnippetId,
  CommandId,
  KeybindingId,
  TerminalId,
  PTYProcessId,
  TerminalSessionId,
  TerminalBufferId,
  MultiplexSessionId,
  MultiplexWindowId,
  MultiplexPaneId,
  RecordingId,
  RecordingFrameId,
  ShareSessionId,
  ParticipantId,
  CloudTerminalId,
  AIRequestId,
  AIResponseId,
  AIAgentId,
  AIConversationId,
  AIMessageId,
  AIModelId,
  CacheEntryId,
  WorkspaceId,
  PanelId,
  TabId,
  TabGroupId,
  LayoutNodeId,
  GitRepositoryId,
  CommitHash,
  BranchName,
  RemoteName,
  TagName,
  StashId,
  HookId,
  FileId,
  DirectoryId,
  SearchResultId,
  IndexId,
  ProjectId,
  DependencyId,
  EnvironmentId,
  TaskId,
  BuildId,
  PluginId,
  PluginManifestId,
  SettingsProfileId,
  DiffId,
  HunkId,
  ReviewThreadId,
  ReviewCommentId,
  AdapterId,
  EventChannelId,
  StorageNamespace,
  PermissionId,
  LogChannelId,
  DocumentEncoding,
  LineEnding,
  ByteOrderMark,
  EditOperationKind,
  EditOrigin,
  EditOperation,
  DocumentState,
  DocumentSnapshot,
  CursorState,
  CursorGroup,
  SelectionKind,
  ColumnSelection,
  CursorStyle,
  HistoryEntry,
  HistoryBranch,
  HistoryState,
  UndoStopKind,
  MarkerKind,
  MarkerStickiness,
  DiagnosticSeverity,
  DiagnosticRelatedInfo,
  DiagnosticQuickFix,
  DiagnosticData,
  MarkerState,
  TokenType,
  Token,
  TokenizerState,
  TokenLine,
  GrammarCaptures,
  GrammarPattern,
  GrammarRepository,
  GrammarDefinition,
  ScopeResolution,
  CommentRule,
  BracketPair,
  AutoClosingPair,
  SurroundingPair,
  FoldingMarkers,
  FoldingRules,
  IndentationRules,
  OnEnterRule,
  OnEnterAction,
  LanguageConfiguration,
  LanguageDefinition,
  CompletionKind,
  InsertTextRule,
  CompletionItem,
  CompletionTriggerKind,
  CompletionContext,
  HoverInfo,
  DiagnosticTag,
  DiagnosticRelatedInformation,
  DiagnosticInfo,
  CodeActionKind,
  CodeAction,
  ParameterInfo,
  SignatureInfo,
  SignatureHelp,
  LocationLink,
  RenameFileChange,
  RenamePreview,
  RenameResult,
  SymbolKind,
  CallHierarchyItem,
  CallHierarchyDirection,
  TypeHierarchyItem,
  TypeHierarchyDirection,
  DocumentSymbol,
  CommandData,
  CodeLensData,
  InlayHintKind,
  InlayHint,
  BreadcrumbItem,
  FormatOptions,
  SemanticToken,
  SemanticTokenTypes,
  ViewportState,
  WordWrapMode,
  WordWrapConfig,
  MinimapConfig,
  GutterConfig,
  ScrollConfig,
  StickyScrollConfig,
  MinimapDecorationOptions,
  DecorationOptions,
  DecorationData,
  ZoneWidget,
  ContentWidgetPositionPreference,
  ContentWidgetPosition,
  ContentWidget,
  OverlayWidgetPositionPreference,
  OverlayWidgetPosition,
  OverlayWidget,
  EditorMode,
  FocusState,
  ReadOnlyMode,
  EditorConfig,
  BracketGuideConfig,
  EditorState,
  CharChange,
  DiffChange,
  DiffEditorState,
  LinkedEditorState,
  FindState,
  FindMatch,
  FileSearchResult,
  FindInFilesState,
  SnippetTransform,
  TabStop,
  SnippetDefinition,
  SnippetSession,
  SnippetVariable,
  WhenClause,
  CommandRegistry,
  KeybindingData,
  ChordKeybinding,
  KeybindingResolveResult,
  AccessibilityConfig,
  AccessibilityInfo,
  TerminalType,
  TerminalStatus,
  ShellType,
  TerminalTheme,
  TerminalRenderingConfig,
  TerminalBehaviorConfig,
  TerminalProfile,
  TerminalSecurityContext,
  TerminalMetrics,
  TerminalState,
  PTYStatus,
  PTYSignal,
  PTYPlatform,
  PTYResourceUsage,
  PTYState,
  PTYConfig,
  ColorMode,
  TerminalColor,
  CellFlags,
  CellAttributes,
  TerminalCell,
  TerminalLine,
  TerminalCursorState,
  ScrollbackConfig,
  TerminalViewport,
  TerminalBufferState,
  ANSISequenceType,
  ANSISequence,
  SGRAttributeType,
  SGRAttribute,
  MouseTrackingMode,
  TerminalModeState,
  CharsetDesignation,
  OutputClassification,
  EntityType,
  DetectedFormat,
  ExtractedEntity,
  OutputContent,
  ProcessedOutput,
  SessionStatus,
  CommandHistoryEntry,
  CommandHistoryState,
  JobInfo,
  PromptConfig,
  TerminalSessionState,
  MultiplexerType,
  PaneLayout,
  PaneSplitDirection,
  PanePosition,
  PaneSize,
  MultiplexPane,
  MultiplexWindow,
  MultiplexSession,
  StatusBarConfig,
  MultiplexerConfig,
  MultiplexerState,
  RecordingStatus,
  FrameType,
  RecordingFrame,
  RecordingMetadata,
  RecordingConfig,
  RecordingBookmark,
  RecordingExportFormat,
  PlaybackState,
  RecordingState,
  ParticipantRole,
  ShareParticipant,
  SharePermissions,
  ChatMessage,
  ShareConfig,
  TerminalCollabState,
  CloudProvider,
  CloudConnection,
  PortMapping,
  VolumeMount,
  ContainerInfo,
  ResourceLimits,
  ScalingConfig,
  HealthCheckConfig,
  CloudTerminalState,
  CommandSuggestion,
  SuggestedSolution,
  ErrorAnalysis,
  NLContext,
  NLCommandRequest,
  NLCommandResult,
  AIPrivacySettings,
  TerminalAIState,
  AIModule,
  AIRequestType,
  AIPriority,
  AIContext,
  AIConstraints,
  AIRequest,
  AIResponseStatus,
  AIContent,
  AIModelInfo,
  AIPerformanceInfo,
  AIResponse,
  AIAgentType,
  AIAgentStatus,
  AITaskType,
  AITaskResult,
  AIAgentTask,
  AIEpisode,
  AIAgentMemory,
  AIExecutionStep,
  AIExecutionPlan,
  AIAgentPerformance,
  AIAgentConfig,
  AIAgentState,
  ConversationType,
  ConversationParticipant,
  MessageContent,
  MessageType,
  MessageReaction,
  MessageAttachment,
  AIMessage,
  ConversationContext,
  AIConversationState,
  IntentResult,
  CodeGenerationType,
  CodeGenerationContext,
  CodeGenerationConstraints,
  CodeGenerationPreferences,
  CodeGenerationRequest,
  CodeExplanation,
  QualityAssessment,
  SecurityVulnerability,
  SecurityAssessment,
  AlternativeCode,
  CodeGenerationResult,
  CodeAnalysisType,
  AnalysisTarget,
  AnalysisOptions,
  CodeAnalysisRequest,
  AnalysisSummary,
  AnalysisFinding,
  HalsteadMetrics,
  ComplexityMetrics,
  MaintainabilityMetrics,
  DuplicateBlock,
  DuplicationMetrics,
  CodeMetrics,
  CodeAnalysisResult,
  AIProviderType,
  AIModelSpecs,
  AIModelConfig,
  ModelPerformanceMetrics,
  RoutingStrategy,
  CircuitBreakerConfig,
  AIModelRoutingConfig,
  AIModelState,
  AICacheEntry,
  AICacheStats,
  AICacheConfig,
  AICacheState,
  CodingStyleProfile,
  SkillLevel,
  UserPreferences,
  UserProfile,
  BehaviorPattern,
  LearningEvent,
  AdaptationConfig,
  PersonalizationState,
  InputValidationConfig,
  OutputFilteringConfig,
  CodeScanningConfig,
  ComplianceFramework,
  DataClassification,
  ComplianceConfig,
  AIAuditConfig,
  AIAuditEntry,
  AISecurityConfig,
  LayoutType,
  LayoutLeafData,
  LayoutNode,
  WorkspaceLayout,
  PanelType,
  PanelPosition,
  PanelState,
  TabState,
  TabGroupState,
  WorkspaceState,
  CollabParticipant,
  CollabCursor,
  FollowModeState,
  WorkspaceCollabState,
  SyncConflict,
  WorkspaceSyncState,
  GitHead,
  GitBranch,
  GitRemote,
  GitTag,
  GitStash,
  GitStatusCode,
  GitFileStatus,
  GitConfig,
  GitRepositoryState,
  GitPerson,
  GitCommit,
  GitLogOptions,
  GitBlameLine,
  GitBlameResult,
  GitBisectState,
  GitDiffLine,
  GitStagingHunk,
  GitConflict,
  GitMergeState,
  GitHookName,
  GitHookState,
  GitProviderType,
  GitProviderConfig,
  PRReviewer,
  PRCheck,
  PullRequestState,
  FileEntry,
  DirectoryEntry,
  FileWatchEvent,
  FileWatchConfig,
  FileStat,
  GlobPattern,
  LargeFileConfig,
  ASTNode,
  ASTQuery,
  ASTTransform,
  SymbolInfo,
  SearchQuery,
  SearchMatch,
  SearchResult,
  SearchReplaceRequest,
  FileIndex,
  OTOperation,
  CollabDocument,
  ProjectType,
  PackageManagerType,
  TaskSource,
  ProjectTask,
  BuildSystem,
  MonorepoPackage,
  MonorepoConfig,
  CIConfig,
  ProjectConfig,
  VulnerabilityInfo,
  DependencyInfo,
  DependencyGraph,
  EnvironmentState,
  ProjectState,
  PluginStatus,
  PluginPermission,
  MenuContribution,
  ViewContribution,
  ThemeContribution,
  LanguageContribution,
  SnippetContribution,
  GrammarContribution,
  DebuggerContribution,
  FormatterContribution,
  SettingSchema,
  SettingsContribution,
  PluginContributions,
  PluginManifest,
  PluginResourceUsage,
  PluginState,
  SandboxCapability,
  SandboxResourceLimits,
  SandboxConfig,
  MarketplaceEntry,
  MarketplaceSearchQuery,
  MarketplaceSearchResult,
  SettingsScope,
  SettingsProfile,
  SettingsState,
  SettingsSyncConflict,
  SettingsSyncState,
  SettingsChangeEvent,
  SettingsMigration,
  DiffAlgorithm,
  CharRange,
  DiffLine,
  DiffHunk,
  DiffStats,
  DiffRenderMode,
  MovedBlock,
  DiffResult,
  MergeResolutionStrategy,
  MergeConflict,
  MergeResolution,
  ThreeWayMergeState,
  CodeSuggestion,
  ReviewComment,
  ReviewThread,
  ReviewState,
  MonacoAdapterConfig,
  MonacoCommandMapping,
  LSPClientCapabilities,
  LSPClientConfig,
  LSPRequestMapping,
  DAPClientConfig,
  DAPBreakpoint,
  DAPScope,
  DAPStackFrame,
  DAPVariable,
  ExternalToolConfig,
  EventChannel,
  EditorEventBus,
  EditorEventData,
  StorageEntry,
  StorageBackendType,
  StorageConfig,
  PerformanceSnapshot,
  PerformanceBudget,
  TrustLevel,
  PermissionGrant,
  PermissionRequest,
  LogEntry,
} from './code-editor-types.js';

// Re-export all types for leaf modules
export type * from './code-editor-types.js';

// ═══ §2 RESULT CONSTRUCTORS & COMBINATORS — ok/err factories, chaining, unwrap ═══

export function ok<T>(value: T, code: string = 'OK', message: string = 'Success', events: EditorEvent[] = []): EditorResult<T> {
  return { success: true, code, message, value, events };
}

export function err<T>(code: string, message: string, error?: unknown): EditorResult<T> {
  return { success: false, code, message, error };
}

export function andThen<T, U>(result: EditorResult<T>, fn: (val: T) => EditorResult<U>): EditorResult<U> {
  if (!result.success) return result as unknown as EditorResult<U>;
  return fn(result.value);
}

export function mapResult<T, U>(result: EditorResult<T>, fn: (val: T) => U): EditorResult<U> {
  if (!result.success) return result as unknown as EditorResult<U>;
  return ok(fn(result.value), result.code, result.message, result.events);
}

export function unwrapOr<T>(result: EditorResult<T>, fallback: T): T {
  if (!result.success) return fallback;
  return result.value;
}

export function collectResults<T>(results: EditorResult<T>[]): EditorResult<T[]> {
  const values: T[] = [];
  const events: EditorEvent[] = [];
  for (const r of results) {
    if (!r.success) return r as unknown as EditorResult<T[]>;
    values.push(r.value);
    if (r.events) events.push(...r.events);
  }
  return ok(values, 'OK', 'All results collected', events);
}

export function isOk<T>(result: EditorResult<T>): boolean {
  return result.success;
}

export function isErr<T>(result: EditorResult<T>): boolean {
  return !result.success;
}

// ═══ §3 BRANDED ID FACTORIES — factory for every branded ID type ═══

export function generateId(prefix: string): string {
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).substring(2, 10);
  return `${prefix}_${ts}_${rand}`;
}

export function asDocumentId(raw: string): DocumentId { return raw as DocumentId; }
export function asBufferId(raw: string): BufferId { return raw as BufferId; }
export function asCursorGroupId(raw: string): CursorGroupId { return raw as CursorGroupId; }
export function asMarkerId(raw: string): MarkerId { return raw as MarkerId; }
export function asTokenId(raw: string): TokenId { return raw as TokenId; }
export function asGrammarId(raw: string): GrammarId { return raw as GrammarId; }
export function asLanguageId(raw: string): LanguageId { return raw as LanguageId; }
export function asCompletionId(raw: string): CompletionId { return raw as CompletionId; }
export function asDiagnosticId(raw: string): DiagnosticId { return raw as DiagnosticId; }
export function asCodeActionId(raw: string): CodeActionId { return raw as CodeActionId; }
export function asCodeLensId(raw: string): CodeLensId { return raw as CodeLensId; }
export function asViewportId(raw: string): ViewportId { return raw as ViewportId; }
export function asDecorationId(raw: string): DecorationId { return raw as DecorationId; }
export function asWidgetId(raw: string): WidgetId { return raw as WidgetId; }
export function asEditorInstanceId(raw: string): EditorInstanceId { return raw as EditorInstanceId; }
export function asSnippetId(raw: string): SnippetId { return raw as SnippetId; }
export function asCommandId(raw: string): CommandId { return raw as CommandId; }
export function asKeybindingId(raw: string): KeybindingId { return raw as KeybindingId; }
export function asTerminalId(raw: string): TerminalId { return raw as TerminalId; }
export function asPTYProcessId(raw: string): PTYProcessId { return raw as PTYProcessId; }
export function asTerminalSessionId(raw: string): TerminalSessionId { return raw as TerminalSessionId; }
export function asTerminalBufferId(raw: string): TerminalBufferId { return raw as TerminalBufferId; }
export function asMultiplexSessionId(raw: string): MultiplexSessionId { return raw as MultiplexSessionId; }
export function asMultiplexWindowId(raw: string): MultiplexWindowId { return raw as MultiplexWindowId; }
export function asMultiplexPaneId(raw: string): MultiplexPaneId { return raw as MultiplexPaneId; }
export function asRecordingId(raw: string): RecordingId { return raw as RecordingId; }
export function asRecordingFrameId(raw: string): RecordingFrameId { return raw as RecordingFrameId; }
export function asShareSessionId(raw: string): ShareSessionId { return raw as ShareSessionId; }
export function asParticipantId(raw: string): ParticipantId { return raw as ParticipantId; }
export function asCloudTerminalId(raw: string): CloudTerminalId { return raw as CloudTerminalId; }
export function asAIRequestId(raw: string): AIRequestId { return raw as AIRequestId; }
export function asAIResponseId(raw: string): AIResponseId { return raw as AIResponseId; }
export function asAIAgentId(raw: string): AIAgentId { return raw as AIAgentId; }
export function asAIConversationId(raw: string): AIConversationId { return raw as AIConversationId; }
export function asAIMessageId(raw: string): AIMessageId { return raw as AIMessageId; }
export function asAIModelId(raw: string): AIModelId { return raw as AIModelId; }
export function asCacheEntryId(raw: string): CacheEntryId { return raw as CacheEntryId; }
export function asWorkspaceId(raw: string): WorkspaceId { return raw as WorkspaceId; }
export function asPanelId(raw: string): PanelId { return raw as PanelId; }
export function asTabId(raw: string): TabId { return raw as TabId; }
export function asTabGroupId(raw: string): TabGroupId { return raw as TabGroupId; }
export function asLayoutNodeId(raw: string): LayoutNodeId { return raw as LayoutNodeId; }
export function asGitRepositoryId(raw: string): GitRepositoryId { return raw as GitRepositoryId; }
export function asCommitHash(raw: string): CommitHash { return raw as CommitHash; }
export function asBranchName(raw: string): BranchName { return raw as BranchName; }
export function asRemoteName(raw: string): RemoteName { return raw as RemoteName; }
export function asTagName(raw: string): TagName { return raw as TagName; }
export function asStashId(raw: string): StashId { return raw as StashId; }
export function asHookId(raw: string): HookId { return raw as HookId; }
export function asFileId(raw: string): FileId { return raw as FileId; }
export function asDirectoryId(raw: string): DirectoryId { return raw as DirectoryId; }
export function asSearchResultId(raw: string): SearchResultId { return raw as SearchResultId; }
export function asIndexId(raw: string): IndexId { return raw as IndexId; }
export function asProjectId(raw: string): ProjectId { return raw as ProjectId; }
export function asDependencyId(raw: string): DependencyId { return raw as DependencyId; }
export function asEnvironmentId(raw: string): EnvironmentId { return raw as EnvironmentId; }
export function asTaskId(raw: string): TaskId { return raw as TaskId; }
export function asBuildId(raw: string): BuildId { return raw as BuildId; }
export function asPluginId(raw: string): PluginId { return raw as PluginId; }
export function asPluginManifestId(raw: string): PluginManifestId { return raw as PluginManifestId; }
export function asSettingsProfileId(raw: string): SettingsProfileId { return raw as SettingsProfileId; }
export function asDiffId(raw: string): DiffId { return raw as DiffId; }
export function asHunkId(raw: string): HunkId { return raw as HunkId; }
export function asReviewThreadId(raw: string): ReviewThreadId { return raw as ReviewThreadId; }
export function asReviewCommentId(raw: string): ReviewCommentId { return raw as ReviewCommentId; }
export function asAdapterId(raw: string): AdapterId { return raw as AdapterId; }
export function asEventChannelId(raw: string): EventChannelId { return raw as EventChannelId; }
export function asStorageNamespace(raw: string): StorageNamespace { return raw as StorageNamespace; }
export function asPermissionId(raw: string): PermissionId { return raw as PermissionId; }
export function asLogChannelId(raw: string): LogChannelId { return raw as LogChannelId; }

// ═══ §4 TIME & UTILITY HELPERS — pure helper functions ═══

export function now(): number {
  return Date.now();
}

export function clamp(value: number, min: number, max: number): number {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

export function deepFreeze<T>(obj: T): Readonly<T> {
  if (obj === null || obj === undefined || typeof obj !== 'object') return obj;
  Object.freeze(obj);
  const proto = Object.getOwnPropertyNames(obj);
  for (const name of proto) {
    const val = (obj as Record<string, unknown>)[name];
    if (typeof val === 'object' && val !== null && !Object.isFrozen(val)) {
      deepFreeze(val);
    }
  }
  return obj;
}

export function shallowEqual(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  for (const key of keysA) {
    if (a[key] !== b[key]) return false;
  }
  return true;
}

export function pick<T extends Record<string, unknown>, K extends keyof T>(obj: T, keys: K[]): Pick<T, K> {
  const result = {} as Pick<T, K>;
  for (const key of keys) {
    if (key in obj) (result as Record<string, unknown>)[key as string] = obj[key];
  }
  return result;
}

export function omit<T extends Record<string, unknown>, K extends keyof T>(obj: T, keys: K[]): Omit<T, K> {
  const result = { ...obj };
  for (const key of keys) {
    delete (result as Record<string, unknown>)[key as string];
  }
  return result as Omit<T, K>;
}

export function groupBy<T>(items: T[], key: (item: T) => string): Record<string, T[]> {
  const result: Record<string, T[]> = {};
  for (const item of items) {
    const k = key(item);
    if (!result[k]) result[k] = [];
    result[k].push(item);
  }
  return result;
}

export function unique<T>(items: T[], key?: (item: T) => string): T[] {
  if (!key) {
    return [...new Set(items)];
  }
  const seen = new Set<string>();
  const result: T[] = [];
  for (const item of items) {
    const k = key(item);
    if (!seen.has(k)) {
      seen.add(k);
      result.push(item);
    }
  }
  return result;
}

export function debounceValue(ms: number): number {
  return ms;
}

export function positionToOffset(content: string, position: Position): number {
  let offset = 0;
  const lines = content.split('\n');
  for (let i = 0; i < position.line && i < lines.length; i++) {
    offset += lines[i]!.length + 1;
  }
  offset += position.column;
  return Math.min(offset, content.length);
}

export function offsetToPosition(content: string, offset: number): Position {
  let line = 0;
  let col = 0;
  const clamped = Math.min(offset, content.length);
  for (let i = 0; i < clamped; i++) {
    if (content[i] === '\n') {
      line++;
      col = 0;
    } else {
      col++;
    }
  }
  return { line, column: col };
}

export function rangeContains(range: Range, position: Position): boolean {
  if (position.line < range.start.line || position.line > range.end.line) return false;
  if (position.line === range.start.line && position.column < range.start.column) return false;
  if (position.line === range.end.line && position.column > range.end.column) return false;
  return true;
}

export function rangesOverlap(a: Range, b: Range): boolean {
  if (comparePositions(a.end, b.start) <= 0) return false;
  if (comparePositions(b.end, a.start) <= 0) return false;
  return true;
}

export function rangeIsEmpty(range: Range): boolean {
  return range.start.line === range.end.line && range.start.column === range.end.column;
}

export function comparePositions(a: Position, b: Position): number {
  if (a.line < b.line) return -1;
  if (a.line > b.line) return 1;
  if (a.column < b.column) return -1;
  if (a.column > b.column) return 1;
  return 0;
}

export function mergeRanges(ranges: Range[]): Range[] {
  if (ranges.length === 0) return [];
  const sorted = [...ranges].sort((a, b) => comparePositions(a.start, b.start));
  const merged: Range[] = [sorted[0]!];
  for (let i = 1; i < sorted.length; i++) {
    const last = merged[merged.length - 1]!;
    const cur = sorted[i]!;
    if (comparePositions(cur.start, last.end) <= 0) {
      merged[merged.length - 1] = {
        start: last.start,
        end: comparePositions(cur.end, last.end) > 0 ? cur.end : last.end,
      };
    } else {
      merged.push(cur);
    }
  }
  return merged;
}

export function lineCount(content: string): number {
  if (content.length === 0) return 1;
  let count = 1;
  for (let i = 0; i < content.length; i++) {
    if (content[i] === '\n') count++;
  }
  return count;
}

// ═══ §5 DEFAULT CONFIGS & CONSTANTS — source of truth for all defaults ═══

export const MAX_DOCUMENT_SIZE = 50 * 1024 * 1024; // 50 MB
export const MAX_SCROLLBACK_LINES = 10000;
export const MAX_SEARCH_RESULTS = 10000;
export const MAX_UNDO_STACK = 5000;
export const MAX_COMPLETIONS = 500;
export const MAX_DIAGNOSTICS = 5000;

export const DEFAULT_EDITOR_CONFIG: EditorConfig = {
  tabSize: 4,
  insertSpaces: true,
  wordWrap: { mode: 'off', column: 80 },
  minimap: {
    enabled: true,
    side: 'right',
    showSlider: 'mouseover',
    renderCharacters: true,
    maxColumn: 120,
    scale: 1,
  },
  gutter: {
    lineNumbers: 'on',
    folding: true,
    glyphMargin: true,
    lineDecorationsWidth: 10,
  },
  scroll: {
    smoothScrolling: true,
    scrollBeyondLastLine: true,
    scrollBeyondLastColumn: 5,
    fastScrollSensitivity: 5,
    mouseWheelScrollSensitivity: 1,
  },
  stickyScroll: {
    enabled: true,
    maxLineCount: 5,
    defaultModel: 'outline',
  },
  formatOptions: {
    tabSize: 4,
    insertSpaces: true,
    trimAutoWhitespace: true,
    trimFinalNewlines: true,
    insertFinalNewline: true,
  },
  cursorStyle: 'bar',
  cursorBlinking: 'blink',
  lineHeight: 20,
  fontSize: 14,
  fontFamily: 'monospace',
  fontWeight: 'normal',
  fontLigatures: true,
  renderWhitespace: 'none',
  renderControlCharacters: false,
  renderLineHighlight: 'line',
  matchBrackets: 'always',
  autoClosingBrackets: 'languageDefined',
  autoClosingQuotes: 'languageDefined',
  autoSurround: 'languageDefined',
  autoIndent: 'advanced',
  dragAndDrop: true,
  emptySelectionClipboard: true,
  linkedEditing: false,
  occurrencesHighlight: true,
  showFoldingControls: 'mouseover',
  unfoldOnClickAfterEndOfLine: false,
  guides: {
    bracketPairs: 'active',
    bracketPairsHorizontal: 'active',
    highlightActiveBracketPair: true,
    indentation: true,
    highlightActiveIndentation: true,
  },
};

export const DEFAULT_TERMINAL_PROFILE: TerminalProfile = {
  name: 'Default',
  shell: 'zsh',
  args: [],
  env: {},
  cwd: '',
  rendering: {
    fontFamily: 'monospace',
    fontSize: 13,
    fontWeight: 'normal',
    lineHeight: 1.2,
    letterSpacing: 0,
    cursorStyle: 'block',
    cursorBlink: true,
    cursorWidth: 1,
    scrollback: 1000,
    theme: {
      background: '#1e1e1e',
      foreground: '#d4d4d4',
      cursor: '#aeafad',
      cursorAccent: '#000000',
      selectionBackground: '#264f78',
      selectionForeground: '#ffffff',
      ansiColors: [
        '#000000', '#cd3131', '#0dbc79', '#e5e510',
        '#2472c8', '#bc3fbc', '#11a8cd', '#e5e5e5',
        '#666666', '#f14c4c', '#23d18b', '#f5f543',
        '#3b8eea', '#d670d6', '#29b8db', '#ffffff',
      ] as unknown as readonly [string, string, string, string, string, string, string, string, string, string, string, string, string, string, string, string],
    },
    minimumContrastRatio: 4.5,
  },
  behavior: {
    autoClose: false,
    confirmOnExit: true,
    clearOnStart: false,
    copyOnSelection: false,
    rightClickBehavior: 'default',
    scrollToBottom: true,
    detectUrls: true,
    enableBell: true,
    wordSeparators: ' ()[]{}\',:;"',
  },
  keybindings: {},
};

export const DEFAULT_TERMINAL_RENDERING_CONFIG: TerminalRenderingConfig = DEFAULT_TERMINAL_PROFILE.rendering;

export const DEFAULT_TERMINAL_BEHAVIOR_CONFIG: TerminalBehaviorConfig = DEFAULT_TERMINAL_PROFILE.behavior;

export const DEFAULT_PTY_CONFIG: PTYConfig = {
  shell: 'zsh',
  args: [],
  env: {},
  cwd: '',
  cols: 80,
  rows: 24,
  encoding: 'utf-8',
  handleFlowControl: false,
};

export const DEFAULT_TERMINAL_THEME: TerminalTheme = DEFAULT_TERMINAL_PROFILE.rendering.theme;

export const DEFAULT_SCROLLBACK_CONFIG: ScrollbackConfig = {
  maxLines: 1000,
  reflow: true,
};

export const DEFAULT_MULTIPLEXER_CONFIG: MultiplexerConfig = {
  prefix: 'ctrl+b',
  mouse: true,
  statusBar: {
    enabled: true,
    position: 'bottom',
    leftContent: '',
    rightContent: '',
    style: '',
  },
};

export const DEFAULT_RECORDING_CONFIG: RecordingConfig = {
  maxDuration: 3600000,
  maxFrames: 100000,
  compression: true,
  autoSave: false,
  captureKeystrokes: false,
};

export const DEFAULT_SHARE_CONFIG: ShareConfig = {
  maxParticipants: 10,
  requireAuth: true,
  allowRecording: false,
  encryptionEnabled: true,
};

export const DEFAULT_CLOUD_RESOURCE_LIMITS: ResourceLimits = {
  cpuCores: 1,
  memoryMb: 512,
  storageMb: 1024,
  networkMbps: 100,
};

export const DEFAULT_AI_MODEL_CONFIG: AIModelConfig = {
  temperature: 0.7,
  topP: 1,
  maxTokens: 4096,
  stopSequences: [],
};

export const DEFAULT_AI_CACHE_CONFIG: AICacheConfig = {
  maxEntries: 1000,
  maxSizeMb: 100,
  defaultTtlMs: 300000,
  similarityThreshold: 0.85,
  evictionPolicy: 'usefulness',
  compressionEnabled: true,
};

export const DEFAULT_AI_SECURITY_CONFIG: AISecurityConfig = {
  inputValidation: {
    detectInjection: true,
    detectPromptManipulation: true,
    detectDataExtraction: true,
    maxInputLength: 100000,
    blockedPatterns: [],
  },
  outputFiltering: {
    filterSensitiveData: true,
    filterCompliance: true,
    maxOutputLength: 50000,
    contentFilters: [],
  },
  codeScanning: {
    detectSqlInjection: true,
    detectXss: true,
    detectPathTraversal: true,
    detectHardcodedSecrets: true,
    detectUnsafeOps: true,
    customRules: [],
  },
  compliance: {
    frameworks: [],
    dataClassification: 'internal',
    piiDetection: true,
    retentionDays: 90,
  },
  audit: {
    enabled: true,
    logLevel: 'standard',
    retentionDays: 90,
    includeInputs: false,
    includeOutputs: false,
  },
};

export const DEFAULT_WORKSPACE_LAYOUT: WorkspaceLayout = {
  type: 'split',
  rootNode: {
    id: asLayoutNodeId('root'),
    type: 'split',
    direction: 'horizontal',
    children: [],
  },
  orientation: 'horizontal',
};

export const DEFAULT_GIT_CONFIG: GitConfig = {
  defaultBranch: 'main',
  autoSign: false,
  mergeStrategy: 'recursive',
};

export const DEFAULT_FILE_WATCH_CONFIG: FileWatchConfig = {
  paths: [],
  recursive: true,
  ignorePatterns: ['**/node_modules/**', '**/.git/**'],
  debounceMs: 100,
  usePolling: false,
};

export const DEFAULT_SEARCH_QUERY: SearchQuery = {
  pattern: '',
  isRegex: false,
  isCaseSensitive: false,
  isWholeWord: false,
  maxResults: MAX_SEARCH_RESULTS,
};

export const DEFAULT_SANDBOX_RESOURCE_LIMITS: SandboxResourceLimits = {
  maxCpuMs: 10000,
  maxMemoryMb: 256,
  maxStorageMb: 100,
  maxNetworkRequests: 100,
  maxApiCallsPerMinute: 60,
};

export const DEFAULT_SETTINGS_SCOPE_ORDER: SettingsScope[] = [
  'default', 'user', 'workspace', 'folder', 'language', 'runtime',
];

export const DEFAULT_DIFF_CONFIG = {
  algorithm: 'myers' as DiffAlgorithm,
  contextLines: 3,
};

export const DEFAULT_PERFORMANCE_BUDGET: PerformanceBudget = {
  maxFrameTimeMs: 16,
  targetFps: 60,
  maxMemoryMb: 512,
  maxLongTaskMs: 50,
};

export const DEFAULT_FORMAT_OPTIONS: FormatOptions = {
  tabSize: 4,
  insertSpaces: true,
  trimAutoWhitespace: true,
  trimFinalNewlines: true,
  insertFinalNewline: true,
};

// ═══ §6 EMPTY STATE FACTORIES — valid empty/initial states for every major entity ═══

export function emptyDocumentState(id: DocumentId, language?: LanguageId): DocumentState {
  return {
    id,
    content: '',
    language: language ?? asLanguageId('plaintext'),
    version: 0,
    encoding: 'utf-8',
    lineEnding: 'lf',
    byteOrderMark: 'none',
    lineCount: 1,
    charCount: 0,
    isDirty: false,
    isReadOnly: false,
    eol: '\n',
  };
}

export function emptyCursorGroup(id: CursorGroupId): CursorGroup {
  return {
    id,
    cursors: [{ position: { line: 0, column: 0 }, desiredColumn: 0 }],
    primaryIndex: 0,
  };
}

export function emptyHistoryState(): HistoryState {
  return {
    entries: [],
    currentIndex: -1,
    branches: [],
    activeBranch: 'main',
  };
}

export function emptyViewportState(id: ViewportId): ViewportState {
  return {
    id,
    startLine: 0,
    endLine: 0,
    startColumn: 0,
    scrollTop: 0,
    scrollLeft: 0,
    width: 0,
    height: 0,
    lineHeight: 20,
    charWidth: 8,
  };
}

export function emptyEditorState(id: EditorInstanceId, documentId: DocumentId): EditorState {
  return {
    id,
    documentId,
    cursorGroup: emptyCursorGroup(asCursorGroupId(generateId('cg'))),
    viewport: emptyViewportState(asViewportId(generateId('vp'))),
    mode: 'normal',
    focus: { hasFocus: false, focusTarget: 'none' },
    readOnly: 'off',
    config: DEFAULT_EDITOR_CONFIG,
  };
}

export function emptyTerminalSessionState(id: TerminalSessionId): TerminalSessionState {
  return {
    id,
    shell: 'zsh',
    status: 'starting',
    env: {},
    cwd: '',
    history: { entries: [], maxSize: 1000 },
    jobs: [],
    promptConfig: {
      template: '$ ',
      showGitBranch: false,
      showGitStatus: false,
      showCwd: true,
      showUser: false,
      showHost: false,
      colors: {},
    },
  };
}

export function emptyTerminalBufferState(id: TerminalBufferId): TerminalBufferState {
  return {
    id,
    lines: [],
    altLines: [],
    activeBuffer: 'normal',
    cursorState: { row: 0, col: 0, style: 'block', visible: true, blink: true },
    scrollback: DEFAULT_SCROLLBACK_CONFIG,
    viewport: { startLine: 0, endLine: 0, scrollTop: 0, height: 0 },
    dirtyRegions: [],
    modificationSeq: 0,
  };
}

export function emptyPTYState(id: PTYProcessId): PTYState {
  return {
    processId: id,
    pid: 0,
    shell: 'zsh',
    cwd: '',
    cols: 80,
    rows: 24,
    status: 'starting',
    resourceUsage: { cpuPercent: 0, memoryBytes: 0, openFiles: 0 },
  };
}

export function emptyTerminalState(id: TerminalId): TerminalState {
  return {
    id,
    name: 'Terminal',
    type: 'local',
    status: 'initializing',
    session: emptyTerminalSessionState(asTerminalSessionId(generateId('ts'))),
    buffer: emptyTerminalBufferState(asTerminalBufferId(generateId('tb'))),
    pty: emptyPTYState(asPTYProcessId(generateId('pty'))),
    profile: DEFAULT_TERMINAL_PROFILE,
    securityContext: { userId: '', permissions: [], commandRiskLevel: 'safe' },
    metrics: { cpuUsage: 0, memoryUsage: 0, bytesRead: 0, bytesWritten: 0, throughput: 0 },
  };
}

export function emptyMultiplexerState(): MultiplexerState {
  return {
    type: 'built-in',
    sessions: [],
    config: DEFAULT_MULTIPLEXER_CONFIG,
  };
}

export function emptyRecordingState(id: RecordingId, terminalId: TerminalId): RecordingState {
  return {
    id,
    terminalId,
    status: 'idle',
    frames: [],
    metadata: {
      duration: 0,
      frameCount: 0,
      commandCount: 0,
      startedAt: 0,
      tags: [],
    },
    config: DEFAULT_RECORDING_CONFIG,
  };
}

export function emptyTerminalCollabState(shareSessionId: ShareSessionId): TerminalCollabState {
  return {
    shareSessionId,
    isHost: true,
    participants: [],
    permissions: { read: true, write: false, execute: false, share: false, manage: false },
    chat: [],
    config: DEFAULT_SHARE_CONFIG,
  };
}

export function emptyCloudTerminalState(id: CloudTerminalId): CloudTerminalState {
  return {
    id,
    provider: 'local-docker',
    connection: {
      endpoint: '',
      protocol: 'websocket',
      status: 'disconnected',
      latency: 0,
      encrypted: true,
    },
    resourceLimits: DEFAULT_CLOUD_RESOURCE_LIMITS,
  };
}

export function emptyTerminalAIState(): TerminalAIState {
  return {
    enabled: false,
    suggestions: [],
    recentAnalyses: [],
    privacySettings: {
      storeHistory: true,
      shareData: false,
      anonymizeData: true,
      retentionDays: 30,
    },
  };
}

export function emptyAIConversationState(id: AIConversationId): AIConversationState {
  return {
    id,
    type: 'chat',
    participants: [],
    messages: [],
    context: {},
    createdAt: now(),
    updatedAt: now(),
  };
}

export function emptyAIAgentState(id: AIAgentId, type: AIAgentType): AIAgentState {
  return {
    id,
    name: type,
    type,
    status: 'idle',
    capabilities: agentCapabilities(type),
    memory: { shortTerm: {}, longTerm: {}, episodic: [], working: [] },
    performance: { tasksCompleted: 0, tasksSucceeded: 0, averageDuration: 0, averageQuality: 0 },
    config: { maxConcurrentTasks: 1, learningRate: 0.1, autonomyLevel: 'supervised' },
  };
}

export function emptyAICacheState(): AICacheState {
  return {
    entries: new Map(),
    stats: {
      hitRate: 0,
      missRate: 0,
      totalHits: 0,
      totalMisses: 0,
      totalRequests: 0,
      averageResponseTimeMs: 0,
      cacheSize: 0,
      evictionCount: 0,
    },
    config: DEFAULT_AI_CACHE_CONFIG,
  };
}

export function emptyPersonalizationState(userId: string): PersonalizationState {
  return {
    userId,
    profile: {
      codingStyle: {
        naming: 'camelCase',
        indentation: 'spaces-2',
        quoteStyle: 'single',
        semicolons: true,
        trailingComma: 'es5',
        lineLength: 100,
      },
      skillLevel: 'intermediate',
      preferences: {
        verbosity: 'standard',
        explainCode: true,
        suggestTests: true,
        autoComplete: true,
      },
      competencies: {},
    },
    behaviorPatterns: [],
    learningHistory: [],
    adaptationConfig: {
      learningRate: 0.1,
      adaptationSpeed: 'medium',
      privacyLevel: 'standard',
    },
  };
}

export function emptyWorkspaceState(id: WorkspaceId): WorkspaceState {
  return {
    id,
    name: '',
    rootFolders: [],
    layout: DEFAULT_WORKSPACE_LAYOUT,
    panels: [],
    editors: [],
    terminals: [],
    theme: 'dark',
    settings: {},
  };
}

export function emptyGitRepositoryState(id: GitRepositoryId, rootPath: string): GitRepositoryState {
  return {
    id,
    rootPath,
    head: { type: 'branch', name: asBranchName('main'), commitHash: asCommitHash('') },
    branches: [],
    remotes: [],
    tags: [],
    stashes: [],
    status: [],
    config: DEFAULT_GIT_CONFIG,
  };
}

export function emptyProjectState(id: ProjectId, rootPath: string): ProjectState {
  return {
    id,
    rootPath,
    name: '',
    type: 'single',
    packageManager: 'unknown',
    tasks: [],
    config: {},
  };
}

export function emptyPluginState(id: PluginId, manifest: PluginManifest): PluginState {
  return {
    id,
    manifest,
    status: 'discovered',
    activationEvents: manifest.activationEvents,
    contributions: manifest.contributes,
    resourceUsage: { cpuMs: 0, memoryMb: 0, activationTimeMs: 0, apiCalls: 0 },
  };
}

export function emptySettingsState(): SettingsState {
  return {
    scopes: new Map(),
    schema: new Map(),
    profiles: [],
  };
}

export function emptyDiffResult(id: DiffId): DiffResult {
  return {
    id,
    algorithm: 'myers',
    original: '',
    modified: '',
    hunks: [],
    stats: { additions: 0, deletions: 0, changes: 0 },
  };
}

export function emptyReviewState(): ReviewState {
  return {
    status: 'draft',
    threads: [],
  };
}

export function emptyPerformanceSnapshot(): PerformanceSnapshot {
  return {
    timestamp: now(),
    fps: 60,
    frameTimeMs: 16,
    longTasks: 0,
    memoryUsageMb: 0,
    gcPauseMs: 0,
    renderQueueDepth: 0,
  };
}

// ═══ §7 CONFIG MERGE HELPERS — type-safe shallow merges for every config ═══

export function mergeEditorConfig(base: EditorConfig, overrides: Partial<EditorConfig>): EditorConfig {
  return { ...base, ...overrides };
}

export function mergeTerminalProfile(base: TerminalProfile, overrides: Partial<TerminalProfile>): TerminalProfile {
  return { ...base, ...overrides };
}

export function mergePTYConfig(base: PTYConfig, overrides: Partial<PTYConfig>): PTYConfig {
  return { ...base, ...overrides };
}

export function mergeAIModelConfig(base: AIModelConfig, overrides: Partial<AIModelConfig>): AIModelConfig {
  return { ...base, ...overrides };
}

export function mergeWorkspaceLayout(base: WorkspaceLayout, overrides: Partial<WorkspaceLayout>): WorkspaceLayout {
  return { ...base, ...overrides };
}

export function mergeGitConfig(base: GitConfig, overrides: Partial<GitConfig>): GitConfig {
  return { ...base, ...overrides };
}

export function mergeSearchQuery(base: SearchQuery, overrides: Partial<SearchQuery>): SearchQuery {
  return { ...base, ...overrides };
}

export function mergeSandboxConfig(base: SandboxConfig, overrides: Partial<SandboxConfig>): SandboxConfig {
  return { ...base, ...overrides };
}

export function mergeFormatOptions(base: FormatOptions, overrides: Partial<FormatOptions>): FormatOptions {
  return { ...base, ...overrides };
}

export function mergePerformanceBudget(base: PerformanceBudget, overrides: Partial<PerformanceBudget>): PerformanceBudget {
  return { ...base, ...overrides };
}

export function resolveSettings(scopes: Map<SettingsScope, Record<string, unknown>>, key: string): unknown {
  for (let i = DEFAULT_SETTINGS_SCOPE_ORDER.length - 1; i >= 0; i--) {
    const scope = DEFAULT_SETTINGS_SCOPE_ORDER[i];
    const scopeValues = scopes.get(scope!);
    if (scopeValues && key in scopeValues) {
      return scopeValues[key];
    }
  }
  return undefined;
}

// ═══ §8 DOCUMENT ORCHESTRATION — pure functions for document operations ═══

export function applyEdit(state: DocumentState, op: EditOperation): EditorResult<DocumentState> {
  const startOffset = positionToOffset(state.content, op.range.start);
  const endOffset = positionToOffset(state.content, op.range.end);

  let newContent: string;
  switch (op.kind) {
    case 'insert':
      newContent = insertText(state.content, startOffset, op.text);
      break;
    case 'delete':
      newContent = deleteText(state.content, startOffset, endOffset);
      break;
    case 'replace':
      newContent = replaceText(state.content, startOffset, endOffset, op.text);
      break;
    default:
      newContent = replaceText(state.content, startOffset, endOffset, op.text);
      break;
  }

  return ok({
    ...state,
    content: newContent,
    version: state.version + 1,
    lineCount: lineCount(newContent),
    charCount: newContent.length,
    isDirty: true,
  }, 'EDIT_APPLIED', `Applied ${op.kind} edit`, [{
    kind: 'changed',
    domain: 'document',
    timestamp: now(),
    payload: { operation: op.kind },
  }]);
}

export function applyEdits(state: DocumentState, ops: EditOperation[]): EditorResult<DocumentState> {
  let current: EditorResult<DocumentState> = ok(state);
  for (const op of ops) {
    current = andThen(current, (s) => applyEdit(s, op));
  }
  return current;
}

export function insertText(content: string, offset: number, text: string): string {
  return content.slice(0, offset) + text + content.slice(offset);
}

export function deleteText(content: string, start: number, end: number): string {
  return content.slice(0, start) + content.slice(end);
}

export function replaceText(content: string, start: number, end: number, text: string): string {
  return content.slice(0, start) + text + content.slice(end);
}

export function getLineContent(content: string, lineNumber: number): string {
  const lines = content.split('\n');
  if (lineNumber < 0 || lineNumber >= lines.length) return '';
  return lines[lineNumber]!;
}

export function getLineRange(content: string, lineNumber: number): Range {
  const lines = content.split('\n');
  if (lineNumber < 0 || lineNumber >= lines.length) {
    return { start: { line: 0, column: 0 }, end: { line: 0, column: 0 } };
  }
  return {
    start: { line: lineNumber, column: 0 },
    end: { line: lineNumber, column: lines[lineNumber]!.length },
  };
}

export function getWordAtPosition(content: string, position: Position): { word: string; range: Range } | null {
  const line = getLineContent(content, position.line);
  if (!line) return null;
  const wordPattern = /[a-zA-Z_$][a-zA-Z0-9_$]*/g;
  let match: RegExpExecArray | null;
  while ((match = wordPattern.exec(line)) !== null) {
    const start = match.index;
    const end = start + match[0].length;
    if (position.column >= start && position.column <= end) {
      return {
        word: match[0],
        range: {
          start: { line: position.line, column: start },
          end: { line: position.line, column: end },
        },
      };
    }
  }
  return null;
}

export function detectEncoding(bytes: Uint8Array): DocumentEncoding {
  if (bytes.length >= 3 && bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF) return 'utf-8';
  if (bytes.length >= 2 && bytes[0] === 0xFF && bytes[1] === 0xFE) return 'utf-16le';
  if (bytes.length >= 2 && bytes[0] === 0xFE && bytes[1] === 0xFF) return 'utf-16be';
  return 'utf-8';
}

export function detectLineEnding(content: string): LineEnding {
  const crlfCount = (content.match(/\r\n/g) ?? []).length;
  const lfCount = (content.match(/(?<!\r)\n/g) ?? []).length;
  const crCount = (content.match(/\r(?!\n)/g) ?? []).length;
  if (crlfCount > lfCount && crlfCount > crCount) return 'crlf';
  if (crCount > lfCount) return 'cr';
  return 'lf';
}

export function normalizeLineEndings(content: string, target: LineEnding): string {
  const normalized = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  switch (target) {
    case 'crlf': return normalized.replace(/\n/g, '\r\n');
    case 'cr': return normalized.replace(/\n/g, '\r');
    default: return normalized;
  }
}

export function createSnapshot(state: DocumentState): DocumentSnapshot {
  return {
    id: generateId('snap'),
    documentId: state.id,
    version: state.version,
    content: state.content,
    timestamp: now(),
  };
}

// ═══ §9 CURSOR ORCHESTRATION — cursor movement, multi-cursor, selection ═══

export function moveCursor(cursor: CursorState, direction: 'up' | 'down' | 'left' | 'right', content: string): CursorState {
  const lines = content.split('\n');
  const { line, column } = cursor.position;
  switch (direction) {
    case 'up': {
      if (line === 0) return cursor;
      const newLine = line - 1;
      const newCol = Math.min(cursor.desiredColumn, lines[newLine]?.length ?? 0);
      return { ...cursor, position: { line: newLine, column: newCol } };
    }
    case 'down': {
      if (line >= lines.length - 1) return cursor;
      const newLine = line + 1;
      const newCol = Math.min(cursor.desiredColumn, lines[newLine]?.length ?? 0);
      return { ...cursor, position: { line: newLine, column: newCol } };
    }
    case 'left': {
      if (column > 0) {
        return { ...cursor, position: { line, column: column - 1 }, desiredColumn: column - 1 };
      }
      if (line > 0) {
        const prevLen = lines[line - 1]?.length ?? 0;
        return { ...cursor, position: { line: line - 1, column: prevLen }, desiredColumn: prevLen };
      }
      return cursor;
    }
    case 'right': {
      const lineLen = lines[line]?.length ?? 0;
      if (column < lineLen) {
        return { ...cursor, position: { line, column: column + 1 }, desiredColumn: column + 1 };
      }
      if (line < lines.length - 1) {
        return { ...cursor, position: { line: line + 1, column: 0 }, desiredColumn: 0 };
      }
      return cursor;
    }
  }
}

export function moveCursorToPosition(cursor: CursorState, position: Position): CursorState {
  return { ...cursor, position, desiredColumn: position.column };
}

export function moveCursorWordLeft(cursor: CursorState, content: string): CursorState {
  const offset = positionToOffset(content, cursor.position);
  let i = offset - 1;
  while (i >= 0 && /\s/.test(content[i]!)) i--;
  while (i >= 0 && /\w/.test(content[i]!)) i--;
  const newPos = offsetToPosition(content, i + 1);
  return { ...cursor, position: newPos, desiredColumn: newPos.column };
}

export function moveCursorWordRight(cursor: CursorState, content: string): CursorState {
  const offset = positionToOffset(content, cursor.position);
  let i = offset;
  while (i < content.length && /\w/.test(content[i]!)) i++;
  while (i < content.length && /\s/.test(content[i]!)) i++;
  const newPos = offsetToPosition(content, i);
  return { ...cursor, position: newPos, desiredColumn: newPos.column };
}

export function moveCursorToLineStart(cursor: CursorState, _content: string): CursorState {
  return { ...cursor, position: { line: cursor.position.line, column: 0 }, desiredColumn: 0 };
}

export function moveCursorToLineEnd(cursor: CursorState, content: string): CursorState {
  const line = getLineContent(content, cursor.position.line);
  const col = line.length;
  return { ...cursor, position: { line: cursor.position.line, column: col }, desiredColumn: col };
}

export function addCursorAbove(group: CursorGroup, content: string): CursorGroup {
  const primary = group.cursors[group.primaryIndex]!;
  if (primary.position.line === 0) return group;
  const newCursor = moveCursor(primary, 'up', content);
  return { ...group, cursors: [newCursor, ...group.cursors] };
}

export function addCursorBelow(group: CursorGroup, content: string): CursorGroup {
  const primary = group.cursors[group.primaryIndex]!;
  const lines = content.split('\n');
  if (primary.position.line >= lines.length - 1) return group;
  const newCursor = moveCursor(primary, 'down', content);
  return { ...group, cursors: [...group.cursors, newCursor] };
}

export function addCursorAtPosition(group: CursorGroup, position: Position): CursorGroup {
  const newCursor: CursorState = { position, desiredColumn: position.column };
  return { ...group, cursors: [...group.cursors, newCursor] };
}

export function selectAll(content: string): Selection {
  const lines = content.split('\n');
  const lastLine = lines.length - 1;
  const lastCol = lines[lastLine]!.length;
  return {
    anchor: { line: 0, column: 0 },
    active: { line: lastLine, column: lastCol },
    isReversed: false,
  };
}

export function selectWord(content: string, position: Position): Selection | null {
  const word = getWordAtPosition(content, position);
  if (!word) return null;
  return {
    anchor: word.range.start,
    active: word.range.end,
    isReversed: false,
  };
}

export function selectLine(content: string, lineNumber: number): Selection {
  const range = getLineRange(content, lineNumber);
  return { anchor: range.start, active: range.end, isReversed: false };
}

export function expandSelection(selection: Selection, content: string, unit: 'word' | 'line' | 'bracket' | 'all'): Selection {
  switch (unit) {
    case 'all': return selectAll(content);
    case 'line': return selectLine(content, selection.anchor.line);
    case 'word': {
      const wordSel = selectWord(content, selection.anchor);
      return wordSel ?? selection;
    }
    case 'bracket': return selection; // bracket matching requires syntax analysis
  }
}

export function columnSelect(start: Position, end: Position): ColumnSelection {
  return {
    startLine: Math.min(start.line, end.line),
    endLine: Math.max(start.line, end.line),
    startColumn: Math.min(start.column, end.column),
    endColumn: Math.max(start.column, end.column),
  };
}

export function sortCursors(cursors: CursorState[]): CursorState[] {
  return [...cursors].sort((a, b) => comparePositions(a.position, b.position));
}

export function mergeCursors(cursors: CursorState[]): CursorState[] {
  if (cursors.length <= 1) return cursors;
  const sorted = sortCursors(cursors);
  const merged: CursorState[] = [sorted[0]!];
  for (let i = 1; i < sorted.length; i++) {
    const last = merged[merged.length - 1]!;
    if (comparePositions(sorted[i]!.position, last.position) === 0) continue;
    merged.push(sorted[i]!);
  }
  return merged;
}

// ═══ §10 HISTORY ORCHESTRATION — undo/redo with branching ═══

export function pushHistory(state: HistoryState, entry: HistoryEntry): HistoryState {
  const truncated = state.entries.slice(0, state.currentIndex + 1);
  const entries = [...truncated, entry];
  if (entries.length > MAX_UNDO_STACK) entries.shift();
  return {
    ...state,
    entries,
    currentIndex: entries.length - 1,
  };
}

export function undo(state: HistoryState): EditorResult<{ history: HistoryState; operations: EditOperation[] }> {
  if (state.currentIndex < 0) {
    return err('NO_UNDO', 'Nothing to undo');
  }
  const entry = state.entries[state.currentIndex]!;
  const inverseOps = entry.operations.map((op) => ({
    ...op,
    text: '',
    origin: 'undo' as EditOrigin,
  }));
  return ok({
    history: { ...state, currentIndex: state.currentIndex - 1 },
    operations: inverseOps,
  }, 'UNDO', 'Undo applied');
}

export function redo(state: HistoryState): EditorResult<{ history: HistoryState; operations: EditOperation[] }> {
  if (state.currentIndex >= state.entries.length - 1) {
    return err('NO_REDO', 'Nothing to redo');
  }
  const entry = state.entries[state.currentIndex + 1]!;
  return ok({
    history: { ...state, currentIndex: state.currentIndex + 1 },
    operations: entry.operations,
  }, 'REDO', 'Redo applied');
}

export function createBranch(state: HistoryState): HistoryState {
  const branchId = generateId('branch');
  const newBranch: HistoryBranch = {
    id: branchId,
    parentEntryId: state.entries[state.currentIndex]?.id ?? '',
    entries: [],
  };
  return {
    ...state,
    branches: [...state.branches, newBranch],
    activeBranch: branchId,
  };
}

export function switchBranch(state: HistoryState, branchId: string): EditorResult<HistoryState> {
  const branch = state.branches.find((b) => b.id === branchId);
  if (!branch) return err('BRANCH_NOT_FOUND', `Branch ${branchId} not found`);
  return ok({
    ...state,
    activeBranch: branchId,
  }, 'BRANCH_SWITCHED', `Switched to branch ${branchId}`);
}

export function shouldCreateUndoStop(lastEntry: HistoryEntry | null, kind: UndoStopKind, elapsed: number): boolean {
  if (kind === 'explicit') return true;
  if (kind === 'cursor-move') return true;
  if (kind === 'content-type-change') return true;
  if (kind === 'timeout' && elapsed > 1000) return true;
  if (!lastEntry) return true;
  return false;
}

export function groupOperations(ops: EditOperation[], groupId: string): HistoryEntry {
  return {
    id: generateId('hist'),
    operations: ops,
    beforeCursors: [],
    afterCursors: [],
    timestamp: now(),
    groupId,
  };
}

// ═══ §11 MARKER ORCHESTRATION — bookmarks, breakpoints, annotations ═══

export function addMarker(markers: MarkerState[], marker: MarkerState): MarkerState[] {
  return [...markers, marker];
}

export function removeMarker(markers: MarkerState[], id: MarkerId): MarkerState[] {
  return markers.filter((m) => m.id !== id);
}

export function updateMarkerRange(markers: MarkerState[], id: MarkerId, range: Range): MarkerState[] {
  return markers.map((m) => m.id === id ? { ...m, range } : m);
}

export function getMarkersInRange(markers: MarkerState[], range: Range): MarkerState[] {
  return markers.filter((m) => rangesOverlap(m.range, range));
}

export function getMarkersByKind(markers: MarkerState[], kind: MarkerKind): MarkerState[] {
  return markers.filter((m) => m.kind === kind);
}

export function adjustMarkersForEdit(markers: MarkerState[], edit: EditOperation): MarkerState[] {
  const editStart = edit.range.start;
  const editEnd = edit.range.end;
  const insertedLines = edit.text.split('\n').length - 1;
  const deletedLines = editEnd.line - editStart.line;
  const lineDelta = insertedLines - deletedLines;

  return markers.map((marker) => {
    const mStart = marker.range.start;
    const mEnd = marker.range.end;

    if (comparePositions(mEnd, editStart) <= 0) return marker;

    if (comparePositions(mStart, editEnd) >= 0) {
      const newStartLine = mStart.line + lineDelta;
      const newEndLine = mEnd.line + lineDelta;
      let newStartCol = mStart.column;
      let newEndCol = mEnd.column;
      if (mStart.line === editEnd.line) {
        const lastInsertedLine = edit.text.split('\n').pop() ?? '';
        newStartCol = mStart.column - editEnd.column + lastInsertedLine.length;
      }
      if (mEnd.line === editEnd.line) {
        const lastInsertedLine = edit.text.split('\n').pop() ?? '';
        newEndCol = mEnd.column - editEnd.column + lastInsertedLine.length;
      }
      return {
        ...marker,
        range: {
          start: { line: newStartLine, column: newStartCol },
          end: { line: newEndLine, column: newEndCol },
        },
      };
    }

    switch (marker.stickiness) {
      case 'never-grows':
        return marker;
      case 'always-grows':
        return {
          ...marker,
          range: { start: mStart, end: { line: mEnd.line + lineDelta, column: mEnd.column } },
        };
      default:
        return marker;
    }
  });
}

// ═══ §12 TOKENIZER ORCHESTRATION — tokenization, classification, pattern matching ═══

export function tokenizeLine(line: string, state: TokenizerState, grammar: GrammarDefinition): { tokens: Token[]; endState: TokenizerState } {
  const tokens: Token[] = [];
  let offset = 0;

  while (offset < line.length) {
    let matched = false;
    for (const pattern of grammar.patterns) {
      const result = matchPattern(line, pattern, offset);
      if (result) {
        const scopes = pattern.name ? [grammar.scopeName, pattern.name] : [grammar.scopeName];
        tokens.push({
          type: classifyToken(scopes),
          startOffset: offset,
          endOffset: offset + result.match.length,
          scopes,
        });
        offset += result.match.length;
        matched = true;
        break;
      }
    }
    if (!matched) {
      tokens.push({
        type: 'identifier',
        startOffset: offset,
        endOffset: offset + 1,
        scopes: [grammar.scopeName],
      });
      offset++;
    }
  }

  return { tokens, endState: state };
}

export function classifyToken(scopes: string[]): TokenType {
  const joined = scopes.join(' ');
  if (joined.includes('keyword')) return 'keyword';
  if (joined.includes('string')) return 'string';
  if (joined.includes('comment')) return 'comment';
  if (joined.includes('number') || joined.includes('constant.numeric')) return 'number';
  if (joined.includes('operator')) return 'operator';
  if (joined.includes('punctuation')) return 'punctuation';
  if (joined.includes('type')) return 'type';
  if (joined.includes('decorator') || joined.includes('annotation')) return 'decorator';
  if (joined.includes('regexp') || joined.includes('regex')) return 'regex';
  if (joined.includes('template')) return 'template';
  if (joined.includes('embedded')) return 'embedded';
  if (joined.includes('invalid')) return 'invalid';
  return 'identifier';
}

export function matchPattern(text: string, pattern: GrammarPattern, offset: number): { match: string; captures: Record<string, string> } | null {
  if (!pattern.match) return null;
  try {
    const regex = new RegExp(pattern.match, 'g');
    regex.lastIndex = offset;
    const match = regex.exec(text);
    if (!match || match.index !== offset) return null;
    const captures: Record<string, string> = {};
    if (pattern.captures) {
      for (const [key, value] of Object.entries(pattern.captures)) {
        const idx = parseInt(key, 10);
        if (match[idx]) captures[value.name] = match[idx];
      }
    }
    return { match: match[0], captures };
  } catch {
    return null;
  }
}

export function resolveScope(scopes: string[], repository: Record<string, GrammarPattern>): GrammarPattern | null {
  for (const scope of scopes) {
    const key = scope.split('.').pop() ?? '';
    if (repository[key]) return repository[key];
  }
  return null;
}

export function incrementalRetokenize(lines: TokenLine[], changedRange: Range, grammar: GrammarDefinition): TokenLine[] {
  const result = [...lines];
  const startLine = changedRange.start.line;
  const endLine = Math.min(changedRange.end.line, lines.length - 1);

  let state: TokenizerState = startLine > 0 && lines[startLine - 1]
    ? lines[startLine - 1]!.state
    : { ruleStack: [], languageId: grammar.id as unknown as LanguageId };

  for (let i = startLine; i <= endLine && i < result.length; i++) {
    const lineContent = result[i]!.tokens.map(() => '').join('');
    const tokenized = tokenizeLine(lineContent, state, grammar);
    result[i] = {
      lineNumber: i,
      tokens: tokenized.tokens,
      state: tokenized.endState,
      version: (result[i]!.version ?? 0) + 1,
    };
    state = tokenized.endState;
  }
  return result;
}

const LANGUAGE_EXTENSIONS: Record<string, string> = {
  '.ts': 'typescript', '.tsx': 'typescriptreact', '.js': 'javascript', '.jsx': 'javascriptreact',
  '.py': 'python', '.rs': 'rust', '.go': 'go', '.java': 'java', '.c': 'c', '.cpp': 'cpp',
  '.h': 'c', '.hpp': 'cpp', '.cs': 'csharp', '.rb': 'ruby', '.php': 'php', '.swift': 'swift',
  '.kt': 'kotlin', '.scala': 'scala', '.r': 'r', '.lua': 'lua', '.zig': 'zig', '.nim': 'nim',
  '.ex': 'elixir', '.erl': 'erlang', '.hs': 'haskell', '.ml': 'ocaml', '.fs': 'fsharp',
  '.clj': 'clojure', '.dart': 'dart', '.v': 'v', '.sql': 'sql', '.sh': 'shellscript',
  '.bash': 'shellscript', '.zsh': 'shellscript', '.fish': 'fish', '.ps1': 'powershell',
  '.html': 'html', '.htm': 'html', '.css': 'css', '.scss': 'scss', '.less': 'less',
  '.json': 'json', '.yaml': 'yaml', '.yml': 'yaml', '.toml': 'toml', '.xml': 'xml',
  '.md': 'markdown', '.mdx': 'mdx', '.svelte': 'svelte', '.vue': 'vue',
  '.dockerfile': 'dockerfile', '.tf': 'terraform', '.proto': 'protobuf',
  '.graphql': 'graphql', '.gql': 'graphql', '.wasm': 'wasm',
};

export function detectLanguage(filename: string, firstLine?: string, _content?: string): LanguageId | null {
  const ext = filename.includes('.') ? '.' + filename.split('.').pop()!.toLowerCase() : '';
  if (LANGUAGE_EXTENSIONS[ext]) return asLanguageId(LANGUAGE_EXTENSIONS[ext]);

  const base = filename.split('/').pop()?.toLowerCase() ?? '';
  if (base === 'dockerfile' || base === 'containerfile') return asLanguageId('dockerfile');
  if (base === 'makefile') return asLanguageId('makefile');
  if (base === 'justfile') return asLanguageId('just');
  if (base === 'cmakelists.txt') return asLanguageId('cmake');
  if (base === 'gemfile' || base === 'rakefile') return asLanguageId('ruby');

  if (firstLine) {
    if (firstLine.startsWith('#!/usr/bin/env python') || firstLine.startsWith('#!/usr/bin/python')) return asLanguageId('python');
    if (firstLine.startsWith('#!/usr/bin/env node') || firstLine.startsWith('#!/usr/bin/node')) return asLanguageId('javascript');
    if (firstLine.startsWith('#!/bin/bash') || firstLine.startsWith('#!/usr/bin/env bash')) return asLanguageId('shellscript');
    if (firstLine.startsWith('#!/bin/sh')) return asLanguageId('shellscript');
    if (firstLine.startsWith('#!/usr/bin/env ruby')) return asLanguageId('ruby');
    if (firstLine.startsWith('<?xml')) return asLanguageId('xml');
  }

  return null;
}

export function getLanguageConfig(languageId: LanguageId, registry: LanguageDefinition[]): LanguageDefinition | null {
  return registry.find((l) => l.id === languageId) ?? null;
}

// ═══ §13 LANGUAGE SERVICE ORCHESTRATION — completions, diagnostics, formatting ═══

export function filterCompletions(items: CompletionItem[], prefix: string): CompletionItem[] {
  if (!prefix) return items;
  const lower = prefix.toLowerCase();
  return items.filter((item) => {
    const text = (item.filterText ?? item.label).toLowerCase();
    return text.includes(lower);
  });
}

export function sortCompletions(items: CompletionItem[]): CompletionItem[] {
  return [...items].sort((a, b) => {
    if (a.preselect && !b.preselect) return -1;
    if (!a.preselect && b.preselect) return 1;
    const sortA = a.sortText ?? a.label;
    const sortB = b.sortText ?? b.label;
    return sortA.localeCompare(sortB);
  });
}

export function matchCompletion(item: CompletionItem, input: string): { score: number; highlights: number[] } {
  const label = item.label.toLowerCase();
  const query = input.toLowerCase();
  const highlights: number[] = [];
  let score = 0;
  let queryIdx = 0;

  for (let i = 0; i < label.length && queryIdx < query.length; i++) {
    if (label[i] === query[queryIdx]) {
      highlights.push(i);
      score += (i === queryIdx) ? 2 : 1;
      queryIdx++;
    }
  }

  if (queryIdx < query.length) return { score: 0, highlights: [] };
  if (label.startsWith(query)) score += 10;
  return { score, highlights };
}

export function findMatchingBracket(content: string, position: Position, brackets: BracketPair[]): Position | null {
  const offset = positionToOffset(content, position);
  const charAtPos = content[offset];

  for (const pair of brackets) {
    if (charAtPos === pair.open) {
      let depth = 1;
      for (let i = offset + 1; i < content.length && depth > 0; i++) {
        if (content[i] === pair.open) depth++;
        if (content[i] === pair.close) depth--;
        if (depth === 0) return offsetToPosition(content, i);
      }
      return null;
    }
    if (charAtPos === pair.close) {
      let depth = 1;
      for (let i = offset - 1; i >= 0 && depth > 0; i--) {
        if (content[i] === pair.close) depth++;
        if (content[i] === pair.open) depth--;
        if (depth === 0) return offsetToPosition(content, i);
      }
      return null;
    }
  }
  return null;
}

export function getFoldingRanges(content: string, language: LanguageConfiguration): Range[] {
  const lines = content.split('\n');
  const ranges: Range[] = [];
  const stack: number[] = [];

  if (language.folding.markers) {
    const startRegex = new RegExp(language.folding.markers.start);
    const endRegex = new RegExp(language.folding.markers.end);
    for (let i = 0; i < lines.length; i++) {
      if (startRegex.test(lines[i]!)) stack.push(i);
      if (endRegex.test(lines[i]!) && stack.length > 0) {
        const start = stack.pop()!;
        ranges.push({ start: { line: start, column: 0 }, end: { line: i, column: lines[i]!.length } });
      }
    }
  }

  if (language.folding.offSide) {
    const indents: number[] = lines.map((l) => getIndentLevel(l, 4));
    for (let i = 0; i < lines.length - 1; i++) {
      if (lines[i]!.trim().length === 0) continue;
      let j = i + 1;
      while (j < lines.length && (lines[j]!.trim().length === 0 || indents[j]! > indents[i]!)) j++;
      if (j > i + 1) {
        ranges.push({ start: { line: i, column: 0 }, end: { line: j - 1, column: lines[j - 1]!.length } });
      }
    }
  }

  return ranges;
}

export function getIndentLevel(line: string, tabSize: number): number {
  let indent = 0;
  for (const ch of line) {
    if (ch === ' ') indent++;
    else if (ch === '\t') indent += tabSize;
    else break;
  }
  return indent;
}

export function computeAutoIndent(content: string, position: Position, rules: IndentationRules, tabSize: number): string {
  if (position.line === 0) return '';
  const prevLine = getLineContent(content, position.line - 1);
  const currentIndent = getIndentLevel(prevLine, tabSize);

  let indent = currentIndent;
  const increaseRegex = new RegExp(rules.increaseIndentPattern);
  const decreaseRegex = new RegExp(rules.decreaseIndentPattern);

  if (increaseRegex.test(prevLine)) indent += tabSize;

  const currentLine = getLineContent(content, position.line);
  if (currentLine.trim().length > 0 && decreaseRegex.test(currentLine.trimStart())) {
    indent = Math.max(0, indent - tabSize);
  }

  return ' '.repeat(indent);
}

export function getDiagnosticsForRange(diagnostics: DiagnosticInfo[], range: Range): DiagnosticInfo[] {
  return diagnostics.filter((d) => rangesOverlap(d.range, range));
}

export function severityToNumber(severity: DiagnosticSeverity): number {
  switch (severity) {
    case 'error': return 0;
    case 'warning': return 1;
    case 'info': return 2;
    case 'hint': return 3;
  }
}

export function formatDocument(content: string, options: FormatOptions): string {
  let result = content;
  if (options.trimAutoWhitespace) {
    result = result.split('\n').map((line) => line.trimEnd()).join('\n');
  }
  if (options.trimFinalNewlines) {
    result = result.replace(/\n+$/, '\n');
  }
  if (options.insertFinalNewline && !result.endsWith('\n')) {
    result += '\n';
  }
  return result;
}

export function formatRange(content: string, range: Range, options: FormatOptions): string {
  const lines = content.split('\n');
  const before = lines.slice(0, range.start.line).join('\n');
  const target = lines.slice(range.start.line, range.end.line + 1).join('\n');
  const after = lines.slice(range.end.line + 1).join('\n');
  const formatted = formatDocument(target, { ...options, insertFinalNewline: false, trimFinalNewlines: false });
  const parts = [before, formatted, after].filter((p) => p.length > 0);
  return parts.join('\n');
}

// ═══ §14 LAYOUT ORCHESTRATION — viewport, minimap, gutter, decorations ═══

export function computeVisibleRange(viewport: ViewportState, totalLines: number): Range {
  const visibleLines = Math.ceil(viewport.height / viewport.lineHeight);
  const startLine = Math.max(0, viewport.startLine);
  const endLine = Math.min(totalLines - 1, startLine + visibleLines);
  return {
    start: { line: startLine, column: 0 },
    end: { line: endLine, column: 0 },
  };
}

export function scrollToLine(viewport: ViewportState, line: number, totalLines: number): ViewportState {
  const clamped = clamp(line, 0, Math.max(0, totalLines - 1));
  return {
    ...viewport,
    startLine: clamped,
    scrollTop: clamped * viewport.lineHeight,
  };
}

export function scrollByDelta(viewport: ViewportState, deltaLines: number, totalLines: number): ViewportState {
  return scrollToLine(viewport, viewport.startLine + deltaLines, totalLines);
}

export function computeMinimapViewport(viewport: ViewportState, totalLines: number, minimapScale: number): { startLine: number; endLine: number } {
  const minimapHeight = viewport.height;
  const minimapLineHeight = viewport.lineHeight * minimapScale;
  const visibleMinimapLines = Math.ceil(minimapHeight / minimapLineHeight);
  const startLine = Math.max(0, viewport.startLine - Math.floor(visibleMinimapLines / 4));
  const endLine = Math.min(totalLines - 1, startLine + visibleMinimapLines);
  return { startLine, endLine };
}

export function computeGutterWidth(lCount: number, config: GutterConfig): number {
  if (config.lineNumbers === 'off') return config.glyphMargin ? 20 : 0;
  const digits = Math.max(2, Math.floor(Math.log10(Math.max(1, lCount))) + 1);
  let width = digits * 8 + 16;
  if (config.glyphMargin) width += 20;
  if (config.folding) width += 16;
  width += config.lineDecorationsWidth;
  return width;
}

export function wrapLine(line: string, column: number, _mode: WordWrapMode): string[] {
  if (column <= 0 || line.length <= column) return [line];
  const wrapped: string[] = [];
  let remaining = line;
  while (remaining.length > column) {
    let breakAt = column;
    const lastSpace = remaining.lastIndexOf(' ', column);
    if (lastSpace > column * 0.5) breakAt = lastSpace + 1;
    wrapped.push(remaining.slice(0, breakAt));
    remaining = remaining.slice(breakAt);
  }
  if (remaining.length > 0) wrapped.push(remaining);
  return wrapped;
}

export function computeStickyHeaders(content: string, viewport: ViewportState, config: StickyScrollConfig): { line: number; depth: number }[] {
  if (!config.enabled) return [];
  const lines = content.split('\n');
  const headers: { line: number; depth: number }[] = [];
  const tabSize = 4;

  for (let i = viewport.startLine; i >= 0 && headers.length < config.maxLineCount; i--) {
    const trimmed = lines[i]?.trim() ?? '';
    if (trimmed.length === 0) continue;
    const indent = getIndentLevel(lines[i]!, tabSize);
    const depth = Math.floor(indent / tabSize);

    if (headers.length === 0 || depth < headers[headers.length - 1]!.depth) {
      if (/^(function|class|if|for|while|switch|export|import|const|let|var|interface|type|enum|module|namespace)\b/.test(trimmed) ||
          trimmed.endsWith('{') || trimmed.endsWith(':')) {
        headers.push({ line: i, depth });
      }
    }
  }

  return headers.reverse();
}

export function addDecoration(decorations: DecorationData[], decoration: DecorationData): DecorationData[] {
  return [...decorations, decoration];
}

export function removeDecoration(decorations: DecorationData[], id: DecorationId): DecorationData[] {
  return decorations.filter((d) => d.id !== id);
}

export function getDecorationsInRange(decorations: DecorationData[], range: Range): DecorationData[] {
  return decorations.filter((d) => rangesOverlap(d.range, range));
}

// ═══ §15 EDITOR STATE ORCHESTRATION — state machine, focus, linking ═══

export function createEditorState(id: EditorInstanceId, documentId: DocumentId, config?: Partial<EditorConfig>): EditorState {
  return {
    ...emptyEditorState(id, documentId),
    config: config ? mergeEditorConfig(DEFAULT_EDITOR_CONFIG, config) : DEFAULT_EDITOR_CONFIG,
  };
}

const VALID_MODE_TRANSITIONS: Record<string, EditorMode[]> = {
  normal: ['insert', 'visual', 'visual-line', 'visual-block', 'command', 'replace', 'select'],
  insert: ['normal'],
  visual: ['normal', 'visual-line', 'visual-block'],
  'visual-line': ['normal', 'visual', 'visual-block'],
  'visual-block': ['normal', 'visual', 'visual-line'],
  command: ['normal'],
  replace: ['normal'],
  select: ['normal', 'insert'],
};

export function changeEditorMode(state: EditorState, mode: EditorMode): EditorResult<EditorState> {
  const allowed = VALID_MODE_TRANSITIONS[state.mode];
  if (!allowed || !allowed.includes(mode)) {
    return err('INVALID_MODE_TRANSITION', `Cannot transition from ${state.mode} to ${mode}`);
  }
  return ok({ ...state, mode }, 'MODE_CHANGED', `Mode changed to ${mode}`, [{
    kind: 'changed', domain: 'editor', timestamp: now(), payload: { from: state.mode, to: mode },
  }]);
}

export function setFocus(state: EditorState, focus: FocusState): EditorState {
  return { ...state, focus };
}

export function setReadOnly(state: EditorState, mode: ReadOnlyMode): EditorState {
  return { ...state, readOnly: mode };
}

export function linkEditors(editors: EditorState[]): LinkedEditorState {
  return {
    editorIds: editors.map((e) => e.id),
    syncScrolling: true,
    syncCursors: false,
  };
}

export function unlinkEditor(linked: LinkedEditorState, editorId: EditorInstanceId): LinkedEditorState {
  return {
    ...linked,
    editorIds: linked.editorIds.filter((id) => id !== editorId),
  };
}

// ═══ §16 FIND & REPLACE ORCHESTRATION — search, regex, replace ═══

export function findMatches(content: string, state: FindState): FindMatch[] {
  const regex = buildSearchRegex(state);
  if (!regex) return [];
  const matches: FindMatch[] = [];
  let match: RegExpExecArray | null;
  let count = 0;
  while ((match = regex.exec(content)) !== null && count < MAX_SEARCH_RESULTS) {
    const start = offsetToPosition(content, match.index);
    const end = offsetToPosition(content, match.index + match[0].length);
    matches.push({
      range: { start, end },
      matches: [match[0], ...match.slice(1)],
    });
    count++;
    if (!regex.global) break;
    if (match[0].length === 0) regex.lastIndex++;
  }
  return matches;
}

export function findNext(content: string, state: FindState, fromPosition: Position): FindMatch | null {
  const matches = findMatches(content, state);
  for (const match of matches) {
    if (comparePositions(match.range.start, fromPosition) > 0) return match;
  }
  return matches[0] ?? null;
}

export function findPrevious(content: string, state: FindState, fromPosition: Position): FindMatch | null {
  const matches = findMatches(content, state);
  for (let i = matches.length - 1; i >= 0; i--) {
    if (comparePositions(matches[i]!.range.start, fromPosition) < 0) return matches[i]!;
  }
  return matches[matches.length - 1] ?? null;
}

export function replaceMatch(content: string, match: FindMatch, replacement: string, preserveCase: boolean): { content: string; offset: number } {
  const start = positionToOffset(content, match.range.start);
  const end = positionToOffset(content, match.range.end);
  const actual = preserveCase ? preserveCaseReplace(match.matches[0]!, replacement) : replacement;
  return {
    content: content.slice(0, start) + actual + content.slice(end),
    offset: actual.length - (end - start),
  };
}

export function replaceAll(content: string, state: FindState): { content: string; count: number } {
  const matches = findMatches(content, state);
  if (matches.length === 0) return { content, count: 0 };
  let result = content;
  let offsetDelta = 0;
  for (const match of matches) {
    const start = positionToOffset(content, match.range.start) + offsetDelta;
    const end = positionToOffset(content, match.range.end) + offsetDelta;
    const replacement = state.preserveCase
      ? preserveCaseReplace(match.matches[0]!, state.replaceString)
      : state.replaceString;
    result = result.slice(0, start) + replacement + result.slice(end);
    offsetDelta += replacement.length - (end - start);
  }
  return { content: result, count: matches.length };
}

export function escapeRegex(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function buildSearchRegex(state: FindState): RegExp | null {
  if (!state.searchString) return null;
  try {
    let pattern = state.isRegex ? state.searchString : escapeRegex(state.searchString);
    if (state.isWholeWord) pattern = `\\b${pattern}\\b`;
    const flags = state.isCaseSensitive ? 'g' : 'gi';
    return new RegExp(pattern, flags);
  } catch {
    return null;
  }
}

// ═══ §17 SNIPPET ORCHESTRATION — parsing, insertion, tab stops ═══

export function parseSnippet(body: string): { tabStops: TabStop[]; text: string } {
  const tabStops: TabStop[] = [];
  let text = '';
  let i = 0;
  let line = 0;
  let col = 0;

  while (i < body.length) {
    if (body[i] === '$' && i + 1 < body.length) {
      if (body[i + 1] === '{') {
        const closeBrace = body.indexOf('}', i + 2);
        if (closeBrace !== -1) {
          const inner = body.slice(i + 2, closeBrace);
          const colonIdx = inner.indexOf(':');
          const index = parseInt(colonIdx >= 0 ? inner.slice(0, colonIdx) : inner, 10);
          const placeholder = colonIdx >= 0 ? inner.slice(colonIdx + 1) : undefined;
          const startPos: Position = { line, column: col };
          const placeholderLen = placeholder?.length ?? 0;
          const endPos: Position = { line, column: col + placeholderLen };
          tabStops.push({ index, range: { start: startPos, end: endPos }, placeholder });
          if (placeholder) {
            text += placeholder;
            col += placeholderLen;
          }
          i = closeBrace + 1;
          continue;
        }
      } else if (/\d/.test(body[i + 1]!)) {
        const index = parseInt(body[i + 1]!, 10);
        const pos: Position = { line, column: col };
        tabStops.push({ index, range: { start: pos, end: pos } });
        i += 2;
        continue;
      }
    }
    if (body[i] === '\n') {
      line++;
      col = 0;
    } else {
      col++;
    }
    text += body[i];
    i++;
  }

  tabStops.sort((a, b) => a.index - b.index);
  return { tabStops, text };
}

export function insertSnippet(
  content: string,
  position: Position,
  snippet: SnippetDefinition,
  variables: Record<string, string>,
): EditorResult<{ content: string; session: SnippetSession }> {
  let body = snippet.body.join('\n');

  // Resolve variables
  const varPattern = /\$([A-Z_]+)/g;
  body = body.replace(varPattern, (_, name: string) => {
    return variables[name] ?? '';
  });

  const parsed = parseSnippet(body);
  const offset = positionToOffset(content, position);
  const newContent = insertText(content, offset, parsed.text);

  const session: SnippetSession = {
    snippetId: snippet.id,
    tabStops: parsed.tabStops,
    currentTabStop: 0,
    placeholders: new Map(parsed.tabStops
      .filter((ts) => ts.placeholder !== undefined)
      .map((ts) => [ts.index, ts.placeholder!])),
  };

  return ok({ content: newContent, session }, 'SNIPPET_INSERTED', 'Snippet inserted');
}

export function nextTabStop(session: SnippetSession): SnippetSession | null {
  if (session.currentTabStop >= session.tabStops.length - 1) return null;
  return { ...session, currentTabStop: session.currentTabStop + 1 };
}

export function previousTabStop(session: SnippetSession): SnippetSession | null {
  if (session.currentTabStop <= 0) return null;
  return { ...session, currentTabStop: session.currentTabStop - 1 };
}

export function resolveVariable(variable: SnippetVariable, context: Record<string, string>): string {
  return context[variable] ?? '';
}

export function applyTransform(text: string, transform: SnippetTransform): string {
  try {
    const regex = new RegExp(transform.regex, transform.options ?? 'g');
    return text.replace(regex, transform.replacement);
  } catch {
    return text;
  }
}

// ═══ §18 COMMAND ORCHESTRATION — registry, keybinding resolution, when-clauses ═══

export function registerCommand(registry: CommandRegistry, command: CommandData): CommandRegistry {
  const commands = new Map(registry.commands);
  commands.set(command.id, command);
  return { commands };
}

export function unregisterCommand(registry: CommandRegistry, id: CommandId): CommandRegistry {
  const commands = new Map(registry.commands);
  commands.delete(id);
  return { commands };
}

export function resolveKeybinding(keybindings: KeybindingData[], key: string, context: Record<string, boolean>): KeybindingResolveResult | null {
  for (const kb of keybindings) {
    if (kb.key === key) {
      if (kb.when && !evaluateWhenClause(kb.when, context)) continue;
      return { command: kb.command, args: kb.args, when: kb.when };
    }
  }
  return null;
}

export function evaluateWhenClause(clause: WhenClause, context: Record<string, boolean>): boolean {
  if (!clause) return true;
  const parts = clause.split('&&').map((p) => p.trim());
  for (const part of parts) {
    if (part.startsWith('!')) {
      const key = part.slice(1).trim();
      if (context[key]) return false;
    } else {
      if (!context[part]) return false;
    }
  }
  return true;
}

export function parseKeyCombo(key: string): { ctrl: boolean; shift: boolean; alt: boolean; meta: boolean; key: string } {
  const parts = key.toLowerCase().split('+');
  const mainKey = parts.pop() ?? '';
  return {
    ctrl: parts.includes('ctrl'),
    shift: parts.includes('shift'),
    alt: parts.includes('alt'),
    meta: parts.includes('meta') || parts.includes('cmd'),
    key: mainKey,
  };
}

export function formatKeyCombo(combo: { ctrl: boolean; shift: boolean; alt: boolean; meta: boolean; key: string }): string {
  const parts: string[] = [];
  if (combo.ctrl) parts.push('Ctrl');
  if (combo.shift) parts.push('Shift');
  if (combo.alt) parts.push('Alt');
  if (combo.meta) parts.push('Cmd');
  parts.push(combo.key.charAt(0).toUpperCase() + combo.key.slice(1));
  return parts.join('+');
}

// ═══ §19 TERMINAL ORCHESTRATION — terminal entity, status machine, metrics ═══

export function createTerminal(id: TerminalId, profile?: Partial<TerminalProfile>): TerminalState {
  const merged = profile ? mergeTerminalProfile(DEFAULT_TERMINAL_PROFILE, profile) : DEFAULT_TERMINAL_PROFILE;
  return { ...emptyTerminalState(id), profile: merged, name: merged.name };
}

const VALID_TERMINAL_TRANSITIONS: Record<string, TerminalStatus[]> = {
  initializing: ['connecting', 'active', 'error', 'closed'],
  connecting: ['active', 'disconnected', 'error', 'closed'],
  active: ['busy', 'disconnected', 'suspended', 'error', 'closed'],
  busy: ['active', 'disconnected', 'error', 'closed'],
  disconnected: ['connecting', 'closed', 'error'],
  suspended: ['active', 'closed', 'error'],
  error: ['initializing', 'closed'],
  closed: [],
};

export function transitionTerminalStatus(state: TerminalState, newStatus: TerminalStatus): EditorResult<TerminalState> {
  if (!validateTerminalTransition(state.status, newStatus)) {
    return err('INVALID_TRANSITION', `Cannot transition terminal from ${state.status} to ${newStatus}`);
  }
  return ok({ ...state, status: newStatus }, 'TERMINAL_STATUS_CHANGED', `Terminal status: ${newStatus}`, [{
    kind: 'changed', domain: 'terminal', timestamp: now(), payload: { from: state.status, to: newStatus },
  }]);
}

export function validateTerminalTransition(from: TerminalStatus, to: TerminalStatus): boolean {
  const allowed = VALID_TERMINAL_TRANSITIONS[from];
  return allowed ? allowed.includes(to) : false;
}

export function updateTerminalMetrics(state: TerminalState, metrics: Partial<TerminalMetrics>): TerminalState {
  return { ...state, metrics: { ...state.metrics, ...metrics } };
}

export function isTerminalActive(state: TerminalState): boolean {
  return state.status === 'active' || state.status === 'busy';
}

export function isTerminalInteractive(state: TerminalState): boolean {
  return state.status === 'active';
}

// ═══ §20 PTY ORCHESTRATION — process config, status, shell detection ═══

export function createPTYConfig(shell: ShellType, overrides?: Partial<PTYConfig>): PTYConfig {
  const base: PTYConfig = { ...DEFAULT_PTY_CONFIG, shell, args: defaultShellArgs(shell) };
  return overrides ? mergePTYConfig(base, overrides) : base;
}

const VALID_PTY_TRANSITIONS: Record<string, PTYStatus[]> = {
  starting: ['running', 'error', 'killed'],
  running: ['suspended', 'exited', 'killed', 'error'],
  suspended: ['running', 'killed', 'error'],
  exited: [],
  killed: [],
  error: ['starting'],
};

export function transitionPTYStatus(state: PTYState, newStatus: PTYStatus): EditorResult<PTYState> {
  if (!validatePTYTransition(state.status, newStatus)) {
    return err('INVALID_PTY_TRANSITION', `Cannot transition PTY from ${state.status} to ${newStatus}`);
  }
  return ok({ ...state, status: newStatus }, 'PTY_STATUS_CHANGED', `PTY status: ${newStatus}`);
}

export function validatePTYTransition(from: PTYStatus, to: PTYStatus): boolean {
  const allowed = VALID_PTY_TRANSITIONS[from];
  return allowed ? allowed.includes(to) : false;
}

export function resizePTY(state: PTYState, cols: number, rows: number): PTYState {
  return { ...state, cols: Math.max(1, cols), rows: Math.max(1, rows) };
}

export function detectShellType(path: string): ShellType {
  const basename = path.split('/').pop()?.toLowerCase() ?? '';
  const shellMap: Record<string, ShellType> = {
    bash: 'bash', zsh: 'zsh', fish: 'fish', sh: 'sh', dash: 'dash',
    ksh: 'ksh', csh: 'csh', tcsh: 'tcsh', nu: 'nu', nushell: 'nushell',
    elvish: 'elvish', xonsh: 'xonsh', ion: 'ion', oil: 'oil',
    murex: 'murex', hilbish: 'hilbish', pwsh: 'pwsh',
    'powershell.exe': 'powershell', 'powershell': 'powershell',
    'cmd.exe': 'cmd', cmd: 'cmd',
  };
  return shellMap[basename] ?? 'custom';
}

export function shellToCommand(shell: ShellType): string {
  const commandMap: Record<ShellType, string> = {
    bash: '/bin/bash', zsh: '/bin/zsh', fish: '/usr/local/bin/fish',
    sh: '/bin/sh', dash: '/bin/dash', ksh: '/bin/ksh', csh: '/bin/csh',
    tcsh: '/bin/tcsh', nu: '/usr/local/bin/nu', nushell: '/usr/local/bin/nu',
    elvish: '/usr/local/bin/elvish', xonsh: '/usr/local/bin/xonsh',
    ion: '/usr/local/bin/ion', oil: '/usr/local/bin/oil',
    murex: '/usr/local/bin/murex', hilbish: '/usr/local/bin/hilbish',
    powershell: 'powershell.exe', pwsh: '/usr/local/bin/pwsh',
    cmd: 'cmd.exe', custom: '/bin/sh',
  };
  return commandMap[shell];
}

export function defaultShellArgs(shell: ShellType): string[] {
  switch (shell) {
    case 'bash': return ['--login'];
    case 'zsh': return ['--login'];
    case 'fish': return ['--login'];
    case 'powershell': return ['-NoLogo'];
    case 'pwsh': return ['-NoLogo'];
    default: return [];
  }
}

// ═══ §21 TERMINAL BUFFER ORCHESTRATION — cells, scrollback, write, resize ═══

export function createTerminalBuffer(id: TerminalBufferId, cols: number, rows: number): TerminalBufferState {
  const lines: TerminalLine[] = [];
  for (let i = 0; i < rows; i++) {
    lines.push({ cells: createEmptyRow(cols), isWrapped: false, version: 0 });
  }
  return {
    ...emptyTerminalBufferState(id),
    lines,
    cursorState: { row: 0, col: 0, style: 'block', visible: true, blink: true },
  };
}

function createEmptyRow(cols: number): TerminalCell[] {
  const cells: TerminalCell[] = [];
  for (let i = 0; i < cols; i++) {
    cells.push(createCell(' '));
  }
  return cells;
}

export function writeToBuffer(state: TerminalBufferState, data: string): TerminalBufferState {
  const lines = [...state.lines];
  let { row, col } = state.cursorState;
  const totalCols = lines[0]?.cells.length ?? 80;

  for (const ch of data) {
    if (ch === '\n') {
      row++;
      col = 0;
      if (row >= lines.length) {
        lines.push({ cells: createEmptyRow(totalCols), isWrapped: false, version: 0 });
      }
      continue;
    }
    if (ch === '\r') {
      col = 0;
      continue;
    }
    if (row < lines.length) {
      const cells = [...lines[row]!.cells];
      if (col < cells.length) {
        cells[col] = createCell(ch, cells[col]!.fg, cells[col]!.bg, cells[col]!.flags);
      }
      lines[row] = { ...lines[row]!, cells, version: lines[row]!.version + 1 };
    }
    col++;
    if (col >= totalCols) {
      col = 0;
      row++;
      if (row >= lines.length) {
        lines.push({ cells: createEmptyRow(totalCols), isWrapped: true, version: 0 });
      }
    }
  }

  return {
    ...state,
    lines,
    cursorState: { ...state.cursorState, row, col },
    modificationSeq: state.modificationSeq + 1,
  };
}

export function clearBuffer(state: TerminalBufferState): TerminalBufferState {
  const cols = state.lines[0]?.cells.length ?? 80;
  const rows = state.lines.length;
  const lines: TerminalLine[] = [];
  for (let i = 0; i < rows; i++) {
    lines.push({ cells: createEmptyRow(cols), isWrapped: false, version: 0 });
  }
  return {
    ...state,
    lines,
    cursorState: { ...state.cursorState, row: 0, col: 0 },
    modificationSeq: state.modificationSeq + 1,
  };
}

export function switchBuffer(state: TerminalBufferState, target: 'normal' | 'alternate'): TerminalBufferState {
  if (state.activeBuffer === target) return state;
  return {
    ...state,
    lines: state.altLines,
    altLines: state.lines,
    activeBuffer: target,
    modificationSeq: state.modificationSeq + 1,
  };
}

export function resizeBuffer(state: TerminalBufferState, cols: number, rows: number): TerminalBufferState {
  const lines = [...state.lines];
  while (lines.length < rows) {
    lines.push({ cells: createEmptyRow(cols), isWrapped: false, version: 0 });
  }
  while (lines.length > rows) lines.pop();
  const resized = lines.map((line) => {
    const cells = [...line.cells];
    while (cells.length < cols) cells.push(createCell(' '));
    while (cells.length > cols) cells.pop();
    return { ...line, cells };
  });
  return { ...state, lines: resized, modificationSeq: state.modificationSeq + 1 };
}

export function scrollBuffer(state: TerminalBufferState, linesDelta: number): TerminalBufferState {
  const newStart = clamp(state.viewport.startLine + linesDelta, 0, Math.max(0, state.lines.length - 1));
  return {
    ...state,
    viewport: { ...state.viewport, startLine: newStart },
  };
}

export function getBufferContent(state: TerminalBufferState, range?: Range): string {
  const startLine = range?.start.line ?? 0;
  const endLine = range?.end.line ?? state.lines.length - 1;
  const result: string[] = [];
  for (let i = startLine; i <= endLine && i < state.lines.length; i++) {
    const line = state.lines[i]!.cells.map((c) => c.char).join('').trimEnd();
    result.push(line);
  }
  return result.join('\n');
}

export function createCell(char: string, fg?: TerminalColor, bg?: TerminalColor, flags?: number): TerminalCell {
  return {
    char,
    width: 1,
    fg: fg ?? defaultTerminalColor(),
    bg: bg ?? defaultTerminalColor(),
    flags: flags ?? 0,
  };
}

export function defaultTerminalColor(): TerminalColor {
  return { mode: 'default' };
}

export function trimScrollback(state: TerminalBufferState, maxLines: number): TerminalBufferState {
  if (state.lines.length <= maxLines) return state;
  const excess = state.lines.length - maxLines;
  return {
    ...state,
    lines: state.lines.slice(excess),
    cursorState: {
      ...state.cursorState,
      row: Math.max(0, state.cursorState.row - excess),
    },
  };
}

// ═══ §22 ANSI ORCHESTRATION — parsing, SGR, color handling ═══

export const ANSI_REGEX = /(?:\x1b\[[\x30-\x3f]*[\x20-\x2f]*[\x40-\x7e]|\x1b\][\s\S]*?(?:\x07|\x1b\\)|\x1b[\x40-\x5f]|\x1b[()][A-Z0-9])/g;

export const C0_HANDLERS: Record<number, string> = {
  0: 'NUL', 7: 'BEL', 8: 'BS', 9: 'HT', 10: 'LF', 11: 'VT',
  12: 'FF', 13: 'CR', 14: 'SO', 15: 'SI', 24: 'CAN', 26: 'SUB', 27: 'ESC',
};

export const DEFAULT_ANSI_PALETTE: string[] = [
  '#000000', '#800000', '#008000', '#808000', '#000080', '#800080', '#008080', '#c0c0c0',
  '#808080', '#ff0000', '#00ff00', '#ffff00', '#0000ff', '#ff00ff', '#00ffff', '#ffffff',
  ...(() => {
    const colors: string[] = [];
    for (let r = 0; r < 6; r++) {
      for (let g = 0; g < 6; g++) {
        for (let b = 0; b < 6; b++) {
          const rv = r ? r * 40 + 55 : 0;
          const gv = g ? g * 40 + 55 : 0;
          const bv = b ? b * 40 + 55 : 0;
          colors.push(`#${rv.toString(16).padStart(2, '0')}${gv.toString(16).padStart(2, '0')}${bv.toString(16).padStart(2, '0')}`);
        }
      }
    }
    return colors;
  })(),
  ...(() => {
    const grays: string[] = [];
    for (let i = 0; i < 24; i++) {
      const v = i * 10 + 8;
      grays.push(`#${v.toString(16).padStart(2, '0')}${v.toString(16).padStart(2, '0')}${v.toString(16).padStart(2, '0')}`);
    }
    return grays;
  })(),
];

export function parseANSI(input: string): ANSISequence[] {
  const sequences: ANSISequence[] = [];
  const regex = new RegExp(ANSI_REGEX.source, 'g');
  let match: RegExpExecArray | null;
  while ((match = regex.exec(input)) !== null) {
    const raw = match[0];
    if (raw.startsWith('\x1b[')) {
      sequences.push(parseCSISequence(raw));
    } else if (raw.startsWith('\x1b]')) {
      sequences.push(parseOSCSequence(raw));
    } else {
      sequences.push({ type: 'c1', raw, params: [], command: raw.slice(1) });
    }
  }
  return sequences;
}

export function parseCSISequence(raw: string): ANSISequence {
  const body = raw.slice(2);
  const command = body.charAt(body.length - 1);
  const paramStr = body.slice(0, -1);
  const params = paramStr
    ? paramStr.split(';').map((p) => parseInt(p, 10) || 0)
    : [];

  let type: ANSISequenceType = 'csi';
  if (command === 'm') type = 'sgr';
  else if ('ABCDHG'.includes(command)) type = 'cursor';
  else if ('JK'.includes(command)) type = 'erase';
  else if ('ST'.includes(command)) type = 'scroll';
  else if ('hl'.includes(command)) type = 'mode';

  return { type, raw, command, params };
}

export function parseOSCSequence(raw: string): ANSISequence {
  const body = raw.slice(2).replace(/(\x07|\x1b\\)$/, '');
  const semiIdx = body.indexOf(';');
  const cmd = semiIdx >= 0 ? body.slice(0, semiIdx) : body;
  const stringParam = semiIdx >= 0 ? body.slice(semiIdx + 1) : undefined;

  let type: ANSISequenceType = 'osc';
  if (cmd === '0' || cmd === '2') type = 'title';
  if (cmd === '8') type = 'hyperlink';

  return { type, raw, command: cmd, params: [parseInt(cmd, 10) || 0], stringParam };
}

export function parseSGR(params: number[]): SGRAttribute[] {
  const attrs: SGRAttribute[] = [];
  let i = 0;
  while (i < params.length) {
    const p = params[i]!;
    switch (p) {
      case 0: attrs.push({ attribute: 'reset' }); break;
      case 1: attrs.push({ attribute: 'bold' }); break;
      case 2: attrs.push({ attribute: 'dim' }); break;
      case 3: attrs.push({ attribute: 'italic' }); break;
      case 4: attrs.push({ attribute: 'underline' }); break;
      case 5: attrs.push({ attribute: 'slow-blink' }); break;
      case 6: attrs.push({ attribute: 'rapid-blink' }); break;
      case 7: attrs.push({ attribute: 'inverse' }); break;
      case 8: attrs.push({ attribute: 'invisible' }); break;
      case 9: attrs.push({ attribute: 'strikethrough' }); break;
      case 21: attrs.push({ attribute: 'double-underline' }); break;
      case 22: attrs.push({ attribute: 'normal-intensity' }); break;
      case 23: attrs.push({ attribute: 'not-italic' }); break;
      case 24: attrs.push({ attribute: 'not-underline' }); break;
      case 25: attrs.push({ attribute: 'not-blink' }); break;
      case 27: attrs.push({ attribute: 'not-inverse' }); break;
      case 28: attrs.push({ attribute: 'not-invisible' }); break;
      case 29: attrs.push({ attribute: 'not-strikethrough' }); break;
      case 53: attrs.push({ attribute: 'overline' }); break;
      case 55: attrs.push({ attribute: 'not-overline' }); break;
      case 38:
        if (params[i + 1] === 5 && i + 2 < params.length) {
          attrs.push({ attribute: 'foreground', value: colorFrom256(params[i + 2]!) });
          i += 2;
        } else if (params[i + 1] === 2 && i + 4 < params.length) {
          attrs.push({ attribute: 'foreground', value: colorFromRGB(params[i + 2]!, params[i + 3]!, params[i + 4]!) });
          i += 4;
        }
        break;
      case 48:
        if (params[i + 1] === 5 && i + 2 < params.length) {
          attrs.push({ attribute: 'background', value: colorFrom256(params[i + 2]!) });
          i += 2;
        } else if (params[i + 1] === 2 && i + 4 < params.length) {
          attrs.push({ attribute: 'background', value: colorFromRGB(params[i + 2]!, params[i + 3]!, params[i + 4]!) });
          i += 4;
        }
        break;
      default:
        if (p >= 30 && p <= 37) {
          attrs.push({ attribute: 'foreground', value: colorFrom256(p - 30) });
        } else if (p >= 40 && p <= 47) {
          attrs.push({ attribute: 'background', value: colorFrom256(p - 40) });
        } else if (p >= 90 && p <= 97) {
          attrs.push({ attribute: 'foreground', value: colorFrom256(p - 90 + 8) });
        } else if (p >= 100 && p <= 107) {
          attrs.push({ attribute: 'background', value: colorFrom256(p - 100 + 8) });
        }
        break;
    }
    i++;
  }
  return attrs;
}

export function sgrToFlags(attrs: SGRAttribute[]): number {
  let flags = 0;
  for (const attr of attrs) {
    switch (attr.attribute) {
      case 'reset': flags = 0; break;
      case 'bold': flags |= 1; break;
      case 'italic': flags |= 2; break;
      case 'underline': flags |= 4; break;
      case 'strikethrough': flags |= 8; break;
      case 'slow-blink': case 'rapid-blink': flags |= 16; break;
      case 'inverse': flags |= 32; break;
      case 'invisible': flags |= 64; break;
      case 'dim': flags |= 128; break;
      case 'overline': flags |= 256; break;
      case 'double-underline': flags |= 512; break;
      case 'normal-intensity': flags &= ~(1 | 128); break;
      case 'not-italic': flags &= ~2; break;
      case 'not-underline': flags &= ~(4 | 512); break;
      case 'not-blink': flags &= ~16; break;
      case 'not-inverse': flags &= ~32; break;
      case 'not-invisible': flags &= ~64; break;
      case 'not-strikethrough': flags &= ~8; break;
      case 'not-overline': flags &= ~256; break;
    }
  }
  return flags;
}

export function sgrToColors(attrs: SGRAttribute[]): { fg?: TerminalColor; bg?: TerminalColor } {
  let fg: TerminalColor | undefined;
  let bg: TerminalColor | undefined;
  for (const attr of attrs) {
    if (attr.attribute === 'foreground' && attr.value && typeof attr.value === 'object') {
      fg = attr.value as TerminalColor;
    }
    if (attr.attribute === 'background' && attr.value && typeof attr.value === 'object') {
      bg = attr.value as TerminalColor;
    }
    if (attr.attribute === 'reset') {
      fg = undefined;
      bg = undefined;
    }
  }
  return { fg, bg };
}

export function colorFrom256(index: number): TerminalColor {
  return { mode: '8bit', index };
}

export function colorFromRGB(r: number, g: number, b: number): TerminalColor {
  return { mode: '24bit', r, g, b };
}

export function colorToCSS(color: TerminalColor, palette?: string[]): string {
  const pal = palette ?? DEFAULT_ANSI_PALETTE;
  switch (color.mode) {
    case 'default': return 'inherit';
    case '24bit': return `rgb(${color.r ?? 0},${color.g ?? 0},${color.b ?? 0})`;
    case '8bit': case '3bit': case '4bit': case 'indexed':
      return pal[color.index ?? 0] ?? 'inherit';
    case 'theme': return `var(--terminal-${color.theme ?? 'foreground'})`;
    default: return 'inherit';
  }
}

// ═══ §23 TERMINAL OUTPUT ORCHESTRATION — classification, detection, entities ═══

export const FILE_PATH_REGEX = /(?:\/[\w.-]+)+\/?|[A-Z]:\\[\w.\\-]+/g;
export const URL_REGEX = /https?:\/\/[^\s<>"{}|\\^`\[\]]+/g;
export const EMAIL_REGEX = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
export const IP_REGEX = /\b(?:\d{1,3}\.){3}\d{1,3}\b/g;
export const PORT_REGEX = /\b:\d{2,5}\b/g;
export const PID_REGEX = /\bPID[:\s]+(\d+)\b|\bpid[:\s]+(\d+)\b/g;
export const GIT_HASH_REGEX = /\b[0-9a-f]{7,40}\b/g;
export const UUID_REGEX = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;
export const TIMESTAMP_REGEX = /\b\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?\b/g;
export const ENV_VAR_REGEX = /\$[A-Z_][A-Z0-9_]*/g;
export const ERROR_CODE_REGEX = /\b(?:E|ERR|ERROR)[_-]?\d{3,5}\b/gi;
export const DOCKER_ID_REGEX = /\b[0-9a-f]{12,64}\b/g;
export const HOSTNAME_REGEX = /\b(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,6}\b/gi;
export const VERSION_REGEX = /\bv?\d+\.\d+(?:\.\d+)?(?:-[a-zA-Z0-9.]+)?\b/g;

export function classifyOutput(text: string): OutputClassification {
  const lower = text.toLowerCase();
  if (/\b(?:error|fatal|panic|exception|traceback|fail(?:ed)?)\b/i.test(text)) return 'error-message';
  if (/\b(?:warn(?:ing)?)\b/i.test(text)) return 'warning';
  if (/\b(?:success(?:fully)?|done|complete(?:d)?|pass(?:ed)?)\b/i.test(text)) return 'success';
  if (isProgressOutput(text)) return 'progress';
  if (lower.startsWith('$') || lower.startsWith('#') || lower.startsWith('>')) return 'prompt';
  if (/^\[?\d{4}-\d{2}-\d{2}/.test(text)) return 'log';
  return 'command-output';
}

export function detectFormat(text: string): DetectedFormat {
  const trimmed = text.trim();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try { JSON.parse(trimmed); return 'json'; } catch { /* not json */ }
  }
  if (trimmed.startsWith('<?xml') || trimmed.startsWith('<')) return 'xml';
  if (/^[\w"]+(?:,[\w"]+)+/.test(trimmed)) return 'csv';
  if (/^[\w"]+(?:\t[\w"]+)+/.test(trimmed)) return 'tsv';
  if (/^\w+:\s+.+$/m.test(trimmed) && !/\n\n/.test(trimmed)) return 'key-value';
  if (/^---/.test(trimmed)) return 'yaml';
  if (/^\[[\w.]+\]/.test(trimmed)) return 'ini';
  return 'plain';
}

export function extractEntities(text: string): ExtractedEntity[] {
  const entities: ExtractedEntity[] = [];
  const patterns: Array<[RegExp, EntityType]> = [
    [new RegExp(URL_REGEX.source, 'g'), 'url'],
    [new RegExp(EMAIL_REGEX.source, 'g'), 'email'],
    [new RegExp(IP_REGEX.source, 'g'), 'ip-address'],
    [new RegExp(UUID_REGEX.source, 'gi'), 'uuid'],
    [new RegExp(TIMESTAMP_REGEX.source, 'g'), 'timestamp'],
    [new RegExp(VERSION_REGEX.source, 'g'), 'version'],
    [new RegExp(FILE_PATH_REGEX.source, 'g'), 'file-path'],
  ];

  for (const [regex, type] of patterns) {
    let match: RegExpExecArray | null;
    while ((match = regex.exec(text)) !== null) {
      const pos = offsetToPosition(text, match.index);
      const endPos = offsetToPosition(text, match.index + match[0].length);
      entities.push({
        type,
        value: match[0],
        range: { start: pos, end: endPos },
        confidence: 0.9,
      });
    }
  }
  return entities;
}

export function parseJSON(text: string): unknown | null {
  try { return JSON.parse(text); } catch { return null; }
}

export function parseCSV(text: string, delimiter: string = ','): string[][] {
  return text.split('\n').filter((l) => l.trim().length > 0).map((line) => line.split(delimiter));
}

export function parseKeyValue(text: string): Record<string, string> {
  const result: Record<string, string> = {};
  for (const line of text.split('\n')) {
    const idx = line.indexOf('=');
    if (idx === -1) {
      const colonIdx = line.indexOf(':');
      if (colonIdx !== -1) {
        result[line.slice(0, colonIdx).trim()] = line.slice(colonIdx + 1).trim();
      }
    } else {
      result[line.slice(0, idx).trim()] = line.slice(idx + 1).trim();
    }
  }
  return result;
}

export function isErrorOutput(text: string): boolean {
  return /\b(?:error|fatal|panic|exception|traceback|fail(?:ed)?)\b/i.test(text);
}

export function isProgressOutput(text: string): boolean {
  return /\d+%|\[#+\s*\]|\[=+>\s*\]|\.{3,}$|\bprogress\b/i.test(text);
}

// ═══ §24 TERMINAL SESSION ORCHESTRATION — shell, history, jobs ═══

export function createSession(id: TerminalSessionId, shell: ShellType): TerminalSessionState {
  return { ...emptyTerminalSessionState(id), shell, status: 'starting' };
}

const VALID_SESSION_TRANSITIONS: Record<string, SessionStatus[]> = {
  starting: ['active', 'error', 'terminated'],
  active: ['busy', 'suspended', 'error', 'terminated'],
  busy: ['active', 'error', 'terminated'],
  suspended: ['active', 'terminated', 'error'],
  terminated: [],
  error: ['starting', 'terminated'],
};

export function transitionSessionStatus(state: TerminalSessionState, newStatus: SessionStatus): EditorResult<TerminalSessionState> {
  const allowed = VALID_SESSION_TRANSITIONS[state.status];
  if (!allowed || !allowed.includes(newStatus)) {
    return err('INVALID_SESSION_TRANSITION', `Cannot transition session from ${state.status} to ${newStatus}`);
  }
  return ok({ ...state, status: newStatus }, 'SESSION_STATUS_CHANGED', `Session status: ${newStatus}`);
}

export function addToHistory(state: CommandHistoryState, entry: CommandHistoryEntry): CommandHistoryState {
  const entries = [...state.entries, entry];
  if (entries.length > state.maxSize) entries.shift();
  return { ...state, entries, searchIndex: undefined };
}

export function searchHistory(state: CommandHistoryState, query: string): CommandHistoryEntry[] {
  const lower = query.toLowerCase();
  return state.entries.filter((e) => e.command.toLowerCase().includes(lower));
}

export function navigateHistory(state: CommandHistoryState, direction: 'up' | 'down'): { state: CommandHistoryState; entry?: CommandHistoryEntry } {
  const idx = state.searchIndex ?? state.entries.length;
  const newIdx = direction === 'up' ? Math.max(0, idx - 1) : Math.min(state.entries.length, idx + 1);
  const entry = state.entries[newIdx];
  return { state: { ...state, searchIndex: newIdx }, entry };
}

export function addJob(state: TerminalSessionState, job: JobInfo): TerminalSessionState {
  return { ...state, jobs: [...state.jobs, job] };
}

export function updateJob(state: TerminalSessionState, jobId: number, status: JobInfo['status']): TerminalSessionState {
  return {
    ...state,
    jobs: state.jobs.map((j) => j.id === jobId ? { ...j, status } : j),
  };
}

export function removeJob(state: TerminalSessionState, jobId: number): TerminalSessionState {
  return { ...state, jobs: state.jobs.filter((j) => j.id !== jobId) };
}

// ═══ §25 MULTIPLEXER ORCHESTRATION — sessions, windows, panes, layouts ═══

export function createMultiplexer(type: MultiplexerType, config?: Partial<MultiplexerConfig>): MultiplexerState {
  return {
    type,
    sessions: [],
    config: config ? { ...DEFAULT_MULTIPLEXER_CONFIG, ...config } : DEFAULT_MULTIPLEXER_CONFIG,
  };
}

export function createMultiplexSession(state: MultiplexerState, name: string): EditorResult<MultiplexerState> {
  const session: MultiplexSession = {
    id: asMultiplexSessionId(generateId('ms')),
    name,
    windows: [],
    activeWindowIndex: 0,
    isAttached: true,
    createdAt: now(),
  };
  return ok(
    { ...state, sessions: [...state.sessions, session], activeSessionId: session.id },
    'MULTIPLEX_SESSION_CREATED', `Session ${name} created`,
  );
}

export function createMultiplexWindow(state: MultiplexerState, sessionId: MultiplexSessionId, name: string): EditorResult<MultiplexerState> {
  const sessionIdx = state.sessions.findIndex((s) => s.id === sessionId);
  if (sessionIdx === -1) return err('SESSION_NOT_FOUND', `Session ${sessionId} not found`);
  const window: MultiplexWindow = {
    id: asMultiplexWindowId(generateId('mw')),
    name,
    panes: [],
    activePaneId: asMultiplexPaneId(''),
    layout: 'tiled',
  };
  const sessions = [...state.sessions];
  sessions[sessionIdx] = { ...sessions[sessionIdx]!, windows: [...sessions[sessionIdx]!.windows, window] };
  return ok({ ...state, sessions }, 'MULTIPLEX_WINDOW_CREATED', `Window ${name} created`);
}

export function splitPane(state: MultiplexerState, paneId: MultiplexPaneId, direction: PaneSplitDirection, terminalId: TerminalId): EditorResult<MultiplexerState> {
  const newPane: MultiplexPane = {
    id: asMultiplexPaneId(generateId('mp')),
    terminalId,
    position: { row: 0, col: 0 },
    size: { width: 50, height: 50 },
    isActive: false,
    isBorderVisible: true,
  };

  const sessions = state.sessions.map((session) => ({
    ...session,
    windows: session.windows.map((window) => {
      const idx = window.panes.findIndex((p) => p.id === paneId);
      if (idx === -1) return window;
      const existingPane = window.panes[idx]!;
      const updatedExisting: MultiplexPane = direction === 'horizontal'
        ? { ...existingPane, size: { ...existingPane.size, width: existingPane.size.width / 2 } }
        : { ...existingPane, size: { ...existingPane.size, height: existingPane.size.height / 2 } };
      const panes = [...window.panes];
      panes[idx] = updatedExisting;
      panes.splice(idx + 1, 0, newPane);
      return { ...window, panes };
    }),
  }));
  return ok({ ...state, sessions }, 'PANE_SPLIT', `Pane split ${direction}`);
}

export function resizePane(state: MultiplexerState, paneId: MultiplexPaneId, size: Partial<PaneSize>): MultiplexerState {
  return {
    ...state,
    sessions: state.sessions.map((s) => ({
      ...s,
      windows: s.windows.map((w) => ({
        ...w,
        panes: w.panes.map((p) => p.id === paneId ? { ...p, size: { ...p.size, ...size } } : p),
      })),
    })),
  };
}

export function removePane(state: MultiplexerState, paneId: MultiplexPaneId): EditorResult<MultiplexerState> {
  const sessions = state.sessions.map((s) => ({
    ...s,
    windows: s.windows.map((w) => ({
      ...w,
      panes: w.panes.filter((p) => p.id !== paneId),
    })),
  }));
  return ok({ ...state, sessions }, 'PANE_REMOVED', 'Pane removed');
}

export function activatePane(state: MultiplexerState, paneId: MultiplexPaneId): MultiplexerState {
  return {
    ...state,
    sessions: state.sessions.map((s) => ({
      ...s,
      windows: s.windows.map((w) => ({
        ...w,
        activePaneId: w.panes.some((p) => p.id === paneId) ? paneId : w.activePaneId,
        panes: w.panes.map((p) => ({ ...p, isActive: p.id === paneId })),
      })),
    })),
  };
}

export function applyLayout(panes: MultiplexPane[], layout: PaneLayout, width: number, height: number): MultiplexPane[] {
  if (panes.length === 0) return panes;
  switch (layout) {
    case 'even-horizontal': {
      const paneWidth = width / panes.length;
      return panes.map((p, i) => ({
        ...p,
        position: { row: 0, col: Math.floor(i * paneWidth) },
        size: { ...p.size, width: Math.floor(paneWidth), height },
      }));
    }
    case 'even-vertical': {
      const paneHeight = height / panes.length;
      return panes.map((p, i) => ({
        ...p,
        position: { row: Math.floor(i * paneHeight), col: 0 },
        size: { ...p.size, width, height: Math.floor(paneHeight) },
      }));
    }
    case 'main-horizontal': {
      if (panes.length === 1) return [{ ...panes[0]!, position: { row: 0, col: 0 }, size: { ...panes[0]!.size, width, height } }];
      const mainHeight = Math.floor(height * 0.6);
      const subHeight = height - mainHeight;
      const subWidth = width / (panes.length - 1);
      return panes.map((p, i) => i === 0
        ? { ...p, position: { row: 0, col: 0 }, size: { ...p.size, width, height: mainHeight } }
        : { ...p, position: { row: mainHeight, col: Math.floor((i - 1) * subWidth) }, size: { ...p.size, width: Math.floor(subWidth), height: subHeight } });
    }
    case 'main-vertical': {
      if (panes.length === 1) return [{ ...panes[0]!, position: { row: 0, col: 0 }, size: { ...panes[0]!.size, width, height } }];
      const mainWidth = Math.floor(width * 0.6);
      const subWidth = width - mainWidth;
      const subHeight = height / (panes.length - 1);
      return panes.map((p, i) => i === 0
        ? { ...p, position: { row: 0, col: 0 }, size: { ...p.size, width: mainWidth, height } }
        : { ...p, position: { row: Math.floor((i - 1) * subHeight), col: mainWidth }, size: { ...p.size, width: subWidth, height: Math.floor(subHeight) } });
    }
    default: return panes;
  }
}

export function attachSession(state: MultiplexerState, sessionId: MultiplexSessionId): MultiplexerState {
  return {
    ...state,
    activeSessionId: sessionId,
    sessions: state.sessions.map((s) => ({ ...s, isAttached: s.id === sessionId })),
  };
}

export function detachSession(state: MultiplexerState, sessionId: MultiplexSessionId): MultiplexerState {
  return {
    ...state,
    activeSessionId: state.activeSessionId === sessionId ? undefined : state.activeSessionId,
    sessions: state.sessions.map((s) => s.id === sessionId ? { ...s, isAttached: false } : s),
  };
}

// ═══ §26 RECORDING ORCHESTRATION — frame recording, playback, export ═══

export function startRecording(state: RecordingState): EditorResult<RecordingState> {
  if (state.status !== 'idle' && state.status !== 'stopped') {
    return err('INVALID_RECORDING_STATE', `Cannot start recording from ${state.status}`);
  }
  return ok({
    ...state,
    status: 'recording',
    frames: [],
    metadata: { ...state.metadata, startedAt: now(), duration: 0, frameCount: 0, commandCount: 0 },
  }, 'RECORDING_STARTED', 'Recording started');
}

export function stopRecording(state: RecordingState): EditorResult<RecordingState> {
  if (state.status !== 'recording' && state.status !== 'paused') {
    return err('INVALID_RECORDING_STATE', `Cannot stop recording from ${state.status}`);
  }
  const duration = computeRecordingDuration(state.frames);
  return ok({
    ...state,
    status: 'stopped',
    metadata: { ...state.metadata, endedAt: now(), duration },
  }, 'RECORDING_STOPPED', 'Recording stopped');
}

export function pauseRecording(state: RecordingState): EditorResult<RecordingState> {
  if (state.status !== 'recording') return err('INVALID_RECORDING_STATE', `Cannot pause from ${state.status}`);
  return ok({ ...state, status: 'paused' }, 'RECORDING_PAUSED', 'Recording paused');
}

export function resumeRecording(state: RecordingState): EditorResult<RecordingState> {
  if (state.status !== 'paused') return err('INVALID_RECORDING_STATE', `Cannot resume from ${state.status}`);
  return ok({ ...state, status: 'recording' }, 'RECORDING_RESUMED', 'Recording resumed');
}

export function addFrame(state: RecordingState, frame: RecordingFrame): RecordingState {
  const frames = [...state.frames, frame];
  const commandCount = state.metadata.commandCount + (frame.type === 'command' ? 1 : 0);
  return {
    ...state,
    frames,
    metadata: { ...state.metadata, frameCount: frames.length, commandCount },
  };
}

export function addBookmark(state: RecordingState, bookmark: RecordingBookmark): RecordingState {
  const metadata = { ...state.metadata, tags: [...state.metadata.tags, bookmark.label] };
  return { ...state, metadata };
}

export function computeRecordingDuration(frames: RecordingFrame[]): number {
  if (frames.length < 2) return 0;
  return frames[frames.length - 1]!.timestamp - frames[0]!.timestamp;
}

export function extractCommands(frames: RecordingFrame[]): string[] {
  return frames.filter((f) => f.type === 'command').map((f) => f.data);
}

export function seekToFrame(playback: PlaybackState, frameIndex: number): PlaybackState {
  return { ...playback, currentFrame: frameIndex };
}

export function advancePlayback(playback: PlaybackState, _deltaMs: number): PlaybackState {
  if (!playback.isPlaying || playback.isPaused) return playback;
  return { ...playback, currentFrame: playback.currentFrame + 1 };
}

// ═══ §27 TERMINAL COLLABORATION ORCHESTRATION — sharing, participants, permissions ═══

export function addParticipant(state: TerminalCollabState, participant: ShareParticipant): EditorResult<TerminalCollabState> {
  if (state.participants.length >= state.config.maxParticipants) {
    return err('MAX_PARTICIPANTS', 'Maximum participants reached');
  }
  return ok(
    { ...state, participants: [...state.participants, participant] },
    'PARTICIPANT_ADDED', `${participant.name} joined`,
  );
}

export function removeParticipant(state: TerminalCollabState, participantId: ParticipantId): TerminalCollabState {
  return { ...state, participants: state.participants.filter((p) => p.id !== participantId) };
}

export function updateParticipantRole(state: TerminalCollabState, participantId: ParticipantId, role: ParticipantRole): EditorResult<TerminalCollabState> {
  const participant = state.participants.find((p) => p.id === participantId);
  if (!participant) return err('PARTICIPANT_NOT_FOUND', `Participant ${participantId} not found`);
  return ok({
    ...state,
    participants: state.participants.map((p) => p.id === participantId ? { ...p, role } : p),
  }, 'ROLE_UPDATED', `Role updated to ${role}`);
}

export function canParticipantPerform(state: TerminalCollabState, participantId: ParticipantId, action: 'read' | 'write' | 'execute' | 'share' | 'manage'): boolean {
  const participant = state.participants.find((p) => p.id === participantId);
  if (!participant) return false;
  const perms = roleToPermissions(participant.role);
  return perms[action];
}

export function addChatMessage(state: TerminalCollabState, message: ChatMessage): TerminalCollabState {
  return { ...state, chat: [...state.chat, message] };
}

export function isShareExpired(config: ShareConfig): boolean {
  if (!config.expiresAt) return false;
  return now() > config.expiresAt;
}

export function roleToPermissions(role: ParticipantRole): SharePermissions {
  switch (role) {
    case 'owner': return { read: true, write: true, execute: true, share: true, manage: true };
    case 'editor': return { read: true, write: true, execute: true, share: false, manage: false };
    case 'commenter': return { read: true, write: false, execute: false, share: false, manage: false };
    case 'viewer': return { read: true, write: false, execute: false, share: false, manage: false };
  }
}

// ═══ §28 CLOUD TERMINAL ORCHESTRATION — providers, containers, cost ═══

export function createCloudTerminalConfig(provider: CloudProvider, overrides?: Partial<ResourceLimits>): CloudTerminalState {
  const id = asCloudTerminalId(generateId('cloud'));
  return {
    ...emptyCloudTerminalState(id),
    provider,
    resourceLimits: overrides ? { ...DEFAULT_CLOUD_RESOURCE_LIMITS, ...overrides } : DEFAULT_CLOUD_RESOURCE_LIMITS,
  };
}

export function transitionCloudConnection(state: CloudTerminalState, status: CloudConnection['status']): CloudTerminalState {
  return { ...state, connection: { ...state.connection, status } };
}

export function updateContainerStatus(state: CloudTerminalState, status: ContainerInfo['status']): CloudTerminalState {
  if (!state.container) return state;
  return { ...state, container: { ...state.container, status } };
}

export function isCloudTerminalHealthy(state: CloudTerminalState): boolean {
  return state.connection.status === 'connected' && (!state.container || state.container.status === 'running');
}

export function estimateCloudCost(limits: ResourceLimits, durationMs: number): number {
  const hours = durationMs / 3600000;
  const cpuCostPerHour = 0.05;
  const memCostPerGbHour = 0.01;
  return (limits.cpuCores * cpuCostPerHour + (limits.memoryMb / 1024) * memCostPerGbHour) * hours;
}

export function validateResourceLimits(limits: ResourceLimits): EditorResult<ResourceLimits> {
  if (limits.cpuCores <= 0) return err('INVALID_CPU', 'CPU cores must be positive');
  if (limits.memoryMb <= 0) return err('INVALID_MEMORY', 'Memory must be positive');
  if (limits.storageMb <= 0) return err('INVALID_STORAGE', 'Storage must be positive');
  if (limits.cpuCores > 16) return err('EXCEEDS_CPU_LIMIT', 'CPU cores exceed maximum of 16');
  if (limits.memoryMb > 65536) return err('EXCEEDS_MEMORY_LIMIT', 'Memory exceeds maximum of 64GB');
  return ok(limits, 'VALID', 'Resource limits are valid');
}

// ═══ §29 TERMINAL AI ORCHESTRATION — suggestions, risk assessment ═══

export const DANGEROUS_COMMANDS: string[] = [
  'rm -rf /', 'rm -rf ~', 'rm -rf *', 'mkfs', 'dd if=/dev/zero',
  'chmod -R 777 /', 'chown -R', ':(){:|:&};:', 'fork bomb',
  'DROP TABLE', 'DROP DATABASE', 'DELETE FROM', 'TRUNCATE',
  'shutdown', 'reboot', 'halt', 'poweroff',
  'format c:', 'del /f /s /q',
];

export const DESTRUCTIVE_PATTERNS: RegExp[] = [
  /rm\s+-r?f?\s+\//,
  /rm\s+-r?f?\s+~/,
  /rm\s+-r?f?\s+\*/,
  /mkfs\./,
  /dd\s+if=\/dev\/(zero|random)/,
  />\s*\/dev\/sd[a-z]/,
  /chmod\s+-R\s+777\s+\//,
  /DROP\s+(TABLE|DATABASE)/i,
  /DELETE\s+FROM\s+\w+\s*;?\s*$/i,
  /TRUNCATE\s+TABLE/i,
  /format\s+[a-zA-Z]:/i,
  /shutdown\s+(-h|now|\/s)/i,
];

export function scoreSuggestion(suggestion: CommandSuggestion, context: NLContext): number {
  let score = suggestion.confidence;
  if (context.recentCommands.some((cmd) => cmd.startsWith(suggestion.command.split(' ')[0]!))) score += 0.1;
  if (suggestion.source === 'history') score += 0.05;
  if (suggestion.isPersonalized) score += 0.1;
  if (suggestion.risk === 'dangerous') score -= 0.3;
  if (suggestion.risk === 'moderate') score -= 0.1;
  return clamp(score, 0, 1);
}

export function rankSuggestions(suggestions: CommandSuggestion[]): CommandSuggestion[] {
  return [...suggestions].sort((a, b) => b.confidence - a.confidence);
}

export function buildNLContext(session: TerminalSessionState, cwd: string, platform: PTYPlatform): NLContext {
  return {
    cwd,
    recentCommands: session.history.entries.slice(-10).map((e) => e.command),
    shellType: session.shell,
    os: platform,
  };
}

export function assessCommandRisk(command: string): CommandSuggestion['risk'] {
  if (isDestructiveCommand(command)) return 'dangerous';
  const moderatePatterns = [/sudo\s+/, /pip\s+install/, /npm\s+install\s+-g/, /curl.*\|\s*(?:bash|sh)/, /wget.*\|\s*(?:bash|sh)/];
  for (const pattern of moderatePatterns) {
    if (pattern.test(command)) return 'moderate';
  }
  return 'safe';
}

export function isDestructiveCommand(command: string): boolean {
  for (const pattern of DESTRUCTIVE_PATTERNS) {
    if (pattern.test(command)) return true;
  }
  return DANGEROUS_COMMANDS.some((dc) => command.includes(dc));
}

// ═══ §30 AI REQUEST/RESPONSE ORCHESTRATION — envelope, routing, validation ═══

export function createAIRequest(module: AIModule, type: AIRequestType, context: AIContext, overrides?: Partial<AIRequest>): AIRequest {
  return {
    id: asAIRequestId(generateId('aireq')),
    module,
    type,
    context,
    priority: 'normal',
    qualityThreshold: 0.7,
    timestamp: now(),
    ...overrides,
  };
}

export function createAIResponse(requestId: AIRequestId, status: AIResponseStatus, content: AIContent, model: AIModelInfo, performance: AIPerformanceInfo, confidence: number): AIResponse {
  return {
    id: asAIResponseId(generateId('airesp')),
    requestId,
    status,
    content,
    model,
    performance,
    confidence,
  };
}

export function isAIResponseSuccess(response: AIResponse): boolean {
  return response.status === 'success' || response.status === 'partial';
}

export function mergeAIContexts(base: AIContext, override: Partial<AIContext>): AIContext {
  return { ...base, ...override };
}

export function validateAIRequest(request: AIRequest): EditorResult<AIRequest> {
  if (!request.module) return err('MISSING_MODULE', 'AI request requires a module');
  if (!request.type) return err('MISSING_TYPE', 'AI request requires a type');
  if (request.qualityThreshold < 0 || request.qualityThreshold > 1) {
    return err('INVALID_THRESHOLD', 'Quality threshold must be between 0 and 1');
  }
  return ok(request, 'VALID', 'AI request is valid');
}

export function priorityToNumber(priority: AIPriority): number {
  switch (priority) {
    case 'critical': return 3;
    case 'high': return 2;
    case 'normal': return 1;
    case 'low': return 0;
  }
}

// ═══ §31 AI AGENT ORCHESTRATION — agents, tasks, memory, execution plans ═══

export function createAgent(id: AIAgentId, type: AIAgentType, config?: Partial<AIAgentConfig>): AIAgentState {
  const base = emptyAIAgentState(id, type);
  return config ? { ...base, config: { ...base.config, ...config } } : base;
}

const VALID_AGENT_TRANSITIONS: Record<string, AIAgentStatus[]> = {
  idle: ['planning', 'executing', 'terminated'],
  planning: ['executing', 'idle', 'error', 'paused', 'terminated'],
  executing: ['waiting', 'learning', 'idle', 'error', 'paused', 'terminated'],
  waiting: ['executing', 'idle', 'error', 'paused', 'terminated'],
  learning: ['idle', 'executing', 'error', 'terminated'],
  error: ['idle', 'terminated'],
  paused: ['idle', 'planning', 'executing', 'terminated'],
  terminated: [],
};

export function transitionAgentStatus(state: AIAgentState, newStatus: AIAgentStatus): EditorResult<AIAgentState> {
  if (!validateAgentTransition(state.status, newStatus)) {
    return err('INVALID_AGENT_TRANSITION', `Cannot transition agent from ${state.status} to ${newStatus}`);
  }
  return ok({ ...state, status: newStatus }, 'AGENT_STATUS_CHANGED', `Agent status: ${newStatus}`);
}

export function validateAgentTransition(from: AIAgentStatus, to: AIAgentStatus): boolean {
  const allowed = VALID_AGENT_TRANSITIONS[from];
  return allowed ? allowed.includes(to) : false;
}

export function assignTask(state: AIAgentState, task: AIAgentTask): EditorResult<AIAgentState> {
  if (state.currentTask) return err('TASK_IN_PROGRESS', 'Agent already has a task');
  return ok({ ...state, currentTask: task, status: 'planning' }, 'TASK_ASSIGNED', 'Task assigned to agent');
}

export function completeTask(state: AIAgentState, result: AITaskResult): AIAgentState {
  const perf = state.performance;
  const total = perf.tasksCompleted + 1;
  const succeeded = perf.tasksSucceeded + (result.success ? 1 : 0);
  return {
    ...state,
    currentTask: undefined,
    status: 'idle',
    performance: {
      tasksCompleted: total,
      tasksSucceeded: succeeded,
      averageDuration: (perf.averageDuration * perf.tasksCompleted + result.duration) / total,
      averageQuality: perf.averageQuality,
    },
  };
}

export function updateAgentMemory(state: AIAgentState, memoryType: 'shortTerm' | 'longTerm' | 'working', key: string, value: unknown): AIAgentState {
  if (memoryType === 'working') {
    return { ...state, memory: { ...state.memory, working: [...state.memory.working, value] } };
  }
  return {
    ...state,
    memory: {
      ...state.memory,
      [memoryType]: { ...state.memory[memoryType], [key]: value },
    },
  };
}

export function addEpisode(state: AIAgentState, episode: AIEpisode): AIAgentState {
  return {
    ...state,
    memory: { ...state.memory, episodic: [...state.memory.episodic, episode] },
  };
}

export function createExecutionPlan(steps: AIExecutionStep[]): AIExecutionPlan {
  const totalDuration = steps.reduce((sum, s) => sum + s.estimatedDuration, 0);
  return {
    steps,
    estimatedDuration: totalDuration,
    risks: [],
    successCriteria: [],
  };
}

export function advancePlan(plan: AIExecutionPlan, stepId: string, status: AIExecutionStep['status']): AIExecutionPlan {
  return {
    ...plan,
    steps: plan.steps.map((s) => s.id === stepId ? { ...s, status } : s),
  };
}

export function agentCapabilities(type: AIAgentType): string[] {
  const caps: Record<AIAgentType, string[]> = {
    'code-generator': ['generate', 'complete', 'scaffold', 'translate'],
    'code-reviewer': ['review', 'suggest', 'annotate', 'assess'],
    'test-generator': ['generate-tests', 'coverage-analysis', 'test-design'],
    'refactoring-expert': ['refactor', 'rename', 'extract', 'inline', 'restructure'],
    'debugging-specialist': ['debug', 'diagnose', 'trace', 'bisect', 'fix'],
    'security-auditor': ['scan', 'assess-risk', 'detect-vulnerability', 'recommend'],
    'performance-optimizer': ['profile', 'analyze-bottleneck', 'optimize', 'benchmark'],
    'project-manager': ['plan', 'estimate', 'track', 'report', 'coordinate'],
    'architect': ['design', 'model', 'evaluate-patterns', 'document-architecture'],
    'devops-engineer': ['deploy', 'configure', 'monitor', 'automate', 'scale'],
    'qa-engineer': ['test-plan', 'regression', 'acceptance', 'load-test'],
    'documentation-writer': ['document', 'explain', 'tutorial', 'reference', 'changelog'],
  };
  return caps[type] ?? [];
}

// ═══ §32 AI CONVERSATION ORCHESTRATION — messages, threading, token estimation ═══

export function createConversation(id: AIConversationId, type: ConversationType): AIConversationState {
  return { ...emptyAIConversationState(id), type };
}

export function addMessage(state: AIConversationState, message: AIMessage): AIConversationState {
  return { ...state, messages: [...state.messages, message], updatedAt: now() };
}

export function editMessage(state: AIConversationState, messageId: AIMessageId, content: MessageContent): EditorResult<AIConversationState> {
  const idx = state.messages.findIndex((m) => m.id === messageId);
  if (idx === -1) return err('MESSAGE_NOT_FOUND', `Message ${messageId} not found`);
  const messages = [...state.messages];
  messages[idx] = { ...messages[idx]!, content };
  return ok({ ...state, messages, updatedAt: now() }, 'MESSAGE_EDITED', 'Message edited');
}

export function addReaction(state: AIConversationState, messageId: AIMessageId, reaction: MessageReaction): AIConversationState {
  return {
    ...state,
    messages: state.messages.map((m) =>
      m.id === messageId ? { ...m, reactions: [...m.reactions, reaction] } : m,
    ),
    updatedAt: now(),
  };
}

export function getMessageThread(state: AIConversationState, messageId: AIMessageId): AIMessage[] {
  const thread: AIMessage[] = [];
  let current = state.messages.find((m) => m.id === messageId);
  while (current) {
    thread.unshift(current);
    current = current.parentId ? state.messages.find((m) => m.id === current!.parentId) : undefined;
  }
  return thread;
}

export function conversationTokenCount(state: AIConversationState): number {
  let count = 0;
  for (const msg of state.messages) {
    const text = msg.content.text ?? '';
    const code = msg.content.code?.value ?? '';
    count += Math.ceil((text.length + code.length) / 4);
  }
  return count;
}

export function truncateConversation(state: AIConversationState, maxMessages: number): AIConversationState {
  if (state.messages.length <= maxMessages) return state;
  const systemMessages = state.messages.filter((m) => m.role === 'system');
  const otherMessages = state.messages.filter((m) => m.role !== 'system');
  const kept = otherMessages.slice(-maxMessages + systemMessages.length);
  return { ...state, messages: [...systemMessages, ...kept] };
}

// ═══ §33 AI CODE GENERATION ORCHESTRATION — requests, quality, security ═══

export const DEFAULT_GENERATION_PREFERENCES: CodeGenerationPreferences = {
  codeStyle: 'balanced',
  errorHandling: 'result-type',
  testingApproach: 'unit',
  documentationLevel: 'standard',
};

export function buildGenerationRequest(type: CodeGenerationType, context: CodeGenerationContext, overrides?: Partial<CodeGenerationConstraints>): CodeGenerationRequest {
  return {
    type,
    context,
    constraints: { maxLength: 10000, ...overrides },
    preferences: DEFAULT_GENERATION_PREFERENCES,
  };
}

export function validateGeneratedCode(result: CodeGenerationResult): EditorResult<CodeGenerationResult> {
  if (!result.code || result.code.trim().length === 0) {
    return err('EMPTY_CODE', 'Generated code is empty');
  }
  if (result.quality.score < 0.3) {
    return err('LOW_QUALITY', 'Generated code quality is below threshold');
  }
  if (result.security.vulnerabilities.some((v) => v.severity === 'critical')) {
    return err('SECURITY_ISSUE', 'Generated code has critical security vulnerabilities');
  }
  return ok(result, 'VALID', 'Generated code is valid');
}

export function scoreCodeQuality(code: string): QualityAssessment {
  const lines = code.split('\n');
  const issues: string[] = [];
  let readability = 0.7;
  let maintainability = 0.7;
  let testability = 0.7;

  const avgLineLength = lines.reduce((sum, l) => sum + l.length, 0) / Math.max(1, lines.length);
  if (avgLineLength > 120) { issues.push('Lines too long'); readability -= 0.1; }
  if (avgLineLength > 200) { issues.push('Very long lines'); readability -= 0.2; }

  const commentLines = lines.filter((l) => l.trim().startsWith('//') || l.trim().startsWith('/*') || l.trim().startsWith('*'));
  const commentRatio = commentLines.length / Math.max(1, lines.length);
  if (commentRatio < 0.05) { issues.push('Low comment ratio'); maintainability -= 0.1; }

  const deepNesting = lines.filter((l) => getIndentLevel(l, 2) > 16);
  if (deepNesting.length > 0) { issues.push('Deep nesting detected'); readability -= 0.15; maintainability -= 0.1; }

  const score = (readability + maintainability + testability) / 3;
  return { score: clamp(score, 0, 1), readability: clamp(readability, 0, 1), maintainability: clamp(maintainability, 0, 1), testability: clamp(testability, 0, 1), issues };
}

export function detectSecurityIssues(code: string): SecurityVulnerability[] {
  const vulns: SecurityVulnerability[] = [];
  const patterns: Array<[RegExp, string, string, string]> = [
    [/eval\s*\(/, 'Unsafe eval usage', 'high', 'CWE-95'],
    [/innerHTML\s*=/, 'Potential XSS via innerHTML', 'high', 'CWE-79'],
    [/document\.write\s*\(/, 'Unsafe document.write', 'medium', 'CWE-79'],
    [/\bexec\s*\(/, 'Command injection risk', 'high', 'CWE-78'],
    [/password\s*=\s*['"][^'"]+['"]/i, 'Hardcoded password', 'critical', 'CWE-798'],
    [/api[_-]?key\s*=\s*['"][^'"]+['"]/i, 'Hardcoded API key', 'critical', 'CWE-798'],
    [/secret\s*=\s*['"][^'"]+['"]/i, 'Hardcoded secret', 'critical', 'CWE-798'],
  ];

  for (const [regex, desc, severity, cwe] of patterns) {
    const match = regex.exec(code);
    if (match) {
      const pos = offsetToPosition(code, match.index);
      vulns.push({
        type: cwe,
        severity: severity as SecurityVulnerability['severity'],
        description: desc,
        location: { start: pos, end: pos },
        mitigation: `Avoid ${desc.toLowerCase()}`,
        cweId: cwe,
      });
    }
  }
  return vulns;
}

export function mergeGenerationPreferences(base: CodeGenerationPreferences, overrides: Partial<CodeGenerationPreferences>): CodeGenerationPreferences {
  return { ...base, ...overrides };
}

// ═══ §34 AI ANALYSIS ORCHESTRATION — complexity, Halstead, duplication ═══

export function buildAnalysisRequest(type: CodeAnalysisType, target: AnalysisTarget, options?: Partial<AnalysisOptions>): CodeAnalysisRequest {
  return {
    type,
    target,
    options: { depth: 'standard', includeHistory: false, includeDependencies: false, timeout: 30000, ...options },
  };
}

export function computeComplexity(code: string): ComplexityMetrics {
  const lines = code.split('\n');
  let cyclomatic = 1;
  let cognitive = 0;
  let nestingDepth = 0;

  const branchKeywords = /\b(if|else\s+if|else|for|while|do|switch|case|catch|&&|\|\||\?\?)\b/g;
  for (const line of lines) {
    const trimmed = line.trim();
    let match: RegExpExecArray | null;
    const regex = new RegExp(branchKeywords.source, 'g');
    while ((match = regex.exec(trimmed)) !== null) {
      cyclomatic++;
      cognitive += 1 + nestingDepth;
    }
    const opens = (trimmed.match(/{/g) ?? []).length;
    const closes = (trimmed.match(/}/g) ?? []).length;
    nestingDepth += opens - closes;
    nestingDepth = Math.max(0, nestingDepth);
  }

  const tokens = code.match(/[a-zA-Z_$]\w*|[+\-*/%=<>!&|^~?:;.,{}()\[\]]/g) ?? [];
  const operators = tokens.filter((t) => /^[+\-*/%=<>!&|^~?:;.,{}()\[\]]$/.test(t));
  const operands = tokens.filter((t) => /^[a-zA-Z_$]\w*$/.test(t));
  const uniqueOps = new Set(operators).size;
  const uniqueOperands = new Set(operands).size;

  const halstead = computeHalstead(operators.length, operands.length, uniqueOps, uniqueOperands);
  return { cyclomatic, cognitive, halstead };
}

export function computeHalstead(operators: number, operands: number, uniqueOps: number, uniqueOperands: number): HalsteadMetrics {
  const N = operators + operands;
  const n = uniqueOps + uniqueOperands;
  const volume = n > 0 ? N * Math.log2(Math.max(1, n)) : 0;
  const difficulty = uniqueOperands > 0 ? (uniqueOps / 2) * (operands / Math.max(1, uniqueOperands)) : 0;
  const effort = volume * difficulty;
  const timeToImplement = effort / 18;
  const estimatedBugs = volume / 3000;
  return { length: N, vocabulary: n, volume, difficulty, effort, timeToImplement, estimatedBugs };
}

export function computeMaintainability(halstead: HalsteadMetrics, cyclomatic: number, linesOfCode: number): number {
  const loc = Math.max(1, linesOfCode);
  const vol = Math.max(1, halstead.volume);
  const raw = 171 - 5.2 * Math.log(vol) - 0.23 * cyclomatic - 16.2 * Math.log(loc);
  return clamp(raw / 171 * 100, 0, 100);
}

export function findDuplicateBlocks(content: string, minLines: number): DuplicateBlock[] {
  const lines = content.split('\n');
  const duplicates: DuplicateBlock[] = [];
  const seen = new Map<string, number[]>();

  for (let i = 0; i <= lines.length - minLines; i++) {
    const block = lines.slice(i, i + minLines).map((l) => l.trim()).join('\n');
    if (block.trim().length === 0) continue;
    const existing = seen.get(block);
    if (existing) {
      existing.push(i);
    } else {
      seen.set(block, [i]);
    }
  }

  for (const [, lineNumbers] of seen) {
    if (lineNumbers.length > 1) {
      duplicates.push({
        ranges: lineNumbers.map((ln) => ({
          start: { line: ln, column: 0 },
          end: { line: ln + minLines - 1, column: 0 },
        })),
        lineCount: minLines,
        tokenCount: 0,
      });
    }
  }
  return duplicates;
}

export function categorizeFindings(findings: AnalysisFinding[]): Record<string, AnalysisFinding[]> {
  return groupBy(findings, (f) => f.type);
}

export function computeHealthScore(findings: AnalysisFinding[], _metrics: CodeMetrics): number {
  let score = 100;
  for (const finding of findings) {
    switch (finding.severity) {
      case 'critical': score -= 20; break;
      case 'high': score -= 10; break;
      case 'medium': score -= 5; break;
      case 'low': score -= 2; break;
      case 'info': score -= 0.5; break;
    }
  }
  return clamp(score, 0, 100);
}

// ═══ §35 AI MODEL ORCHESTRATION — selection, scoring, circuit breaker ═══

export function selectModel(models: AIModelState[], requirements: AIConstraints, strategy: RoutingStrategy): AIModelState | null {
  const available = models.filter(isModelAvailable);
  if (available.length === 0) return null;
  const sorted = sortModelsByStrategy(available, strategy);
  for (const model of sorted) {
    if (requirements.requiredCapabilities) {
      const hasAll = requirements.requiredCapabilities.every((cap) => model.specs.capabilities.includes(cap));
      if (!hasAll) continue;
    }
    if (requirements.maxTokens && model.specs.maxOutputTokens < requirements.maxTokens) continue;
    return model;
  }
  return sorted[0] ?? null;
}

export function scoreModelFit(model: AIModelState, requirements: AIConstraints): number {
  let score = 0.5;
  if (model.status === 'ready') score += 0.2;
  if (requirements.requiredCapabilities) {
    const matched = requirements.requiredCapabilities.filter((c) => model.specs.capabilities.includes(c)).length;
    score += 0.3 * (matched / Math.max(1, requirements.requiredCapabilities.length));
  }
  if (requirements.maxLatencyMs && model.performance.latencyP50Ms < requirements.maxLatencyMs) score += 0.1;
  if (requirements.maxCost) {
    const estimated = modelCostEstimate(model, requirements.maxTokens ?? 1000);
    if (estimated <= requirements.maxCost) score += 0.1;
  }
  return clamp(score, 0, 1);
}

export function isModelAvailable(model: AIModelState): boolean {
  return model.status === 'available' || model.status === 'ready';
}

export function shouldTripCircuitBreaker(failures: number, config: CircuitBreakerConfig): boolean {
  return failures >= config.failureThreshold;
}

export function modelCostEstimate(model: AIModelState, tokens: number): number {
  return (tokens / 1_000_000) * model.performance.costPerMToken;
}

export function sortModelsByStrategy(models: AIModelState[], strategy: RoutingStrategy): AIModelState[] {
  switch (strategy) {
    case 'performance': return [...models].sort((a, b) => a.performance.latencyP50Ms - b.performance.latencyP50Ms);
    case 'cost': return [...models].sort((a, b) => a.performance.costPerMToken - b.performance.costPerMToken);
    case 'quality': return [...models].sort((a, b) => b.performance.qualityScore - a.performance.qualityScore);
    case 'availability': return [...models].sort((a, b) => b.performance.reliabilityScore - a.performance.reliabilityScore);
    case 'round-robin': return models;
    case 'weighted': return [...models].sort((a, b) => {
      const scoreA = a.performance.qualityScore * 0.4 + a.performance.reliabilityScore * 0.3 + (1 - a.performance.costPerMToken / 100) * 0.3;
      const scoreB = b.performance.qualityScore * 0.4 + b.performance.reliabilityScore * 0.3 + (1 - b.performance.costPerMToken / 100) * 0.3;
      return scoreB - scoreA;
    });
    default: return models;
  }
}

// ═══ §36 AI CACHE ORCHESTRATION — key computation, lookup, eviction ═══

export function computeCacheKey(request: AIRequest): string {
  const parts = [request.module, request.type, JSON.stringify(request.context)];
  let hash = 0;
  const str = parts.join('|');
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return `cache_${Math.abs(hash).toString(36)}`;
}

export function lookupCache(state: AICacheState, key: string): AICacheEntry | null {
  const entry = state.entries.get(key);
  if (!entry) return null;
  if (now() - entry.timestamp > entry.ttl) return null;
  return entry;
}

export function insertCache(state: AICacheState, entry: AICacheEntry): AICacheState {
  const entries = new Map(state.entries);
  entries.set(entry.key, entry);
  return {
    ...state,
    entries,
    stats: { ...state.stats, cacheSize: entries.size },
  };
}

export function evictCache(state: AICacheState): AICacheState {
  const entries = new Map(state.entries);
  const toEvict: string[] = [];
  const n = now();

  for (const [key, entry] of entries) {
    if (n - entry.timestamp > entry.ttl) toEvict.push(key);
  }

  if (toEvict.length === 0 && entries.size > state.config.maxEntries) {
    const sorted = [...entries.entries()].sort((a, b) => computeUsefulness(a[1]) - computeUsefulness(b[1]));
    const excess = entries.size - state.config.maxEntries;
    for (let i = 0; i < excess; i++) toEvict.push(sorted[i]![0]);
  }

  for (const key of toEvict) entries.delete(key);
  return {
    ...state,
    entries,
    stats: { ...state.stats, cacheSize: entries.size, evictionCount: state.stats.evictionCount + toEvict.length },
  };
}

export function computeTTL(confidence: number, requestType: AIRequestType, config: AICacheConfig): number {
  let multiplier = 1;
  if (confidence > 0.9) multiplier = 4;
  else if (confidence > 0.7) multiplier = 2;
  else if (confidence < 0.5) multiplier = 0.5;

  const volatileTypes: AIRequestType[] = ['completion', 'suggestion'];
  if (volatileTypes.includes(requestType)) multiplier *= 0.5;

  return Math.floor(config.defaultTtlMs * multiplier);
}

export function computeUsefulness(entry: AICacheEntry): number {
  const n = now();
  const age = n - entry.lastAccessed;
  const maxAge = 86400000; // 24h
  const recency = 1 - Math.min(1, age / maxAge);
  const frequency = Math.min(1, entry.accessCount / 100);
  const quality = entry.quality;
  return recency * 0.4 + frequency * 0.4 + quality * 0.2;
}

export function textSimilarity(a: string, b: string): number {
  const setA = new Set(a.toLowerCase().split(/\s+/));
  const setB = new Set(b.toLowerCase().split(/\s+/));
  let intersection = 0;
  for (const word of setA) {
    if (setB.has(word)) intersection++;
  }
  const union = setA.size + setB.size - intersection;
  return union === 0 ? 0 : intersection / union;
}

export function updateCacheStats(stats: AICacheStats, hit: boolean, responseTimeMs: number): AICacheStats {
  const totalRequests = stats.totalRequests + 1;
  const totalHits = stats.totalHits + (hit ? 1 : 0);
  const totalMisses = stats.totalMisses + (hit ? 0 : 1);
  const avgTime = (stats.averageResponseTimeMs * stats.totalRequests + responseTimeMs) / totalRequests;
  return {
    ...stats,
    totalRequests,
    totalHits,
    totalMisses,
    hitRate: totalHits / totalRequests,
    missRate: totalMisses / totalRequests,
    averageResponseTimeMs: avgTime,
  };
}

// ═══ §37 AI PERSONALIZATION ORCHESTRATION — profiles, behavior, learning ═══

export function updateProfile(state: PersonalizationState, update: Partial<UserProfile>): PersonalizationState {
  return { ...state, profile: { ...state.profile, ...update } };
}

export function recordBehavior(state: PersonalizationState, pattern: BehaviorPattern): PersonalizationState {
  const existing = state.behaviorPatterns.findIndex((p) => p.pattern === pattern.pattern && p.type === pattern.type);
  if (existing >= 0) {
    const patterns = [...state.behaviorPatterns];
    patterns[existing] = { ...patterns[existing]!, frequency: patterns[existing]!.frequency + 1, lastSeen: now() };
    return { ...state, behaviorPatterns: patterns };
  }
  return { ...state, behaviorPatterns: [...state.behaviorPatterns, pattern] };
}

export function recordLearning(state: PersonalizationState, event: LearningEvent): PersonalizationState {
  return { ...state, learningHistory: [...state.learningHistory, event] };
}

export function inferSkillLevel(patterns: BehaviorPattern[], history: LearningEvent[]): SkillLevel {
  const acceptRate = history.length > 0
    ? history.filter((e) => e.outcome === 'accepted').length / history.length
    : 0.5;
  const avgFrequency = patterns.length > 0
    ? patterns.reduce((sum, p) => sum + p.frequency, 0) / patterns.length
    : 0;

  if (acceptRate > 0.8 && avgFrequency > 50) return 'expert';
  if (acceptRate > 0.6 && avgFrequency > 20) return 'advanced';
  if (acceptRate > 0.4 || avgFrequency > 5) return 'intermediate';
  return 'beginner';
}

export function adaptSuggestions<T>(items: T[], profile: UserProfile, scorer: (item: T, profile: UserProfile) => number): T[] {
  return [...items].sort((a, b) => scorer(b, profile) - scorer(a, profile));
}

export function detectCodingStyle(code: string): CodingStyleProfile {
  const usesSpaces = /^ {2,}/m.test(code);
  const usesTabs = /^\t/m.test(code);
  const indentation: CodingStyleProfile['indentation'] = usesTabs ? 'tabs' : (code.match(/^ {4}/m) ? 'spaces-4' : 'spaces-2');

  const singleQuotes = (code.match(/'/g) ?? []).length;
  const doubleQuotes = (code.match(/"/g) ?? []).length;
  const quoteStyle: CodingStyleProfile['quoteStyle'] = singleQuotes > doubleQuotes ? 'single' : 'double';

  const hasSemicolons = /;\s*$/m.test(code);
  const camelCase = (code.match(/[a-z][a-zA-Z0-9]*[A-Z][a-zA-Z0-9]*/g) ?? []).length;
  const snakeCase = (code.match(/[a-z]+_[a-z]+/g) ?? []).length;
  const naming: CodingStyleProfile['naming'] = snakeCase > camelCase ? 'snake_case' : 'camelCase';

  const lines = code.split('\n');
  const maxLine = Math.max(...lines.map((l) => l.length));

  return {
    naming,
    indentation,
    quoteStyle,
    semicolons: hasSemicolons,
    trailingComma: 'es5',
    lineLength: Math.min(maxLine, 120),
  };
}

// ═══ §38 AI SECURITY ORCHESTRATION — validation, scanning, PII, audit ═══

export const SQL_INJECTION_PATTERNS: RegExp[] = [
  /'\s*(OR|AND)\s+/i,
  /;\s*(DROP|DELETE|UPDATE|INSERT|ALTER|CREATE)/i,
  /UNION\s+SELECT/i,
  /--\s*$/,
  /\/\*.*\*\//,
];

export const XSS_PATTERNS: RegExp[] = [
  /<script\b[^>]*>/i,
  /on\w+\s*=\s*['"]/i,
  /javascript\s*:/i,
  /data\s*:\s*text\/html/i,
];

export const PATH_TRAVERSAL_PATTERNS: RegExp[] = [
  /\.\.\//,
  /\.\.\\/, 
  /%2e%2e/i,
  /%252e%252e/i,
];

export const SECRET_PATTERNS: RegExp[] = [
  /(?:password|passwd|pwd)\s*[:=]\s*['"][^'"]{3,}/i,
  /(?:api[_-]?key|apikey)\s*[:=]\s*['"][^'"]{8,}/i,
  /(?:secret|token)\s*[:=]\s*['"][^'"]{8,}/i,
  /(?:AWS|aws)[_-]?(?:ACCESS|SECRET)[_-]?(?:KEY|ID)\s*[:=]\s*['"][^'"]+/,
  /-----BEGIN (?:RSA |DSA |EC )?PRIVATE KEY-----/,
  /ghp_[0-9a-zA-Z]{36}/,
  /sk-[0-9a-zA-Z]{32,}/,
];

export function validateInput(input: string, config: InputValidationConfig): EditorResult<string> {
  if (input.length > config.maxInputLength) {
    return err('INPUT_TOO_LONG', `Input exceeds max length of ${config.maxInputLength}`);
  }
  if (config.detectInjection) {
    for (const pattern of SQL_INJECTION_PATTERNS) {
      if (pattern.test(input)) return err('INJECTION_DETECTED', 'Potential injection detected');
    }
  }
  for (const pattern of config.blockedPatterns) {
    if (new RegExp(pattern).test(input)) {
      return err('BLOCKED_PATTERN', 'Input matches blocked pattern');
    }
  }
  return ok(input, 'VALID', 'Input is valid');
}

export function filterOutput(output: string, config: OutputFilteringConfig): string {
  let filtered = output;
  if (filtered.length > config.maxOutputLength) {
    filtered = filtered.slice(0, config.maxOutputLength);
  }
  if (config.filterSensitiveData) {
    for (const pattern of SECRET_PATTERNS) {
      filtered = filtered.replace(pattern, '[REDACTED]');
    }
  }
  return filtered;
}

export function scanCode(code: string, config: CodeScanningConfig): SecurityVulnerability[] {
  const vulns: SecurityVulnerability[] = [];

  if (config.detectSqlInjection) {
    for (const pattern of SQL_INJECTION_PATTERNS) {
      if (pattern.test(code)) {
        vulns.push({ type: 'sql-injection', severity: 'high', description: 'Potential SQL injection', mitigation: 'Use parameterized queries' });
      }
    }
  }
  if (config.detectXss) {
    for (const pattern of XSS_PATTERNS) {
      if (pattern.test(code)) {
        vulns.push({ type: 'xss', severity: 'high', description: 'Potential XSS vulnerability', mitigation: 'Sanitize user input' });
      }
    }
  }
  if (config.detectPathTraversal) {
    for (const pattern of PATH_TRAVERSAL_PATTERNS) {
      if (pattern.test(code)) {
        vulns.push({ type: 'path-traversal', severity: 'high', description: 'Potential path traversal', mitigation: 'Validate file paths' });
      }
    }
  }
  if (config.detectHardcodedSecrets) {
    for (const pattern of SECRET_PATTERNS) {
      if (pattern.test(code)) {
        vulns.push({ type: 'hardcoded-secret', severity: 'critical', description: 'Hardcoded secret detected', mitigation: 'Use environment variables' });
      }
    }
  }
  return vulns;
}

export function detectPII(text: string): { type: string; value: string; range: Range }[] {
  const detections: { type: string; value: string; range: Range }[] = [];
  const patterns: Array<[RegExp, string]> = [
    [new RegExp(EMAIL_REGEX.source, 'g'), 'email'],
    [/\b\d{3}-\d{2}-\d{4}\b/g, 'ssn'],
    [/\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g, 'credit-card'],
    [/\b\+?1?[-.\s]?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/g, 'phone'],
  ];
  for (const [regex, type] of patterns) {
    let match: RegExpExecArray | null;
    while ((match = regex.exec(text)) !== null) {
      const start = offsetToPosition(text, match.index);
      const end = offsetToPosition(text, match.index + match[0].length);
      detections.push({ type, value: match[0], range: { start, end } });
    }
  }
  return detections;
}

export function anonymizeText(text: string, detections: { type: string; value: string; range: Range }[]): string {
  let result = text;
  const sorted = [...detections].sort((a, b) =>
    positionToOffset(text, b.range.start) - positionToOffset(text, a.range.start));
  for (const det of sorted) {
    const start = positionToOffset(result, det.range.start);
    const end = positionToOffset(result, det.range.end);
    const placeholder = `[${det.type.toUpperCase()}]`;
    result = result.slice(0, start) + placeholder + result.slice(end);
  }
  return result;
}

export function createAuditEntry(requestId: AIRequestId, action: string, userId: string, riskLevel: AIAuditEntry['riskLevel'], outcome: AIAuditEntry['outcome']): AIAuditEntry {
  return { id: generateId('audit'), requestId, action, timestamp: now(), userId, riskLevel, outcome, details: {} };
}

export function assessRequestRisk(request: AIRequest): AIAuditEntry['riskLevel'] {
  if (request.priority === 'critical') return 'high';
  const riskyTypes: AIRequestType[] = ['generation', 'fix', 'refactor'];
  if (riskyTypes.includes(request.type)) return 'medium';
  return 'low';
}

// ═══ §39 WORKSPACE ORCHESTRATION — layout, panels, tabs ═══

export function createWorkspace(name: string, rootFolders: string[] = []): EditorResult<WorkspaceState> {
  const state: WorkspaceState = {
    id: asWorkspaceId(generateId('ws')),
    name,
    rootFolders,
    layout: { ...DEFAULT_WORKSPACE_LAYOUT },
    panels: [],
    editors: [],
    terminals: [],
    theme: 'default',
    settings: {},
  };
  return ok(state, 'WORKSPACE_CREATED', `Workspace "${name}" created`, [{ kind: 'created', domain: 'workspace', timestamp: now(), payload: { workspaceId: state.id } } as EditorEvent]);
}

export function addRootFolder(state: WorkspaceState, folder: string): EditorResult<WorkspaceState> {
  if (state.rootFolders.includes(folder)) return err('FOLDER_EXISTS', `Folder "${folder}" already in workspace`);
  return ok({ ...state, rootFolders: [...state.rootFolders, folder] }, 'FOLDER_ADDED', `Root folder added`);
}

export function removeRootFolder(state: WorkspaceState, folder: string): EditorResult<WorkspaceState> {
  if (!state.rootFolders.includes(folder)) return err('FOLDER_NOT_FOUND', `Folder "${folder}" not in workspace`);
  return ok({ ...state, rootFolders: state.rootFolders.filter(f => f !== folder) }, 'FOLDER_REMOVED', `Root folder removed`);
}

export function updateWorkspaceLayout(state: WorkspaceState, layout: Partial<WorkspaceLayout>): EditorResult<WorkspaceState> {
  return ok({ ...state, layout: { ...state.layout, ...layout } }, 'LAYOUT_UPDATED', 'Workspace layout updated');
}

export function addPanel(state: WorkspaceState, panel: PanelState): EditorResult<WorkspaceState> {
  if (state.panels.some(p => p.id === panel.id)) return err('PANEL_EXISTS', `Panel "${panel.id}" already exists`);
  return ok({ ...state, panels: [...state.panels, panel] }, 'PANEL_ADDED', 'Panel added');
}

export function togglePanel(state: WorkspaceState, panelId: PanelId): EditorResult<WorkspaceState> {
  const idx = state.panels.findIndex(p => p.id === panelId);
  if (idx === -1) return err('PANEL_NOT_FOUND', `Panel "${panelId}" not found`);
  const panel = state.panels[idx]!;
  const updated = [...state.panels];
  updated[idx] = { ...panel, isVisible: !panel.isVisible };
  return ok({ ...state, panels: updated }, 'PANEL_TOGGLED', `Panel ${panel.isVisible ? 'hidden' : 'shown'}`);
}

export function createTabState(groupId: TabGroupId, label: string, uri?: string): TabState {
  return {
    id: asTabId(generateId('tab')),
    groupId,
    label,
    uri,
    isPinned: false,
    isPreview: true,
    isDirty: false,
  };
}

export function createTabGroup(): TabGroupState {
  return {
    id: asTabGroupId(generateId('tgrp')),
    tabs: [],
    isActive: true,
  };
}

export function addTab(group: TabGroupState, tab: TabState): EditorResult<TabGroupState> {
  if (group.tabs.some(t => t.id === tab.id)) return err('TAB_EXISTS', `Tab "${tab.id}" already exists`);
  return ok({ ...group, tabs: [...group.tabs, tab], activeTabId: tab.id }, 'TAB_ADDED', 'Tab added');
}

export function removeTab(group: TabGroupState, tabId: TabId): EditorResult<TabGroupState> {
  const idx = group.tabs.findIndex(t => t.id === tabId);
  if (idx === -1) return err('TAB_NOT_FOUND', `Tab "${tabId}" not found`);
  const remaining = group.tabs.filter(t => t.id !== tabId);
  const newActive = group.activeTabId === tabId
    ? (remaining[Math.min(idx, remaining.length - 1)]?.id)
    : group.activeTabId;
  return ok({ ...group, tabs: remaining, activeTabId: newActive }, 'TAB_REMOVED', 'Tab removed');
}

export function activateTab(group: TabGroupState, tabId: TabId): EditorResult<TabGroupState> {
  if (!group.tabs.some(t => t.id === tabId)) return err('TAB_NOT_FOUND', `Tab "${tabId}" not found`);
  const tabs = group.tabs.map(t => t.isPreview && t.id === tabId ? { ...t, isPreview: false } : t);
  return ok({ ...group, tabs, activeTabId: tabId }, 'TAB_ACTIVATED', 'Tab activated');
}

export function pinTab(group: TabGroupState, tabId: TabId): EditorResult<TabGroupState> {
  const idx = group.tabs.findIndex(t => t.id === tabId);
  if (idx === -1) return err('TAB_NOT_FOUND', `Tab "${tabId}" not found`);
  const tab = group.tabs[idx]!;
  const updated = [...group.tabs];
  updated[idx] = { ...tab, isPinned: !tab.isPinned, isPreview: false };
  // Move pinned tabs to front
  updated.sort((a, b) => (a.isPinned === b.isPinned ? 0 : a.isPinned ? -1 : 1));
  return ok({ ...group, tabs: updated }, 'TAB_PINNED', `Tab ${tab.isPinned ? 'unpinned' : 'pinned'}`);
}

export function moveTab(group: TabGroupState, tabId: TabId, toIndex: number): EditorResult<TabGroupState> {
  const fromIdx = group.tabs.findIndex(t => t.id === tabId);
  if (fromIdx === -1) return err('TAB_NOT_FOUND', `Tab "${tabId}" not found`);
  const clamped = clamp(toIndex, 0, group.tabs.length - 1);
  const tabs = [...group.tabs];
  const [tab] = tabs.splice(fromIdx, 1);
  tabs.splice(clamped, 0, tab!);
  return ok({ ...group, tabs }, 'TAB_MOVED', 'Tab moved');
}

// ═══ §40 WORKSPACE COLLABORATION & SYNC — presence, follow, OT ═══

export function emptyCollabState(): WorkspaceCollabState {
  return { isActive: false, participants: [], cursors: [] };
}

export function emptySyncState(): WorkspaceSyncState {
  return { provider: '', lastSyncedAt: 0, status: 'offline', conflicts: [], selectiveSync: {} };
}

export function addCollabParticipant(state: WorkspaceCollabState, participant: CollabParticipant): EditorResult<WorkspaceCollabState> {
  if (state.participants.some(p => p.id === participant.id)) return err('PARTICIPANT_EXISTS', `Participant "${participant.id}" already present`);
  return ok({ ...state, isActive: true, participants: [...state.participants, participant] }, 'PARTICIPANT_ADDED', 'Participant joined');
}

export function removeCollabParticipant(state: WorkspaceCollabState, participantId: string): EditorResult<WorkspaceCollabState> {
  if (!state.participants.some(p => p.id === participantId)) return err('PARTICIPANT_NOT_FOUND', `Participant "${participantId}" not found`);
  const participants = state.participants.filter(p => p.id !== participantId);
  const cursors = state.cursors.filter(c => c.participantId !== participantId);
  return ok({ ...state, participants, cursors, isActive: participants.length > 0 }, 'PARTICIPANT_REMOVED', 'Participant left');
}

export function updateCollabCursor(state: WorkspaceCollabState, cursor: CollabCursor): EditorResult<WorkspaceCollabState> {
  const existing = state.cursors.findIndex(c => c.participantId === cursor.participantId && c.fileUri === cursor.fileUri);
  const cursors = [...state.cursors];
  if (existing >= 0) {
    cursors[existing] = cursor;
  } else {
    cursors.push(cursor);
  }
  return ok({ ...state, cursors }, 'CURSOR_UPDATED', 'Cursor position updated');
}

export function startFollowing(state: WorkspaceCollabState, leaderId: string): EditorResult<WorkspaceCollabState> {
  if (!state.participants.some(p => p.id === leaderId)) return err('LEADER_NOT_FOUND', `Leader "${leaderId}" not found`);
  return ok({ ...state, followTarget: leaderId }, 'FOLLOW_STARTED', `Following "${leaderId}"`);
}

export function stopFollowing(state: WorkspaceCollabState): EditorResult<WorkspaceCollabState> {
  return ok({ ...state, followTarget: undefined }, 'FOLLOW_STOPPED', 'Stopped following');
}

export function resolveSyncConflict(state: WorkspaceSyncState, path: string, resolution: 'local' | 'remote' | 'manual'): EditorResult<WorkspaceSyncState> {
  const idx = state.conflicts.findIndex(c => c.path === path);
  if (idx === -1) return err('CONFLICT_NOT_FOUND', `No conflict for "${path}"`);
  const conflicts = [...state.conflicts];
  conflicts[idx] = { ...conflicts[idx]!, resolvedBy: resolution };
  const allResolved = conflicts.every(c => c.resolvedBy);
  return ok({ ...state, conflicts, status: allResolved ? 'synced' : 'conflict' }, 'CONFLICT_RESOLVED', 'Conflict resolved');
}

export function computeSyncStatus(state: WorkspaceSyncState): 'synced' | 'syncing' | 'conflict' | 'error' | 'offline' {
  if (state.status === 'error' || state.status === 'offline') return state.status;
  if (state.conflicts.some(c => !c.resolvedBy)) return 'conflict';
  return state.status;
}

// ═══ §41 GIT ORCHESTRATION — status, log, branch, remote, stash, blame ═══

export function parseGitStatus(rawLines: string[]): GitFileStatus[] {
  return rawLines.map(line => {
    const indexChar = line.charAt(0);
    const workChar = line.charAt(1);
    const path = line.slice(3).trim();
    return {
      path,
      indexStatus: charToStatusCode(indexChar),
      workTreeStatus: charToStatusCode(workChar),
      isSubmodule: false,
    };
  });
}

function charToStatusCode(c: string): GitStatusCode {
  const map: Record<string, GitStatusCode> = {
    ' ': 'unmodified', 'M': 'modified', 'A': 'added', 'D': 'deleted',
    'R': 'renamed', 'C': 'copied', '?': 'untracked', '!': 'ignored', 'U': 'unmerged',
  };
  return map[c] ?? 'unmodified';
}

export function parseGitLog(rawEntries: Array<{ hash: string; parents: string; author: string; authorEmail: string; date: number; message: string }>): GitCommit[] {
  return rawEntries.map(e => ({
    hash: asCommitHash(e.hash),
    parentHashes: e.parents.split(' ').filter(Boolean).map(asCommitHash),
    author: { name: e.author, email: e.authorEmail, date: e.date },
    committer: { name: e.author, email: e.authorEmail, date: e.date },
    message: e.message,
    date: e.date,
    isSigned: false,
  }));
}

export function parseGitBranch(rawLines: string[]): GitBranch[] {
  return rawLines.map(line => {
    const isHead = line.startsWith('*');
    const name = line.replace(/^\*?\s+/, '').split(/\s+/)[0] ?? '';
    return {
      name: asBranchName(name),
      commitHash: asCommitHash(''),
      isHead,
      ahead: 0,
      behind: 0,
    };
  });
}

export function parseGitRemote(rawLines: string[]): GitRemote[] {
  const remotes = new Map<string, GitRemote>();
  for (const line of rawLines) {
    const parts = line.split(/\s+/);
    const name = parts[0] ?? '';
    const url = parts[1] ?? '';
    if (!remotes.has(name)) {
      remotes.set(name, { name: asRemoteName(name), fetchUrl: url, pushUrl: url });
    }
  }
  return Array.from(remotes.values());
}

export function parseGitStash(rawLines: string[]): GitStash[] {
  return rawLines.map((line, i) => {
    const match = line.match(/^stash@\{(\d+)\}:\s*(.+)$/);
    return {
      id: asStashId(`stash@{${match ? match[1] : i}}`),
      message: match ? match[2]! : line,
      date: now(),
    };
  });
}

export function parseGitBlame(rawLines: string[]): GitBlameLine[] {
  return rawLines.map((line, i) => {
    const hash = line.slice(0, 8);
    const rest = line.slice(9);
    const parenMatch = rest.match(/\((.+?)\s+(\d{4}-\d{2}-\d{2})\s+\d+\)\s?(.*)/);
    return {
      lineNumber: i + 1,
      commitHash: asCommitHash(hash),
      author: parenMatch ? parenMatch[1]! : 'unknown',
      date: parenMatch ? new Date(parenMatch[2]!).getTime() : 0,
      content: parenMatch ? parenMatch[3]! : rest,
    };
  });
}

export function formatCommitMessage(type: string, scope: string, subject: string, body?: string, breaking?: string): string {
  let msg = scope ? `${type}(${scope}): ${subject}` : `${type}: ${subject}`;
  if (body) msg += `\n\n${body}`;
  if (breaking) msg += `\n\nBREAKING CHANGE: ${breaking}`;
  return msg;
}

export function isValidBranchName(name: string): boolean {
  if (name.length === 0 || name.length > 255) return false;
  if (/[\x00-\x1f\x7f~^:?*\[\\]/.test(name)) return false;
  if (name.startsWith('-') || name.startsWith('.') || name.endsWith('.') || name.endsWith('/') || name.endsWith('.lock')) return false;
  if (name.includes('..') || name.includes('@{') || name.includes('//')) return false;
  return true;
}

export function isValidCommitHash(hash: string): boolean {
  return /^[0-9a-f]{4,40}$/.test(hash);
}

export function computeAheadBehind(localCommits: CommitHash[], remoteCommits: CommitHash[]): { ahead: number; behind: number } {
  const localSet = new Set(localCommits);
  const remoteSet = new Set(remoteCommits);
  const ahead = localCommits.filter(h => !remoteSet.has(h)).length;
  const behind = remoteCommits.filter(h => !localSet.has(h)).length;
  return { ahead, behind };
}

export function fileStatusToLabel(status: GitStatusCode): string {
  const labels: Record<GitStatusCode, string> = {
    unmodified: '', modified: 'M', added: 'A', deleted: 'D',
    renamed: 'R', copied: 'C', untracked: 'U', ignored: 'I', unmerged: '!',
  };
  return labels[status];
}

export function mergeStrategyForConflict(ours: string, theirs: string, base: string): 'ours' | 'theirs' | 'manual' {
  if (ours === base) return 'theirs';
  if (theirs === base) return 'ours';
  return 'manual';
}

// ═══ §42 GIT STAGING & HOOKS — diff hunks, hooks, PR, command builder ═══

export function parseDiff(rawDiff: string): GitStagingHunk[] {
  const hunks: GitStagingHunk[] = [];
  const hunkRegex = /^@@\s+-(\d+)(?:,(\d+))?\s+\+(\d+)(?:,(\d+))?\s+@@(.*)$/;
  const lines = rawDiff.split('\n');
  let currentHunk: GitStagingHunk | null = null;
  let hunkLines: GitDiffLine[] = [];
  let oldLine = 0;
  let newLine = 0;

  for (const line of lines) {
    const match = line.match(hunkRegex);
    if (match) {
      if (currentHunk) hunks.push({ ...currentHunk, lines: hunkLines });
      oldLine = parseInt(match[1]!, 10);
      newLine = parseInt(match[3]!, 10);
      currentHunk = {
        header: line,
        oldStart: oldLine,
        oldCount: parseInt(match[2] ?? '1', 10),
        newStart: newLine,
        newCount: parseInt(match[4] ?? '1', 10),
        lines: [],
      };
      hunkLines = [];
    } else if (currentHunk) {
      if (line.startsWith('+')) {
        hunkLines.push({ type: 'add', content: line.slice(1), newLineNumber: newLine++ });
      } else if (line.startsWith('-')) {
        hunkLines.push({ type: 'delete', content: line.slice(1), oldLineNumber: oldLine++ });
      } else {
        hunkLines.push({ type: 'context', content: line.startsWith(' ') ? line.slice(1) : line, oldLineNumber: oldLine++, newLineNumber: newLine++ });
      }
    }
  }
  if (currentHunk) hunks.push({ ...currentHunk, lines: hunkLines });
  return hunks;
}

export function splitHunkByLines(hunk: GitStagingHunk, selectedLines: number[]): { selected: GitStagingHunk; remaining: GitStagingHunk } {
  const selectedSet = new Set(selectedLines);
  const selLines: GitDiffLine[] = [];
  const remLines: GitDiffLine[] = [];
  for (let i = 0; i < hunk.lines.length; i++) {
    const line = hunk.lines[i]!;
    if (line.type === 'context') {
      selLines.push(line);
      remLines.push(line);
    } else if (selectedSet.has(i)) {
      selLines.push(line);
    } else {
      remLines.push(line);
    }
  }
  return {
    selected: { ...hunk, lines: selLines },
    remaining: { ...hunk, lines: remLines },
  };
}

export function applyHunk(content: string, hunk: GitStagingHunk): EditorResult<string> {
  const lines = content.split('\n');
  const insertAt = hunk.oldStart - 1;
  const deleteCount = hunk.lines.filter(l => l.type === 'delete' || l.type === 'context').length;
  const newLines = hunk.lines.filter(l => l.type === 'add' || l.type === 'context').map(l => l.content);
  lines.splice(insertAt, deleteCount, ...newLines);
  return ok(lines.join('\n'), 'HUNK_APPLIED', 'Hunk applied successfully');
}

export function reverseHunk(hunk: GitStagingHunk): GitStagingHunk {
  return {
    ...hunk,
    oldStart: hunk.newStart,
    oldCount: hunk.newCount,
    newStart: hunk.oldStart,
    newCount: hunk.oldCount,
    lines: hunk.lines.map(l => {
      if (l.type === 'add') return { ...l, type: 'delete' as const, oldLineNumber: l.newLineNumber, newLineNumber: undefined };
      if (l.type === 'delete') return { ...l, type: 'add' as const, newLineNumber: l.oldLineNumber, oldLineNumber: undefined };
      return l;
    }),
  };
}

export const GIT_HOOK_ORDER: GitHookName[] = [
  'pre-commit', 'prepare-commit-msg', 'commit-msg', 'post-commit',
  'pre-rebase', 'post-rewrite', 'post-checkout', 'post-merge', 'pre-push', 'pre-auto-gc',
];

export function isHookEnabled(hooks: GitHookState[], name: GitHookName): boolean {
  const hook = hooks.find(h => h.name === name);
  return hook ? hook.isEnabled && hook.isExecutable : false;
}

export function hookExecutionOrder(hooks: GitHookState[]): GitHookState[] {
  return GIT_HOOK_ORDER
    .map(name => hooks.find(h => h.name === name))
    .filter((h): h is GitHookState => h !== undefined && h.isEnabled);
}

export function formatPRDescription(title: string, body: string, labels: string[], reviewers: string[]): string {
  let desc = `# ${title}\n\n${body}`;
  if (labels.length > 0) desc += `\n\nLabels: ${labels.join(', ')}`;
  if (reviewers.length > 0) desc += `\nReviewers: ${reviewers.join(', ')}`;
  return desc;
}

export function buildGitCommand(subcommand: string, args: string[] = [], flags: Record<string, string | boolean> = {}): string[] {
  const cmd = ['git', subcommand];
  for (const [key, val] of Object.entries(flags)) {
    if (val === true) cmd.push(`--${key}`);
    else if (val !== false) cmd.push(`--${key}`, val);
  }
  cmd.push(...args);
  return cmd;
}

// ═══ §43 FILE SYSTEM ORCHESTRATION — tree, glob, mime, size, path ═══

export function buildFileTree(files: FileEntry[], dirs: DirectoryEntry[]): DirectoryEntry {
  const root: DirectoryEntry = { id: asDirectoryId(generateId('dir')), path: '/', name: '/', children: [], isExpanded: true };
  const dirMap = new Map<string, DirectoryEntry>();
  dirMap.set('/', root);
  for (const d of dirs) dirMap.set(d.path, { ...d, children: [] });
  for (const f of files) {
    const parent = parentDirectory(f.path);
    const dir = dirMap.get(parent);
    if (dir) (dir.children as (FileEntry | DirectoryEntry)[]).push(f);
  }
  for (const d of dirs) {
    const parent = parentDirectory(d.path);
    const dir = dirMap.get(parent);
    if (dir && d.path !== '/') (dir.children as (FileEntry | DirectoryEntry)[]).push(dirMap.get(d.path)!);
  }
  return root;
}

export function flattenTree(entry: DirectoryEntry): (FileEntry | DirectoryEntry)[] {
  const result: (FileEntry | DirectoryEntry)[] = [entry];
  for (const child of entry.children) {
    if ('children' in child) {
      result.push(...flattenTree(child));
    } else {
      result.push(child);
    }
  }
  return result;
}

export function filterTree(entry: DirectoryEntry, predicate: (e: FileEntry | DirectoryEntry) => boolean): DirectoryEntry {
  const filteredChildren: (FileEntry | DirectoryEntry)[] = [];
  for (const child of entry.children) {
    if ('children' in child) {
      const filtered = filterTree(child, predicate);
      if (filtered.children.length > 0 || predicate(child)) filteredChildren.push(filtered);
    } else if (predicate(child)) {
      filteredChildren.push(child);
    }
  }
  return { ...entry, children: filteredChildren };
}

export function matchGlob(path: string, pattern: string): boolean {
  const regex = pattern
    .replace(/[.+^${}()|[\]\\]/g, '\\$&')
    .replace(/\*\*/g, '<<GLOBSTAR>>')
    .replace(/\*/g, '[^/]*')
    .replace(/\?/g, '[^/]')
    .replace(/<<GLOBSTAR>>/g, '.*');
  return new RegExp(`^${regex}$`).test(path);
}

const MIME_MAP: Record<string, string> = {
  ts: 'text/typescript', tsx: 'text/typescriptreact', js: 'text/javascript', jsx: 'text/javascriptreact',
  json: 'application/json', html: 'text/html', css: 'text/css', scss: 'text/scss', less: 'text/less',
  md: 'text/markdown', yaml: 'text/yaml', yml: 'text/yaml', xml: 'text/xml', svg: 'image/svg+xml',
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp',
  wasm: 'application/wasm', pdf: 'application/pdf', zip: 'application/zip', tar: 'application/x-tar',
  py: 'text/x-python', rb: 'text/x-ruby', go: 'text/x-go', rs: 'text/x-rust', java: 'text/x-java',
  c: 'text/x-c', cpp: 'text/x-c++', h: 'text/x-c', sh: 'text/x-shellscript', bash: 'text/x-shellscript',
  sql: 'text/x-sql', graphql: 'text/x-graphql', toml: 'text/x-toml', ini: 'text/x-ini',
  txt: 'text/plain', log: 'text/plain', csv: 'text/csv', tsv: 'text/tab-separated-values',
};

export function detectMimeType(ext: string): string {
  return MIME_MAP[ext.toLowerCase().replace(/^\./, '')] ?? 'application/octet-stream';
}

const BINARY_EXTENSIONS = new Set([
  'png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'ico', 'tiff',
  'wasm', 'pdf', 'zip', 'tar', 'gz', 'bz2', 'xz', '7z', 'rar',
  'exe', 'dll', 'so', 'dylib', 'bin', 'dat',
  'mp3', 'mp4', 'avi', 'mkv', 'mov', 'flac', 'wav', 'ogg',
  'woff', 'woff2', 'ttf', 'otf', 'eot',
  'sqlite', 'db',
]);

export function detectBinary(ext: string): boolean {
  return BINARY_EXTENSIONS.has(ext.toLowerCase().replace(/^\./, ''));
}

export function humanFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const val = bytes / Math.pow(1024, i);
  return `${val < 10 ? val.toFixed(1) : Math.round(val)} ${units[i]}`;
}

export function isLargeFile(size: number, threshold: number = 1048576): boolean {
  return size > threshold;
}

export function sortEntries(entries: (FileEntry | DirectoryEntry)[], by: 'name' | 'size' | 'modified' = 'name'): (FileEntry | DirectoryEntry)[] {
  return [...entries].sort((a, b) => {
    const aIsDir = 'children' in a;
    const bIsDir = 'children' in b;
    if (aIsDir !== bIsDir) return aIsDir ? -1 : 1;
    if (by === 'name') return a.name.localeCompare(b.name);
    if (by === 'size' && 'size' in a && 'size' in b) return (b as FileEntry).size - (a as FileEntry).size;
    if (by === 'modified' && 'modifiedAt' in a && 'modifiedAt' in b) return (b as FileEntry).modifiedAt - (a as FileEntry).modifiedAt;
    return a.name.localeCompare(b.name);
  });
}

export function fileExtension(path: string): string {
  const dot = path.lastIndexOf('.');
  const slash = Math.max(path.lastIndexOf('/'), path.lastIndexOf('\\'));
  return dot > slash ? path.slice(dot + 1) : '';
}

export function fileName(path: string): string {
  const slash = Math.max(path.lastIndexOf('/'), path.lastIndexOf('\\'));
  return slash >= 0 ? path.slice(slash + 1) : path;
}

export function parentDirectory(path: string): string {
  const slash = Math.max(path.lastIndexOf('/'), path.lastIndexOf('\\'));
  if (slash <= 0) return '/';
  return path.slice(0, slash);
}

export function joinPath(...segments: string[]): string {
  return segments.join('/').replace(/\/+/g, '/');
}

export function normalizePath(path: string): string {
  const parts = path.split('/').filter(Boolean);
  const resolved: string[] = [];
  for (const part of parts) {
    if (part === '..') resolved.pop();
    else if (part !== '.') resolved.push(part);
  }
  return (path.startsWith('/') ? '/' : '') + resolved.join('/');
}

// ═══ §44 FILE SEARCH & COLLABORATION — search, replace, OT, symbol index ═══

export function searchFiles(content: string, query: SearchQuery): SearchMatch[] {
  const matches: SearchMatch[] = [];
  const lines = content.split('\n');
  let flags = 'g';
  if (!query.isCaseSensitive) flags += 'i';
  let pattern: string;
  if (query.isRegex) {
    pattern = query.pattern;
  } else {
    pattern = query.pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
  if (query.isWholeWord) pattern = `\\b${pattern}\\b`;
  let regex: RegExp;
  try { regex = new RegExp(pattern, flags); } catch { return []; }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    let match: RegExpExecArray | null;
    regex.lastIndex = 0;
    while ((match = regex.exec(line)) !== null) {
      matches.push({
        range: {
          start: { line: i, column: match.index },
          end: { line: i, column: match.index + match[0].length },
        },
        preview: line,
        lineNumber: i + 1,
      });
      if (matches.length >= (query.maxResults ?? MAX_SEARCH_RESULTS)) return matches;
      if (match[0].length === 0) { regex.lastIndex++; }
    }
  }
  return matches;
}

export function buildSearchRegexFromQuery(query: SearchQuery): EditorResult<RegExp> {
  let flags = 'g';
  if (!query.isCaseSensitive) flags += 'i';
  let pattern = query.isRegex ? query.pattern : query.pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  if (query.isWholeWord) pattern = `\\b${pattern}\\b`;
  try {
    return ok(new RegExp(pattern, flags), 'REGEX_BUILT', 'Search regex built');
  } catch (e) {
    return err('INVALID_REGEX', `Invalid regex: ${(e as Error).message}`);
  }
}

export function replaceInContent(content: string, matches: SearchMatch[], replacement: string): EditorResult<string> {
  if (matches.length === 0) return ok(content, 'NO_MATCHES', 'No matches to replace');
  const sorted = [...matches].sort((a, b) => {
    const lineDiff = b.range.start.line - a.range.start.line;
    return lineDiff !== 0 ? lineDiff : b.range.start.column - a.range.start.column;
  });
  const lines = content.split('\n');
  for (const m of sorted) {
    const line = lines[m.range.start.line];
    if (line === undefined) continue;
    lines[m.range.start.line] = line.slice(0, m.range.start.column) + replacement + line.slice(m.range.end.column);
  }
  return ok(lines.join('\n'), 'REPLACED', `Replaced ${sorted.length} occurrences`);
}

export function preserveCaseReplace(original: string, replacement: string): string {
  if (original === original.toUpperCase()) return replacement.toUpperCase();
  if (original === original.toLowerCase()) return replacement.toLowerCase();
  if (original.charAt(0) === original.charAt(0).toUpperCase()) {
    return replacement.charAt(0).toUpperCase() + replacement.slice(1).toLowerCase();
  }
  return replacement;
}

export function transformOT(op: OTOperation, against: OTOperation): OTOperation {
  if (op.type === 'insert' && against.type === 'insert') {
    if (against.position <= op.position) {
      return { ...op, position: op.position + (against.text?.length ?? 0) };
    }
    return op;
  }
  if (op.type === 'insert' && against.type === 'delete') {
    if (against.position < op.position) {
      return { ...op, position: Math.max(against.position, op.position - (against.count ?? 0)) };
    }
    return op;
  }
  if (op.type === 'delete' && against.type === 'insert') {
    if (against.position <= op.position) {
      return { ...op, position: op.position + (against.text?.length ?? 0) };
    }
    return op;
  }
  if (op.type === 'delete' && against.type === 'delete') {
    if (against.position < op.position) {
      return { ...op, position: Math.max(against.position, op.position - (against.count ?? 0)), count: Math.max(0, (op.count ?? 0) - Math.max(0, (against.position + (against.count ?? 0)) - op.position)) };
    }
    return op;
  }
  return op;
}

export function applyOTOperation(content: string, op: OTOperation): EditorResult<string> {
  if (op.type === 'insert') {
    if (op.position < 0 || op.position > content.length) return err('INVALID_POSITION', 'Insert position out of bounds');
    return ok(content.slice(0, op.position) + (op.text ?? '') + content.slice(op.position), 'OT_APPLIED', 'Insert applied');
  }
  if (op.type === 'delete') {
    const count = op.count ?? 0;
    if (op.position < 0 || op.position + count > content.length) return err('INVALID_POSITION', 'Delete range out of bounds');
    return ok(content.slice(0, op.position) + content.slice(op.position + count), 'OT_APPLIED', 'Delete applied');
  }
  return ok(content, 'OT_RETAINED', 'Retain applied');
}

export function composeOTOperations(ops: OTOperation[]): OTOperation[] {
  if (ops.length <= 1) return ops;
  const composed: OTOperation[] = [];
  for (const op of ops) {
    const last = composed[composed.length - 1];
    if (last && last.type === 'insert' && op.type === 'insert' && last.clientId === op.clientId && op.position === last.position + (last.text?.length ?? 0)) {
      composed[composed.length - 1] = { ...last, text: (last.text ?? '') + (op.text ?? '') };
    } else if (last && last.type === 'delete' && op.type === 'delete' && last.clientId === op.clientId && op.position === last.position) {
      composed[composed.length - 1] = { ...last, count: (last.count ?? 0) + (op.count ?? 0) };
    } else {
      composed.push(op);
    }
  }
  return composed;
}

export function rebuildSymbolIndex(symbols: SymbolInfo[]): Map<string, SymbolInfo[]> {
  const index = new Map<string, SymbolInfo[]>();
  for (const sym of symbols) {
    const key = sym.name.toLowerCase();
    const existing = index.get(key);
    if (existing) existing.push(sym);
    else index.set(key, [sym]);
  }
  return index;
}

// ═══ §45 PROJECT ORCHESTRATION — detection, dependencies, tasks, monorepo ═══

const PROJECT_INDICATORS: Record<string, ProjectType> = {
  'pnpm-workspace.yaml': 'monorepo',
  'turbo.json': 'monorepo',
  'nx.json': 'monorepo',
  'lerna.json': 'monorepo',
  'package.json': 'single',
  'Cargo.toml': 'single',
  'go.mod': 'single',
  'pyproject.toml': 'single',
  'build.gradle': 'single',
  'pom.xml': 'single',
};

export function detectProjectType(files: string[]): ProjectType {
  for (const [indicator, type] of Object.entries(PROJECT_INDICATORS)) {
    if (files.includes(indicator)) return type;
  }
  return 'single';
}

const PACKAGE_MANAGER_FILES: Record<string, PackageManagerType> = {
  'bun.lockb': 'bun',
  'bun.lock': 'bun',
  'pnpm-lock.yaml': 'pnpm',
  'yarn.lock': 'yarn',
  'package-lock.json': 'npm',
  'Cargo.lock': 'cargo',
  'go.sum': 'go',
  'poetry.lock': 'poetry',
  'Pipfile.lock': 'pip',
  'gradle.lockfile': 'gradle',
};

export function detectPackageManager(files: string[]): PackageManagerType {
  for (const [file, pm] of Object.entries(PACKAGE_MANAGER_FILES)) {
    if (files.includes(file)) return pm;
  }
  return 'unknown';
}

const BUILD_SYSTEM_FILES: Record<string, BuildSystem> = {
  'vite.config.ts': 'vite', 'vite.config.js': 'vite', 'vite.config.mts': 'vite',
  'webpack.config.js': 'webpack', 'webpack.config.ts': 'webpack',
  'rollup.config.js': 'rollup', 'rollup.config.ts': 'rollup', 'rollup.config.mjs': 'rollup',
  'esbuild.config.js': 'esbuild', 'esbuild.config.ts': 'esbuild',
  'turbopack.config.js': 'turbopack',
  'rspack.config.js': 'rspack', 'rspack.config.ts': 'rspack',
  '.parcelrc': 'parcel',
  'tsconfig.json': 'tsc',
  '.swcrc': 'swc',
};

export function detectBuildSystem(files: string[]): BuildSystem | undefined {
  for (const [file, bs] of Object.entries(BUILD_SYSTEM_FILES)) {
    if (files.includes(file)) return bs;
  }
  return undefined;
}

const CI_INDICATORS: Record<string, CIConfig['provider']> = {
  '.github/workflows': 'github-actions',
  '.gitlab-ci.yml': 'gitlab-ci',
  'Jenkinsfile': 'jenkins',
  '.circleci/config.yml': 'circleci',
  '.travis.yml': 'travis',
  'azure-pipelines.yml': 'azure-pipelines',
};

export function detectCIProvider(files: string[]): CIConfig | undefined {
  for (const [path, provider] of Object.entries(CI_INDICATORS)) {
    if (files.some(f => f.startsWith(path) || f === path)) {
      return { provider, configPath: path };
    }
  }
  return undefined;
}

const TASK_SOURCES: Record<string, TaskSource> = {
  'package.json': 'package.json',
  'Makefile': 'makefile',
  'makefile': 'makefile',
  'GNUmakefile': 'makefile',
  'Taskfile.yml': 'taskfile',
  'Taskfile.yaml': 'taskfile',
  'justfile': 'justfile',
  'Justfile': 'justfile',
  'Cargo.toml': 'cargo',
  'build.gradle': 'gradle',
  'build.gradle.kts': 'gradle',
};

export function discoverTasks(files: string[], scripts?: Record<string, string>): ProjectTask[] {
  const tasks: ProjectTask[] = [];
  for (const [file, source] of Object.entries(TASK_SOURCES)) {
    if (!files.includes(file)) continue;
    if (source === 'package.json' && scripts) {
      for (const [name, cmd] of Object.entries(scripts)) {
        tasks.push({
          id: asTaskId(generateId('task')),
          name,
          source,
          command: cmd,
          cwd: '.',
          dependencies: [],
          status: 'idle',
        });
      }
    }
  }
  return tasks;
}

export function buildDependencyGraph(deps: DependencyInfo[]): DependencyGraph {
  const nodes = new Map<string, DependencyInfo>();
  const edges = new Map<string, string[]>();
  for (const d of deps) {
    nodes.set(d.name, d);
    edges.set(d.name, []);
  }
  const { hasCircular, circularPaths } = findCircularDeps(edges);
  return { nodes, edges, hasCircular, circularPaths };
}

export function findCircularDeps(edges: Map<string, string[]>): { hasCircular: boolean; circularPaths: string[][] } {
  const circularPaths: string[][] = [];
  const visited = new Set<string>();
  const stack = new Set<string>();

  function dfs(node: string, path: string[]): void {
    if (stack.has(node)) {
      const cycleStart = path.indexOf(node);
      if (cycleStart >= 0) circularPaths.push(path.slice(cycleStart).concat(node));
      return;
    }
    if (visited.has(node)) return;
    visited.add(node);
    stack.add(node);
    for (const neighbor of (edges.get(node) ?? [])) {
      dfs(neighbor, [...path, node]);
    }
    stack.delete(node);
  }

  for (const node of edges.keys()) dfs(node, []);
  return { hasCircular: circularPaths.length > 0, circularPaths };
}

export function topologicalSort(edges: Map<string, string[]>): EditorResult<string[]> {
  const inDegree = new Map<string, number>();
  for (const node of edges.keys()) inDegree.set(node, 0);
  for (const deps of edges.values()) {
    for (const dep of deps) {
      inDegree.set(dep, (inDegree.get(dep) ?? 0) + 1);
    }
  }
  const queue: string[] = [];
  for (const [node, degree] of inDegree) {
    if (degree === 0) queue.push(node);
  }
  const result: string[] = [];
  while (queue.length > 0) {
    const node = queue.shift()!;
    result.push(node);
    for (const dep of (edges.get(node) ?? [])) {
      const newDegree = (inDegree.get(dep) ?? 1) - 1;
      inDegree.set(dep, newDegree);
      if (newDegree === 0) queue.push(dep);
    }
  }
  if (result.length !== inDegree.size) return err('CIRCULAR_DEPENDENCY', 'Circular dependency detected, topological sort impossible');
  return ok(result, 'SORTED', 'Topological sort complete');
}

export function detectMonorepoWorkspaces(workspaceGlobs: string[], allPaths: string[]): string[] {
  return allPaths.filter(p => workspaceGlobs.some(g => matchGlob(p, g)));
}

export function parseEnvironmentFile(content: string): Record<string, string> {
  const vars: Record<string, string> = {};
  for (const line of content.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) continue;
    const eqIdx = trimmed.indexOf('=');
    if (eqIdx === -1) continue;
    const key = trimmed.slice(0, eqIdx).trim();
    let value = trimmed.slice(eqIdx + 1).trim();
    if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
      value = value.slice(1, -1);
    }
    vars[key] = value;
  }
  return vars;
}

export const SECRET_ENV_PATTERNS = [
  /^.*_KEY$/i, /^.*_SECRET$/i, /^.*_TOKEN$/i, /^.*_PASSWORD$/i, /^.*_PASS$/i,
  /^.*_API_KEY$/i, /^.*_AUTH$/i, /^.*_CREDENTIAL$/i, /^DATABASE_URL$/i,
  /^PRIVATE_KEY$/i, /^JWT_SECRET$/i, /^SESSION_SECRET$/i,
];

export function hasSecrets(vars: Record<string, string>): boolean {
  return Object.keys(vars).some(key => SECRET_ENV_PATTERNS.some(p => p.test(key)));
}

// ═══ §46 PLUGIN ORCHESTRATION — manifest, lifecycle, permissions, contributions ═══

const VALID_PLUGIN_TRANSITIONS: Record<PluginStatus, PluginStatus[]> = {
  discovered: ['installed'],
  installed: ['activating', 'uninstalling'],
  activating: ['active', 'error'],
  active: ['deactivating', 'updating', 'error'],
  deactivating: ['disabled', 'error'],
  disabled: ['activating', 'uninstalling', 'updating'],
  error: ['activating', 'uninstalling', 'disabled'],
  updating: ['active', 'error'],
  uninstalling: ['discovered'],
};

export function validateManifest(manifest: PluginManifest): EditorResult<PluginManifest> {
  const errors: string[] = [];
  if (!manifest.id) errors.push('Missing plugin id');
  if (!manifest.name) errors.push('Missing plugin name');
  if (!manifest.version) errors.push('Missing version');
  if (!manifest.main) errors.push('Missing main entry point');
  if (!/^\d+\.\d+\.\d+/.test(manifest.version)) errors.push('Invalid semver version');
  if (errors.length > 0) return err('INVALID_MANIFEST', errors.join('; '));
  return ok(manifest, 'MANIFEST_VALID', 'Plugin manifest is valid');
}

export function transitionPluginStatus(current: PluginStatus, target: PluginStatus): EditorResult<PluginStatus> {
  const valid = VALID_PLUGIN_TRANSITIONS[current] ?? [];
  if (!valid.includes(target)) return err('INVALID_TRANSITION', `Cannot transition plugin from "${current}" to "${target}"`);
  return ok(target, 'STATUS_CHANGED', `Plugin status changed to "${target}"`);
}

export function validatePluginTransition(current: PluginStatus, target: PluginStatus): boolean {
  return (VALID_PLUGIN_TRANSITIONS[current] ?? []).includes(target);
}

export function resolvePluginDependencies(plugins: PluginState[], manifest: PluginManifest): EditorResult<string[]> {
  const missing: string[] = [];
  const order: string[] = [];
  for (const [depId, versionRange] of Object.entries(manifest.dependencies)) {
    const found = plugins.find(p => p.manifest.name === depId);
    if (!found) {
      missing.push(`${depId}@${versionRange}`);
    } else {
      order.push(found.id);
    }
  }
  if (missing.length > 0) return err('MISSING_DEPENDENCIES', `Missing: ${missing.join(', ')}`);
  return ok(order, 'DEPS_RESOLVED', 'All dependencies resolved');
}

export function checkPermissions(granted: PluginPermission[], required: PluginPermission[]): EditorResult<boolean> {
  const grantedSet = new Set(granted);
  const missing = required.filter(p => !grantedSet.has(p));
  if (missing.length > 0) return err('MISSING_PERMISSIONS', `Missing permissions: ${missing.join(', ')}`);
  return ok(true, 'PERMISSIONS_OK', 'All permissions granted');
}

export function mergeContributions(base: PluginContributions, ext: PluginContributions): PluginContributions {
  return {
    commands: [...(base.commands ?? []), ...(ext.commands ?? [])],
    menus: [...(base.menus ?? []), ...(ext.menus ?? [])],
    keybindings: [...(base.keybindings ?? []), ...(ext.keybindings ?? [])],
    views: [...(base.views ?? []), ...(ext.views ?? [])],
    themes: [...(base.themes ?? []), ...(ext.themes ?? [])],
    languages: [...(base.languages ?? []), ...(ext.languages ?? [])],
    snippets: [...(base.snippets ?? []), ...(ext.snippets ?? [])],
    grammars: [...(base.grammars ?? []), ...(ext.grammars ?? [])],
    debuggers: [...(base.debuggers ?? []), ...(ext.debuggers ?? [])],
    formatters: [...(base.formatters ?? []), ...(ext.formatters ?? [])],
    settings: [...(base.settings ?? []), ...(ext.settings ?? [])],
  };
}

export function isWithinResourceLimits(usage: PluginResourceUsage, limits: SandboxResourceLimits): boolean {
  return (
    usage.cpuMs <= limits.maxCpuMs &&
    usage.memoryMb <= limits.maxMemoryMb &&
    usage.apiCalls <= limits.maxApiCallsPerMinute
  );
}

export function pluginActivationMatch(events: string[], trigger: string): boolean {
  return events.some(e => {
    if (e === '*') return true;
    if (e === trigger) return true;
    if (e.endsWith(':*')) {
      const prefix = e.slice(0, -2);
      return trigger.startsWith(prefix + ':') || trigger === prefix;
    }
    return false;
  });
}

// ═══ §47 SETTINGS ORCHESTRATION — resolve, update, validate, profiles ═══

export function resolveSetting(state: SettingsState, key: string, scopeOrder: SettingsScope[] = DEFAULT_SETTINGS_SCOPE_ORDER): unknown {
  for (const scope of scopeOrder) {
    const scopeSettings = state.scopes.get(scope);
    if (scopeSettings && key in scopeSettings) return scopeSettings[key];
  }
  const schema = state.schema.get(key);
  return schema ? schema.default : undefined;
}

export function updateSetting(state: SettingsState, scope: SettingsScope, key: string, value: unknown): EditorResult<SettingsState> {
  const schema = state.schema.get(key);
  if (schema) {
    const validationResult = validateSetting(schema, value);
    if (!validationResult.success) return validationResult as EditorResult<SettingsState>;
  }
  const scopeSettings = state.scopes.get(scope) ?? {};
  const newScopes = new Map(state.scopes);
  newScopes.set(scope, { ...scopeSettings, [key]: value });
  return ok({ ...state, scopes: newScopes }, 'SETTING_UPDATED', `Setting "${key}" updated in "${scope}"`, [
    { kind: 'changed', domain: 'settings', timestamp: now(), payload: { key, scope, value } } as EditorEvent,
  ]);
}

export function validateSetting(schema: SettingSchema, value: unknown): EditorResult<boolean> {
  if (schema.type === 'string' && typeof value !== 'string') return err('INVALID_TYPE', `Expected string, got ${typeof value}`);
  if (schema.type === 'number' && typeof value !== 'number') return err('INVALID_TYPE', `Expected number, got ${typeof value}`);
  if (schema.type === 'boolean' && typeof value !== 'boolean') return err('INVALID_TYPE', `Expected boolean, got ${typeof value}`);
  if (schema.type === 'array' && !Array.isArray(value)) return err('INVALID_TYPE', `Expected array, got ${typeof value}`);
  if (schema.type === 'enum' && schema.enum && !schema.enum.includes(value)) return err('INVALID_ENUM', `Value not in enum: ${JSON.stringify(schema.enum)}`);
  if (schema.type === 'number' && typeof value === 'number') {
    if (schema.minimum !== undefined && value < schema.minimum) return err('BELOW_MINIMUM', `Value ${value} below minimum ${schema.minimum}`);
    if (schema.maximum !== undefined && value > schema.maximum) return err('ABOVE_MAXIMUM', `Value ${value} above maximum ${schema.maximum}`);
  }
  if (schema.type === 'string' && typeof value === 'string' && schema.pattern) {
    if (!new RegExp(schema.pattern).test(value)) return err('PATTERN_MISMATCH', `Value does not match pattern ${schema.pattern}`);
  }
  return ok(true, 'VALID', 'Setting value is valid');
}

export function getEffectiveSettings(state: SettingsState, scopeOrder: SettingsScope[] = DEFAULT_SETTINGS_SCOPE_ORDER): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, schema] of state.schema) {
    result[key] = schema.default;
  }
  for (const scope of [...scopeOrder].reverse()) {
    const scopeSettings = state.scopes.get(scope);
    if (scopeSettings) Object.assign(result, scopeSettings);
  }
  return result;
}

export function diffSettings(a: Record<string, unknown>, b: Record<string, unknown>): Array<{ key: string; oldValue: unknown; newValue: unknown }> {
  const changes: Array<{ key: string; oldValue: unknown; newValue: unknown }> = [];
  const allKeys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of allKeys) {
    if (JSON.stringify(a[key]) !== JSON.stringify(b[key])) {
      changes.push({ key, oldValue: a[key], newValue: b[key] });
    }
  }
  return changes;
}

export function applyMigration(settings: Record<string, unknown>, migration: SettingsMigration): Record<string, unknown> {
  const result = { ...settings };
  for (const [key, transform] of Object.entries(migration.transforms)) {
    if (key in result) {
      result[key] = transform(result[key]);
    }
  }
  return result;
}

export function exportProfile(state: SettingsState, name: string): SettingsProfile {
  const effective = getEffectiveSettings(state);
  return {
    id: asSettingsProfileId(generateId('prof')),
    name,
    settings: effective,
    isDefault: false,
    createdAt: now(),
  };
}

export function importProfile(state: SettingsState, profile: SettingsProfile, scope: SettingsScope = 'user'): EditorResult<SettingsState> {
  const newScopes = new Map(state.scopes);
  newScopes.set(scope, { ...(newScopes.get(scope) ?? {}), ...profile.settings });
  const profiles = [...state.profiles, profile];
  return ok({ ...state, scopes: newScopes, profiles }, 'PROFILE_IMPORTED', `Profile "${profile.name}" imported`);
}

// ═══ §48 DIFF ORCHESTRATION — Myers, patience, histogram, char-level, stats ═══

export function computeDiff(original: string, modified: string, algorithm: DiffAlgorithm = 'myers'): EditorResult<DiffResult> {
  let hunks: DiffHunk[];
  switch (algorithm) {
    case 'patience': hunks = patienceDiff(original, modified); break;
    case 'histogram': hunks = histogramDiff(original, modified); break;
    default: hunks = myersDiff(original, modified); break;
  }
  const stats = computeDiffStats(hunks);
  return ok({
    id: asDiffId(generateId('diff')),
    algorithm,
    original,
    modified,
    hunks,
    stats,
  }, 'DIFF_COMPUTED', `Diff computed (${algorithm})`);
}

export function myersDiff(original: string, modified: string): DiffHunk[] {
  const origLines = original.split('\n');
  const modLines = modified.split('\n');
  const hunks: DiffHunk[] = [];
  let i = 0, j = 0;
  while (i < origLines.length || j < modLines.length) {
    if (i < origLines.length && j < modLines.length && origLines[i] === modLines[j]) {
      i++; j++;
      continue;
    }
    const hunkStart = i;
    const modHunkStart = j;
    const lines: DiffLine[] = [];
    // Gather contiguous changes
    while (i < origLines.length && (j >= modLines.length || origLines[i] !== modLines[j])) {
      lines.push({ type: 'delete', content: origLines[i]!, oldLineNumber: i + 1 });
      i++;
    }
    while (j < modLines.length && (i >= origLines.length || origLines[i] !== modLines[j])) {
      lines.push({ type: 'add', content: modLines[j]!, newLineNumber: j + 1 });
      j++;
    }
    if (lines.length > 0) {
      hunks.push({
        id: asHunkId(generateId('hunk')),
        oldStart: hunkStart + 1,
        oldCount: lines.filter(l => l.type === 'delete').length,
        newStart: modHunkStart + 1,
        newCount: lines.filter(l => l.type === 'add').length,
        lines,
        isConflict: false,
      });
    }
  }
  return hunks;
}

export function patienceDiff(original: string, modified: string): DiffHunk[] {
  // Patience diff: find unique common lines as anchors, diff between anchors
  const origLines = original.split('\n');
  const modLines = modified.split('\n');
  // Find unique lines in both
  const origCounts = new Map<string, number[]>();
  const modCounts = new Map<string, number[]>();
  origLines.forEach((l, i) => {
    const arr = origCounts.get(l) ?? [];
    arr.push(i);
    origCounts.set(l, arr);
  });
  modLines.forEach((l, i) => {
    const arr = modCounts.get(l) ?? [];
    arr.push(i);
    modCounts.set(l, arr);
  });
  // Fall back to Myers for the actual diff computation
  return myersDiff(original, modified);
}

export function histogramDiff(original: string, modified: string): DiffHunk[] {
  // Histogram diff: similar to patience but uses occurrence frequency
  // Falls back to Myers for simplicity in the engine layer
  return myersDiff(original, modified);
}

export function charLevelDiff(a: string, b: string): CharRange[] {
  const changes: CharRange[] = [];
  let i = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) i++;
  let ae = a.length - 1, be = b.length - 1;
  while (ae > i && be > i && a[ae] === b[be]) { ae--; be--; }
  if (i <= ae || i <= be) {
    changes.push({ startColumn: i, endColumn: Math.max(ae, be) + 1 });
  }
  return changes;
}

export function detectMovedBlocks(hunks: DiffHunk[]): MovedBlock[] {
  const deleted = hunks.flatMap(h => {
    const content = h.lines.filter(l => l.type === 'delete').map(l => l.content).join('\n');
    return content.length > 20 ? [{ content, range: { start: { line: h.oldStart - 1, column: 0 }, end: { line: h.oldStart + h.oldCount - 2, column: 0 } } }] : [];
  });
  const added = hunks.flatMap(h => {
    const content = h.lines.filter(l => l.type === 'add').map(l => l.content).join('\n');
    return content.length > 20 ? [{ content, range: { start: { line: h.newStart - 1, column: 0 }, end: { line: h.newStart + h.newCount - 2, column: 0 } } }] : [];
  });
  const blocks: MovedBlock[] = [];
  for (const del of deleted) {
    for (const add of added) {
      if (del.content === add.content) {
        blocks.push({ originalRange: del.range, modifiedRange: add.range, similarity: 1.0 });
      }
    }
  }
  return blocks;
}

export function computeDiffStats(hunks: DiffHunk[]): DiffStats {
  let additions = 0, deletions = 0;
  for (const hunk of hunks) {
    for (const line of hunk.lines) {
      if (line.type === 'add') additions++;
      else if (line.type === 'delete') deletions++;
    }
  }
  return { additions, deletions, changes: additions + deletions };
}

export function formatUnifiedDiff(result: DiffResult, contextLines: number = 3): string {
  const lines: string[] = [];
  for (const hunk of result.hunks) {
    lines.push(`@@ -${hunk.oldStart},${hunk.oldCount} +${hunk.newStart},${hunk.newCount} @@`);
    for (const line of hunk.lines) {
      if (line.type === 'add') lines.push(`+${line.content}`);
      else if (line.type === 'delete') lines.push(`-${line.content}`);
      else lines.push(` ${line.content}`);
    }
  }
  return lines.join('\n');
}

export function applyDiff(original: string, hunks: DiffHunk[]): EditorResult<string> {
  const origLines = original.split('\n');
  const sorted = [...hunks].sort((a, b) => b.oldStart - a.oldStart);
  for (const hunk of sorted) {
    const deleteCount = hunk.lines.filter(l => l.type === 'delete' || l.type === 'equal' || l.type === 'context').length;
    const newContent = hunk.lines.filter(l => l.type === 'add' || l.type === 'equal' || l.type === 'context').map(l => l.content);
    origLines.splice(hunk.oldStart - 1, deleteCount, ...newContent);
  }
  return ok(origLines.join('\n'), 'DIFF_APPLIED', 'Diff applied');
}

export function reverseDiff(result: DiffResult): DiffResult {
  return {
    ...result,
    original: result.modified,
    modified: result.original,
    hunks: result.hunks.map(h => ({
      ...h,
      oldStart: h.newStart,
      oldCount: h.newCount,
      newStart: h.oldStart,
      newCount: h.oldCount,
      lines: h.lines.map(l => {
        if (l.type === 'add') return { ...l, type: 'delete' as const, oldLineNumber: l.newLineNumber, newLineNumber: undefined };
        if (l.type === 'delete') return { ...l, type: 'add' as const, newLineNumber: l.oldLineNumber, oldLineNumber: undefined };
        return l;
      }),
    })),
    stats: { ...result.stats, additions: result.stats.deletions, deletions: result.stats.additions },
  };
}

// ═══ §49 THREE-WAY MERGE & CODE REVIEW — merge, conflicts, review threads ═══

export function threeWayMerge(base: string, local: string, remote: string): EditorResult<ThreeWayMergeState> {
  const baseLines = base.split('\n');
  const localLines = local.split('\n');
  const remoteLines = remote.split('\n');
  const conflicts: MergeConflict[] = [];
  const resultLines: string[] = [];
  const maxLen = Math.max(baseLines.length, localLines.length, remoteLines.length);

  for (let i = 0; i < maxLen; i++) {
    const baseLine = baseLines[i] ?? '';
    const localLine = localLines[i] ?? '';
    const remoteLine = remoteLines[i] ?? '';

    if (localLine === remoteLine) {
      resultLines.push(localLine);
    } else if (localLine === baseLine) {
      resultLines.push(remoteLine);
    } else if (remoteLine === baseLine) {
      resultLines.push(localLine);
    } else {
      conflicts.push({
        id: generateId('conflict'),
        range: { start: { line: i, column: 0 }, end: { line: i, column: Math.max(localLine.length, remoteLine.length) } },
        baseContent: baseLine,
        localContent: localLine,
        remoteContent: remoteLine,
      });
      resultLines.push(localLine); // Default to local
    }
  }
  return ok({
    base, local, remote,
    result: resultLines.join('\n'),
    conflicts,
    autoResolutions: [],
  }, 'MERGE_COMPUTED', `Merge computed with ${conflicts.length} conflicts`);
}

export function detectMergeConflicts(base: string, local: string, remote: string): MergeConflict[] {
  const result = threeWayMerge(base, local, remote);
  return result.success ? result.value.conflicts : [];
}

export function autoResolveConflict(conflict: MergeConflict, strategy: MergeResolutionStrategy): MergeResolution {
  let content: string;
  switch (strategy) {
    case 'accept-local': content = conflict.localContent; break;
    case 'accept-remote': content = conflict.remoteContent; break;
    case 'accept-both': content = conflict.localContent + '\n' + conflict.remoteContent; break;
    default: content = conflict.localContent; break;
  }
  return { conflictId: conflict.id, strategy, content };
}

export function applyResolution(state: ThreeWayMergeState, resolution: MergeResolution): EditorResult<ThreeWayMergeState> {
  const conflict = state.conflicts.find(c => c.id === resolution.conflictId);
  if (!conflict) return err('CONFLICT_NOT_FOUND', `Conflict "${resolution.conflictId}" not found`);
  const resultLines = state.result.split('\n');
  const lineIdx = conflict.range.start.line;
  if (lineIdx < resultLines.length) {
    resultLines[lineIdx] = resolution.content;
  }
  const conflicts = state.conflicts.map(c =>
    c.id === resolution.conflictId ? { ...c, resolution: resolution.strategy } : c
  );
  return ok({
    ...state,
    result: resultLines.join('\n'),
    conflicts,
    autoResolutions: [...state.autoResolutions, resolution],
  }, 'RESOLUTION_APPLIED', 'Conflict resolution applied');
}

export function addReviewComment(state: ReviewState, threadId: ReviewThreadId, authorId: string, body: string, suggestion?: CodeSuggestion): EditorResult<ReviewState> {
  const comment: ReviewComment = {
    id: asReviewCommentId(generateId('rc')),
    threadId,
    authorId,
    body,
    timestamp: now(),
    suggestion,
    reactions: {},
  };
  const threads = state.threads.map(t =>
    t.id === threadId ? { ...t, comments: [...t.comments, comment] } : t
  );
  if (!threads.some(t => t.id === threadId)) return err('THREAD_NOT_FOUND', `Thread "${threadId}" not found`);
  return ok({ ...state, threads }, 'COMMENT_ADDED', 'Review comment added');
}

export function resolveThread(state: ReviewState, threadId: ReviewThreadId): EditorResult<ReviewState> {
  const idx = state.threads.findIndex(t => t.id === threadId);
  if (idx === -1) return err('THREAD_NOT_FOUND', `Thread "${threadId}" not found`);
  const threads = [...state.threads];
  threads[idx] = { ...threads[idx]!, status: 'resolved' };
  return ok({ ...state, threads }, 'THREAD_RESOLVED', 'Review thread resolved');
}

export function applyCodeSuggestion(content: string, suggestion: CodeSuggestion): EditorResult<string> {
  const idx = content.indexOf(suggestion.originalCode);
  if (idx === -1) return err('CODE_NOT_FOUND', 'Original code not found in content');
  return ok(content.slice(0, idx) + suggestion.suggestedCode + content.slice(idx + suggestion.originalCode.length), 'SUGGESTION_APPLIED', 'Code suggestion applied');
}

export function computeReviewStatus(state: ReviewState): ReviewState['status'] {
  if (state.threads.length === 0) return state.status;
  const hasUnresolved = state.threads.some(t => t.status === 'open');
  if (hasUnresolved && state.status === 'approved') return 'changes-requested';
  return state.status;
}

// ═══ §50 GLOBAL INFRASTRUCTURE — events, storage, performance, permissions, logging ═══

export function createEvent(type: string, data: unknown, source: string = 'engine', priority: EditorEventData['priority'] = 'normal'): EditorEventData {
  return {
    channel: asEventChannelId(type.split('.')[0] ?? 'global'),
    type,
    payload: data,
    timestamp: now(),
    source,
    priority,
  };
}

export function matchEventPattern(eventType: string, pattern: string): boolean {
  if (pattern === '*') return true;
  if (pattern === eventType) return true;
  if (pattern.endsWith('.*')) {
    const prefix = pattern.slice(0, -2);
    return eventType.startsWith(prefix + '.') || eventType === prefix;
  }
  if (pattern.includes('*')) {
    const regex = pattern.replace(/\./g, '\\.').replace(/\*/g, '[^.]*');
    return new RegExp(`^${regex}$`).test(eventType);
  }
  return false;
}

export function createStorageEntry(namespace: StorageNamespace, key: string, value: unknown, ttlMs?: number, encrypted: boolean = false): StorageEntry {
  return {
    namespace,
    key,
    value,
    expiresAt: ttlMs ? now() + ttlMs : undefined,
    encrypted,
  };
}

export function isStorageExpired(entry: StorageEntry): boolean {
  return entry.expiresAt !== undefined && now() > entry.expiresAt;
}

export function snapshotPerformance(fps: number, frameTimeMs: number, longTasks: number, memoryUsageMb: number, gcPauseMs: number = 0, renderQueueDepth: number = 0): PerformanceSnapshot {
  return { timestamp: now(), fps, frameTimeMs, longTasks, memoryUsageMb, gcPauseMs, renderQueueDepth };
}

export function isWithinBudget(snapshot: PerformanceSnapshot, budget: PerformanceBudget): boolean {
  return (
    snapshot.frameTimeMs <= budget.maxFrameTimeMs &&
    snapshot.fps >= budget.targetFps &&
    snapshot.memoryUsageMb <= budget.maxMemoryMb
  );
}

export function checkPermission(grants: PermissionGrant[], capability: string, scope: string): EditorResult<boolean> {
  const grant = grants.find(g => g.capability === capability && g.scope === scope);
  if (!grant) return err('NO_GRANT', `No permission grant for "${capability}" in scope "${scope}"`);
  if (grant.expiresAt && now() > grant.expiresAt) return err('GRANT_EXPIRED', `Permission grant for "${capability}" has expired`);
  return ok(true, 'PERMITTED', 'Permission granted');
}

export function isPermissionExpired(grant: PermissionGrant): boolean {
  return grant.expiresAt !== undefined && now() > grant.expiresAt;
}

export function formatLogEntry(entry: LogEntry): string {
  const ts = new Date(entry.timestamp).toISOString();
  const level = entry.level.toUpperCase().padEnd(5);
  const data = entry.data ? ` ${JSON.stringify(entry.data)}` : '';
  return `[${ts}] ${level} [${entry.source}] ${entry.message}${data}`;
}

export const LOG_LEVEL_ORDER: Record<LogEntry['level'], number> = {
  trace: 0, debug: 1, info: 2, warn: 3, error: 4, fatal: 5,
};

export function shouldLog(entryLevel: LogEntry['level'], minLevel: LogEntry['level']): boolean {
  return LOG_LEVEL_ORDER[entryLevel] >= LOG_LEVEL_ORDER[minLevel];
}
